// ============================================================================
// CLEO'S SECRET — AdminPanel
// File: src/pages/AdminPanel.jsx
//
// Six-view admin operational dashboard with sidebar navigation.
//   • Overview    — KPIs + revenue chart + conversion funnel + actions
//   • Users       — patient/clinician/nurse roster with role filter
//   • Content     — transformation modules CMS + report copy editor
//   • Scoring     — Cleo Method weights matrix (admin-configurable IP)
//   • Billing     — KPIs + transactions table
//   • Automations — toggleable trigger automation list
//
// Pattern: WORKSTATION variant — obsidian sidebar (240px, always visible)
// over a SAND main canvas with a PAP topbar. Designed for daily operational
// use, so type and spacing are tuned for confident readability — KPIs are
// the largest type on the page, page titles dominate the topbar, every
// label is bold enough to read at a glance.
//
// Auth: gated behind login AND admin role.
//   1. If no session → inline email + password sign-in form
//   2. If signed in but is_admin = false → "access denied" screen
//   3. If signed in and is_admin = true → admin panel renders
// Admin status is determined server-side via /api/check-admin which reads
// patients.is_admin. Both you and Robyn need is_admin = true in the DB.
// ============================================================================

import React, { useState, useEffect } from 'react';
import {
  FF, FS, P,
  BtnPrimary, Spinner,
} from '../lib/design-tokens';
import Ankh from '../components/Ankh';
import { signInWithPassword } from '../lib/supabase-browser';

// ─ Helpers ─
function todayDateLong() {
  return new Date().toLocaleDateString('en-AU', {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
  });
}

// ═════════════════════════════════════════════════════════════════════════
// SHARED ATOMS — dashboard density tuned, not editorial scale
// ═════════════════════════════════════════════════════════════════════════

// KPI tile — used in Overview and Billing.
function KPI({ label, value, trend, trendGood }) {
  return (
    <div style={{
      padding: 'clamp(22px, 2.4vw, 32px)',
      background: P.PAP,
      border: '1px solid ' + P.SAND3,
      borderTop: '3px solid ' + P.GO,
    }}>
      <div style={{
        fontFamily: FF,
        fontSize: 'clamp(12px, 1.05vw, 14px)',
        fontWeight: 700,
        letterSpacing: '0.28em',
        textTransform: 'uppercase',
        color: P.BR2,
        marginBottom: 16,
      }}>{label}</div>
      <div style={{
        fontFamily: FF,
        fontSize: 'clamp(36px, 4.2vw, 52px)',
        fontWeight: 500,
        color: P.INK,
        lineHeight: 1,
        letterSpacing: '-0.02em',
        marginBottom: trend ? 12 : 0,
      }}>{value}</div>
      {trend && (
        <div style={{
          fontFamily: FF,
          fontSize: 'clamp(14px, 1.25vw, 16px)',
          fontStyle: 'italic',
          fontWeight: 500,
          color: trendGood ? P.GREEN : P.TF,
          lineHeight: 1.4,
        }}>{trend}</div>
      )}
    </div>
  );
}

// Card wrapper — used everywhere for grouped panels.
function CardWrap({ title, action, onAction, children, style }) {
  return (
    <div style={{
      background: P.PAP,
      border: '1px solid ' + P.SAND3,
      padding: 'clamp(24px, 2.4vw, 32px)',
      marginBottom: 20,
      ...style,
    }}>
      {(title || action) && (
        <div style={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          gap: 12,
          marginBottom: 'clamp(18px, 2vw, 24px)',
          paddingBottom: 'clamp(12px, 1.4vw, 16px)',
          borderBottom: '1px solid ' + P.SAND3,
        }}>
          {title && (
            <h3 style={{
              fontFamily: FF,
              fontSize: 'clamp(13px, 1.15vw, 16px)',
              fontWeight: 700,
              letterSpacing: '0.32em',
              textTransform: 'uppercase',
              color: P.BR2,
              margin: 0,
            }}>{title}</h3>
          )}
          {action && (
            <button
              onClick={onAction}
              style={{
                fontFamily: FS,
                fontSize: 'clamp(12px, 1vw, 13px)',
                fontWeight: 700,
                letterSpacing: '0.24em',
                textTransform: 'uppercase',
                color: P.BR2,
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                padding: 0,
              }}>{action}</button>
          )}
        </div>
      )}
      {children}
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════
// OVERVIEW VIEW
// ═════════════════════════════════════════════════════════════════════════
function OverviewView() {
  // ── Fetched data 
  var metricsState = useState(null);
  var metrics = metricsState[0];
  var setMetrics = metricsState[1];

  var loadingState = useState(true);
  var loading = loadingState[0];
  var setLoading = loadingState[1];

  var errorState = useState(null);
  var error = errorState[0];
  var setError = errorState[1];

  useEffect(function() {
    var cancelled = false;
    setLoading(true);
    setError(null);

    fetch('/api/admin-overview-metrics')
      .then(function(r) { return r.json(); })
      .then(function(data) {
        if (cancelled) return;
        if (data && data.kpis) {
          setMetrics(data);
          setLoading(false);
        } else {
          setError((data && data.error) || 'Failed to load metrics');
          setLoading(false);
        }
      })
      .catch(function() {
        if (cancelled) return;
        setError('Network error');
        setLoading(false);
      });

    return function() { cancelled = true; };
  }, []);

  // ── Loading state ───────
  if (loading) {
    return (
      <div style={{
        padding: 'clamp(80px, 12vw, 160px) 24px',
        textAlign: 'center',
        background: P.PAP,
        border: '1px solid ' + P.SAND3,
      }}>
        <Spinner />
      </div>
    );
  }

  // ── Error state ─
  if (error || !metrics) {
    return (
      <div style={{
        padding: 'clamp(48px, 6vw, 80px) 24px',
        textAlign: 'center',
        background: P.PAP,
        border: '1px solid ' + P.SAND3,
      }}>
        <p style={{
          fontFamily: FF,
          fontStyle: 'italic',
          fontSize: 'clamp(16px, 1.4vw, 18px)',
          color: P.RED,
          margin: 0,
        }}>
          {error || 'Could not load metrics'}. Try refreshing the page.
        </p>
      </div>
    );
  }

  var kpis = metrics.kpis;
  var chart = metrics.chart;
  var funnel = metrics.funnel;
  var actions = metrics.actions || [];
  var monthsAbbr = chart.data.map(function(d) { return d.month; });
  var maxCount = chart.data.reduce(function(m, d) { return d.count > m ? d.count : m; }, 1);
  var chartDelta = kpis.newClientsThisMonth - kpis.newClientsLastMonth;
  var chartDeltaPercent = kpis.newClientsLastMonth > 0
    ? Math.round((chartDelta / kpis.newClientsLastMonth) * 100)
    : null;

  // Helpers
  function trendLabel(thisCount, lastCount, suffix) {
    if (lastCount === null || lastCount === undefined) return suffix || '';
    var d = thisCount - lastCount;
    var sign = d > 0 ? '+' : '';
    return sign + d + ' vs last month';
  }
  function deltaText(count, label) {
    if (count === 0) return 'None ' + label;
    return '+' + count + ' ' + label;
  }

  return (
    <div>
      {/* KPI row */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
        gap: 16,
        marginBottom: 'clamp(20px, 2.2vw, 28px)',
      }}>
        <KPI
          label="Total clients"
          value={String(kpis.totalClients)}
          trend={deltaText(kpis.newClientsThisMonth, 'this month')}
          trendGood={kpis.newClientsThisMonth > 0}
        />
        <KPI
          label="Active members"
          value={String(kpis.activeMembers)}
          trend={kpis.activeMembers === 0 ? 'No members yet' : 'Clients in program'}
          trendGood={kpis.activeMembers > 0}
        />
        <KPI
          label="MRR"
          value={kpis.mrr !== null ? '$' + kpis.mrr : 'Not yet tracked'}
          trend={kpis.mrr !== null ? 'Recurring revenue' : 'Subscriptions pending'}
          trendGood={false}
        />
        <KPI
          label="Conversion rate"
          value={kpis.conversionRate + '%'}
          trend="Intake to booking"
          trendGood={kpis.conversionRate >= 50}
        />
      </div>

      {/* Chart + Funnel row */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
        gap: 18,
        marginBottom: 'clamp(20px, 2.2vw, 28px)',
      }}>
        <CardWrap title="New patients by month">
          {kpis.totalClients === 0 ? (
            <div style={{
              padding: 'clamp(40px, 5vw, 60px) 16px',
              textAlign: 'center',
            }}>
              <p style={{
                fontFamily: FF,
                fontStyle: 'italic',
                fontSize: 'clamp(16px, 1.4vw, 18px)',
                color: P.BR2,
                margin: '0 0 6px',
              }}>No patients yet.</p>
              <p style={{
                fontFamily: FF,
                fontSize: 'clamp(14px, 1.2vw, 16px)',
                color: P.TF,
                margin: 0,
              }}>The first patient who signs up will appear here.</p>
            </div>
          ) : (
            <React.Fragment>
              <div style={{
                display: 'flex',
                gap: 8,
                alignItems: 'flex-end',
                height: 140,
                marginBottom: 12,
              }}>
                {chart.data.map(function(d, i) {
                  var heightPct = maxCount > 0 ? (d.count / maxCount) * 100 : 0;
                  return (
                    <div
                      key={i}
                      title={d.monthFull + ': ' + d.count + ' patient' + (d.count === 1 ? '' : 's')}
                      style={{
                        flex: 1,
                        height: heightPct + '%',
                        minHeight: d.count > 0 ? 8 : 2,
                        background: d.isCurrent ? P.BR : P.SAND3,
                        cursor: 'pointer',
                        transition: 'opacity 0.15s',
                        minWidth: 22,
                      }}
                    />
                  );
                })}
              </div>
              <div style={{
                display: 'flex',
                gap: 8,
                marginBottom: 22,
              }}>
                {chart.data.map(function(d, i) {
                  return (
                    <div key={i} style={{
                      flex: 1,
                      fontFamily: FF,
                      fontSize: 'clamp(11px, 0.95vw, 13px)',
                      fontWeight: 700,
                      letterSpacing: '0.22em',
                      color: d.isCurrent ? P.BR2 : P.TF,
                      textAlign: 'center',
                    }}>{d.month}</div>
                  );
                })}
              </div>
              <div style={{
                display: 'flex',
                gap: 24,
                flexWrap: 'wrap',
                fontFamily: FF,
                fontSize: 'clamp(16px, 1.3vw, 18px)',
                color: P.TX,
              }}>
                <span>This month: <strong style={{ color: P.BR2, fontWeight: 700 }}>{kpis.newClientsThisMonth}</strong></span>
                <span>Last month: <strong style={{ fontWeight: 600 }}>{kpis.newClientsLastMonth}</strong></span>
                {chartDeltaPercent !== null && chartDelta !== 0 && (
                  <span style={{
                    color: chartDelta > 0 ? P.GREEN : P.RED,
                    fontStyle: 'italic',
                    fontWeight: 600,
                  }}>{chartDelta > 0 ? '+' : ''}{chartDeltaPercent}%</span>
                )}
              </div>
            </React.Fragment>
          )}
        </CardWrap>

        <CardWrap title="Conversion funnel">
          {funnel[0].count === 0 ? (
            <div style={{
              padding: 'clamp(40px, 5vw, 60px) 16px',
              textAlign: 'center',
            }}>
              <p style={{
                fontFamily: FF,
                fontStyle: 'italic',
                fontSize: 'clamp(16px, 1.4vw, 18px)',
                color: P.BR2,
                margin: '0 0 6px',
              }}>No intakes yet.</p>
              <p style={{
                fontFamily: FF,
                fontSize: 'clamp(14px, 1.2vw, 16px)',
                color: P.TF,
                margin: 0,
              }}>Funnel data will appear once patients complete their AI intake.</p>
            </div>
          ) : (
            funnel.map(function(row, i) {
              var colorChoices = [P.INK, P.BR2, P.GO2, P.GO];
              return (
                <div key={i} style={{ marginBottom: 20 }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'baseline',
                    marginBottom: 10,
                  }}>
                    <span style={{
                      fontFamily: FF,
                      fontSize: 'clamp(15px, 1.3vw, 17px)',
                      color: P.TX,
                      fontWeight: 500,
                    }}>{row.label}</span>
                    <span style={{
                      fontFamily: FF,
                      fontSize: 'clamp(13px, 1.1vw, 15px)',
                      fontWeight: 700,
                      letterSpacing: '0.16em',
                      color: P.BR2,
                    }}>{row.count} <span style={{ color: P.TF, fontWeight: 600 }}>{row.percent}%</span></span>
                  </div>
                  <div style={{ height: 6, background: P.SAND2 }}>
                    <div style={{
                      height: 6,
                      background: colorChoices[i] || P.GO,
                      width: row.percent + '%',
                    }} />
                  </div>
                </div>
              );
            })
          )}
        </CardWrap>
      </div>

      {/* Actions required */}
      <CardWrap title={'Actions required (' + actions.length + ')'} action={actions.length > 0 ? 'Mark all done' : null}>
        {actions.length === 0 ? (
          <div style={{
            padding: 'clamp(32px, 4vw, 56px) 16px',
            textAlign: 'center',
          }}>
            <p style={{
              fontFamily: FF,
              fontStyle: 'italic',
              fontSize: 'clamp(16px, 1.4vw, 18px)',
              color: P.GREEN,
              margin: '0 0 6px',
            }}>All caught up.</p>
            <p style={{
              fontFamily: FF,
              fontSize: 'clamp(14px, 1.2vw, 16px)',
              color: P.TF,
              margin: 0,
            }}>No patients need attention right now.</p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {actions.map(function(a, i) {
              var bg = a.tone === 'red' ? P.RED_BG : P.AMBER_BG;
              var bd = a.tone === 'red' ? P.RED : P.AMBER;
              return (
                <div key={i} style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 18,
                  padding: 'clamp(16px, 1.8vw, 22px)',
                  background: bg,
                  border: '1px solid ' + bd,
                  borderLeft: '4px solid ' + bd,
                  flexWrap: 'wrap',
                }}>
                  <div style={{
                    flex: 1,
                    minWidth: 260,
                    fontFamily: FF,
                    fontSize: 'clamp(15px, 1.3vw, 17px)',
                    color: P.TX,
                    lineHeight: 1.6,
                  }}>
                    <strong style={{ color: P.INK, fontWeight: 700 }}>{a.patientName}.</strong> {a.message}
                  </div>
                  <button style={{
                    fontFamily: FS,
                    fontSize: 'clamp(12px, 1vw, 13px)',
                    fontWeight: 700,
                    letterSpacing: '0.24em',
                    textTransform: 'uppercase',
                    padding: '12px 24px',
                    background: a.primary ? P.INK : 'transparent',
                    color: a.primary ? P.PAP : P.BR2,
                    border: '1px solid ' + (a.primary ? P.INK : P.GO),
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                    flexShrink: 0,
                  }}>{a.actionLabel}</button>
                </div>
              );
            })}
          </div>
        )}
      </CardWrap>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════
// USERS VIEW
// ═════════════════════════════════════════════════════════════════════════
// ═════════════════════════════════════════════════════════════════════════
// ADD USER MODAL — admin creates a new user with one or more roles
// ═════════════════════════════════════════════════════════════════════════
//
// Form: email, first/last name, role checkboxes (Admin/Manager/Clinician/
// Client), clinician sub-role dropdown when Clinician is checked, password.
// Inline "Add new sub-role" mini-form when admin needs a sub-role that
// doesn't yet exist in the catalog.
//
function AddUserModal(props) {
  var onClose = props.onClose;
  var onSuccess = props.onSuccess;

  var emailState = useState('');
  var email = emailState[0]; var setEmail = emailState[1];

  var firstNameState = useState('');
  var firstName = firstNameState[0]; var setFirstName = firstNameState[1];

  var lastNameState = useState('');
  var lastName = lastNameState[0]; var setLastName = lastNameState[1];

  var passwordState = useState('');
  var password = passwordState[0]; var setPassword = passwordState[1];

  // Roles: array of strings. Start with 'client' for safety.
  var rolesState = useState(['client']);
  var roles = rolesState[0]; var setRoles = rolesState[1];

  // Clinician sub-role
  var subroleIdState = useState('');
  var subroleId = subroleIdState[0]; var setSubroleId = subroleIdState[1];

  // Sub-role catalog (fetched)
  var subrolesState = useState([]);
  var subroles = subrolesState[0]; var setSubroles = subrolesState[1];

  var subrolesLoadingState = useState(true);
  var subrolesLoading = subrolesLoadingState[0]; var setSubrolesLoading = subrolesLoadingState[1];

  // Inline add-subrole form
  var showAddSubroleState = useState(false);
  var showAddSubrole = showAddSubroleState[0]; var setShowAddSubrole = showAddSubroleState[1];

  var newSubroleNameState = useState('');
  var newSubroleName = newSubroleNameState[0]; var setNewSubroleName = newSubroleNameState[1];

  var subroleErrState = useState(null);
  var subroleErr = subroleErrState[0]; var setSubroleErr = subroleErrState[1];

  var savingSubroleState = useState(false);
  var savingSubrole = savingSubroleState[0]; var setSavingSubrole = savingSubroleState[1];

  var submittingState = useState(false);
  var submitting = submittingState[0]; var setSubmitting = submittingState[1];

  var errorState = useState(null);
  var error = errorState[0]; var setError = errorState[1];

  var doneState = useState(null);
  var done = doneState[0]; var setDone = doneState[1];

  // Load sub-roles on mount
  useEffect(function() {
    var cancelled = false;
    fetch('/api/admin-list-subroles')
      .then(function(r) { return r.json(); })
      .then(function(data) {
        if (cancelled) return;
        setSubroles(data.subroles || []);
        setSubrolesLoading(false);
      })
      .catch(function() {
        if (cancelled) return;
        setSubrolesLoading(false);
      });
    return function() { cancelled = true; };
  }, []);

  function toggleRole(r) {
    if (roles.indexOf(r) !== -1) {
      // Don't allow removing the last role
      if (roles.length === 1) return;
      setRoles(roles.filter(function(x) { return x !== r; }));
      if (r === 'clinician') setSubroleId('');
    } else {
      setRoles(roles.concat([r]));
    }
  }

  function generatePassword() {
    var chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789';
    var p = '';
    for (var i = 0; i < 14; i++) p += chars.charAt(Math.floor(Math.random() * chars.length));
    setPassword(p);
  }

  function handleSaveNewSubrole() {
    setSubroleErr(null);
    var clean = newSubroleName.trim();
    if (!clean) {
      setSubroleErr('Name required.');
      return;
    }
    setSavingSubrole(true);
    fetch('/api/admin-create-subrole', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name: clean }),
    })
      .then(function(r) { return r.json().then(function(j) { return { ok: r.ok, body: j }; }); })
      .then(function(resp) {
        setSavingSubrole(false);
        if (resp.ok && resp.body.success) {
          setSubroles(subroles.concat([resp.body.subrole]).sort(function(a, b) {
            return (a.display_order - b.display_order) || a.name.localeCompare(b.name);
          }));
          setSubroleId(resp.body.subrole.id);
          setNewSubroleName('');
          setShowAddSubrole(false);
        } else {
          setSubroleErr((resp.body && resp.body.error) || 'Could not save.');
        }
      })
      .catch(function() {
        setSavingSubrole(false);
        setSubroleErr('Network error.');
      });
  }

  function handleSubmit(e) {
    if (e) e.preventDefault();
    setError(null);

    var cleanEmail = email.trim().toLowerCase();
    var emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(cleanEmail)) {
      setError('Please enter a valid email address.');
      return;
    }
    if (!firstName.trim()) {
      setError('First name is required.');
      return;
    }
    if (!password || password.length < 8) {
      setError('Password must be at least 8 characters. Use the Generate button if needed.');
      return;
    }
    if (roles.indexOf('clinician') !== -1 && !subroleId) {
      setError('Please select a clinician sub-role.');
      return;
    }

    setSubmitting(true);
    fetch('/api/admin-create-user', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        email: cleanEmail,
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        password: password,
        roles: roles,
        clinicianSubroleId: roles.indexOf('clinician') !== -1 ? subroleId : null,
      }),
    })
      .then(function(r) { return r.json().then(function(j) { return { ok: r.ok, body: j }; }); })
      .then(function(resp) {
        setSubmitting(false);
        if (resp.ok && resp.body.success) {
          var subroleName = '';
          if (roles.indexOf('clinician') !== -1) {
            var found = subroles.find(function(s) { return s.id === subroleId; });
            if (found) subroleName = found.name;
          }
          setDone({
            email: resp.body.email,
            firstName: resp.body.firstName,
            lastName: resp.body.lastName,
            roles: resp.body.roles,
            subroleName: subroleName,
            password: password,
          });
          if (onSuccess) onSuccess();
        } else {
          var msg = (resp.body && resp.body.error) || 'Failed to create user.';
          var detail = (resp.body && resp.body.detail) ? ' ' + resp.body.detail : '';
          setError(msg + detail);
        }
      })
      .catch(function() {
        setSubmitting(false);
        setError('Network error. Try again.');
      });
  }

  function copyPassword() {
    if (done && done.password && typeof navigator !== 'undefined' && navigator.clipboard) {
      navigator.clipboard.writeText(done.password).catch(function() {});
    }
  }

  var ROLE_OPTIONS = [
    { key: 'admin',     label: 'Admin',     desc: 'Full access to admin dashboard and operational data.' },
    { key: 'manager',   label: 'Manager',   desc: 'Clinical operations oversight. Most admin views except billing.' },
    { key: 'clinician', label: 'Clinician', desc: 'Clinical workstation. Requires a sub-role.' },
    { key: 'client',    label: 'Client',    desc: 'Patient portal access. Sees their own data only.' },
  ];

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0,
        background: 'rgba(26,15,8,0.62)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 'clamp(16px, 4vw, 48px)',
        zIndex: 200,
      }}>
      <div
        onClick={function(e) { e.stopPropagation(); }}
        style={{
          background: P.PAP,
          border: '1px solid ' + P.SAND3,
          maxWidth: 640,
          width: '100%',
          maxHeight: '92vh',
          overflowY: 'auto',
          padding: 'clamp(28px, 3.2vw, 44px)',
        }}>

        {!done ? (
          <React.Fragment>
            <div style={{
              fontFamily: FF,
              fontSize: 'clamp(12px, 1.05vw, 14px)',
              fontWeight: 700,
              letterSpacing: '0.32em',
              textTransform: 'uppercase',
              color: P.BR2,
              marginBottom: 12,
            }}>Create user</div>
            <h2 style={{
              fontFamily: FF,
              fontSize: 'clamp(26px, 2.8vw, 36px)',
              fontWeight: 500,
              color: P.INK,
              lineHeight: 1.15,
              letterSpacing: '-0.014em',
              margin: '0 0 28px',
            }}>Add a new user.</h2>

            <form onSubmit={handleSubmit}>
              {/* Email */}
              <div style={{ marginBottom: 18 }}>
                <label style={modalLabelStyle()}>Email</label>
                <input type="email" autoComplete="off" value={email}
                  onChange={function(e) { setEmail(e.target.value); }}
                  disabled={submitting} placeholder="name@example.com"
                  style={modalInputStyle()} />
              </div>

              {/* Names */}
              <div style={{
                display: 'grid', gridTemplateColumns: '1fr 1fr',
                gap: 14, marginBottom: 18,
              }}>
                <div>
                  <label style={modalLabelStyle()}>First name</label>
                  <input type="text" autoComplete="off" value={firstName}
                    onChange={function(e) { setFirstName(e.target.value); }}
                    disabled={submitting} style={modalInputStyle()} />
                </div>
                <div>
                  <label style={modalLabelStyle()}>Last name</label>
                  <input type="text" autoComplete="off" value={lastName}
                    onChange={function(e) { setLastName(e.target.value); }}
                    disabled={submitting} style={modalInputStyle()} />
                </div>
              </div>

              {/* Roles — checkboxes */}
              <div style={{ marginBottom: 18 }}>
                <label style={modalLabelStyle()}>Roles</label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {ROLE_OPTIONS.map(function(opt) {
                    var checked = roles.indexOf(opt.key) !== -1;
                    return (
                      <div
                        key={opt.key}
                        onClick={function() { if (!submitting) toggleRole(opt.key); }}
                        style={{
                          padding: '14px 16px',
                          border: '1px solid ' + (checked ? P.BR : P.SAND3),
                          background: checked ? 'rgba(154,116,48,0.08)' : P.PAP,
                          cursor: submitting ? 'wait' : 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 14,
                          transition: 'all 0.15s',
                        }}>
                        <div style={{
                          width: 20, height: 20,
                          border: '1.5px solid ' + (checked ? P.BR : P.SAND3),
                          background: checked ? P.BR : 'transparent',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          flexShrink: 0,
                          transition: 'all 0.15s',
                        }}>
                          {checked && (
                            <svg width="12" height="12" viewBox="0 0 12 12">
                              <path d="M2 6 L5 9 L10 3" stroke={P.PAP} strokeWidth="2"
                                fill="none" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                          )}
                        </div>
                        <div style={{ flex: 1 }}>
                          <div style={{
                            fontFamily: FF,
                            fontSize: 'clamp(15px, 1.3vw, 17px)',
                            fontWeight: 600,
                            color: P.INK,
                            letterSpacing: '-0.005em',
                          }}>{opt.label}</div>
                          <div style={{
                            fontFamily: FF,
                            fontSize: 'clamp(13px, 1.1vw, 14px)',
                            color: P.TF,
                            marginTop: 2,
                            lineHeight: 1.5,
                          }}>{opt.desc}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Clinician sub-role — shown only when Clinician is checked */}
              {roles.indexOf('clinician') !== -1 && (
                <div style={{ marginBottom: 22 }}>
                  <label style={modalLabelStyle()}>Clinician sub-role</label>
                  {!showAddSubrole ? (
                    <React.Fragment>
                      <select
                        value={subroleId}
                        onChange={function(e) {
                          if (e.target.value === '__add_new__') {
                            setShowAddSubrole(true);
                          } else {
                            setSubroleId(e.target.value);
                          }
                        }}
                        disabled={submitting || subrolesLoading}
                        style={Object.assign({}, modalInputStyle(), { cursor: 'pointer' })}>
                        <option value="">{subrolesLoading ? 'Loading...' : 'Select a sub-role'}</option>
                        {subroles.map(function(s) {
                          return <option key={s.id} value={s.id}>{s.name}</option>;
                        })}
                        <option disabled value=""></option>
                        <option value="__add_new__">+ Add new sub-role</option>
                      </select>
                    </React.Fragment>
                  ) : (
                    <div style={{
                      background: P.SAND,
                      border: '1px solid ' + P.SAND3,
                      padding: 'clamp(14px, 1.6vw, 18px)',
                    }}>
                      <div style={{
                        fontFamily: FF,
                        fontSize: 'clamp(11px, 0.95vw, 12px)',
                        fontWeight: 700,
                        letterSpacing: '0.28em',
                        textTransform: 'uppercase',
                        color: P.BR2,
                        marginBottom: 10,
                      }}>New sub-role</div>
                      <input type="text" autoComplete="off"
                        value={newSubroleName}
                        onChange={function(e) { setNewSubroleName(e.target.value); }}
                        disabled={savingSubrole}
                        placeholder="e.g. Acupuncturist, Counsellor"
                        style={modalInputStyle()} />
                      {subroleErr && (
                        <p style={{
                          fontFamily: FF,
                          fontSize: 'clamp(13px, 1.1vw, 14px)',
                          fontStyle: 'italic',
                          color: P.RED,
                          margin: '10px 0 0',
                        }}>{subroleErr}</p>
                      )}
                      <div style={{ display: 'flex', gap: 10, marginTop: 12 }}>
                        <button type="button"
                          onClick={function() {
                            setShowAddSubrole(false);
                            setNewSubroleName('');
                            setSubroleErr(null);
                          }}
                          disabled={savingSubrole}
                          style={{
                            flex: 1,
                            fontFamily: FS,
                            fontSize: 'clamp(11px, 0.95vw, 12px)',
                            fontWeight: 700,
                            letterSpacing: '0.24em',
                            textTransform: 'uppercase',
                            padding: '11px',
                            background: 'transparent',
                            color: P.BR2,
                            border: '1px solid ' + P.SAND3,
                            cursor: savingSubrole ? 'wait' : 'pointer',
                          }}>Cancel</button>
                        <button type="button"
                          onClick={handleSaveNewSubrole}
                          disabled={savingSubrole}
                          style={{
                            flex: 2,
                            fontFamily: FS,
                            fontSize: 'clamp(11px, 0.95vw, 12px)',
                            fontWeight: 700,
                            letterSpacing: '0.24em',
                            textTransform: 'uppercase',
                            padding: '11px',
                            background: savingSubrole ? P.TL : P.INK,
                            color: P.PAP,
                            border: 'none',
                            cursor: savingSubrole ? 'wait' : 'pointer',
                          }}>
                          {savingSubrole ? 'Saving...' : 'Save sub-role'}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Password */}
              <div style={{ marginBottom: 22 }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'baseline',
                  marginBottom: 8,
                }}>
                  <label style={Object.assign({}, modalLabelStyle(), { marginBottom: 0 })}>Temporary password</label>
                  <button type="button" onClick={generatePassword} disabled={submitting}
                    style={{
                      fontFamily: FS,
                      fontSize: 'clamp(11px, 0.95vw, 12px)',
                      fontWeight: 700,
                      letterSpacing: '0.22em',
                      textTransform: 'uppercase',
                      color: P.BR2,
                      background: 'transparent',
                      border: 'none',
                      cursor: submitting ? 'wait' : 'pointer',
                      padding: 0,
                    }}>Generate</button>
                </div>
                <input type="text" autoComplete="off"
                  value={password}
                  onChange={function(e) { setPassword(e.target.value); }}
                  disabled={submitting}
                  placeholder="At least 8 characters"
                  style={modalInputStyle()} />
              </div>

              {/* Error */}
              {error && (
                <p style={{
                  fontFamily: FF,
                  fontSize: 'clamp(14px, 1.2vw, 16px)',
                  fontStyle: 'italic',
                  color: P.RED,
                  lineHeight: 1.5,
                  margin: '0 0 18px',
                }}>{error}</p>
              )}

              {/* Actions */}
              <div style={{
                display: 'flex',
                gap: 12,
                marginTop: 6,
                flexWrap: 'wrap',
              }}>
                <button type="button" onClick={onClose} disabled={submitting}
                  style={{
                    flex: 1,
                    fontFamily: FS,
                    fontSize: 'clamp(12px, 1vw, 13px)',
                    fontWeight: 700,
                    letterSpacing: '0.28em',
                    textTransform: 'uppercase',
                    padding: '14px',
                    background: 'transparent',
                    color: P.BR2,
                    border: '1px solid ' + P.SAND3,
                    cursor: submitting ? 'wait' : 'pointer',
                  }}>Cancel</button>
                <button type="submit" disabled={submitting}
                  style={{
                    flex: 2,
                    fontFamily: FS,
                    fontSize: 'clamp(12px, 1vw, 13px)',
                    fontWeight: 700,
                    letterSpacing: '0.28em',
                    textTransform: 'uppercase',
                    padding: '14px',
                    background: submitting ? P.TL : P.INK,
                    color: P.PAP,
                    border: 'none',
                    cursor: submitting ? 'wait' : 'pointer',
                  }}>
                  {submitting ? 'Creating...' : 'Create user'}
                </button>
              </div>
            </form>
          </React.Fragment>
        ) : (
          // ─── Success state ───
          <React.Fragment>
            <div style={{
              fontFamily: FF,
              fontSize: 'clamp(12px, 1.05vw, 14px)',
              fontWeight: 700,
              letterSpacing: '0.32em',
              textTransform: 'uppercase',
              color: P.GREEN,
              marginBottom: 12,
            }}>User created</div>
            <h2 style={{
              fontFamily: FF,
              fontSize: 'clamp(26px, 2.8vw, 36px)',
              fontWeight: 500,
              color: P.INK,
              lineHeight: 1.15,
              letterSpacing: '-0.014em',
              margin: '0 0 18px',
            }}>{(done.firstName || done.email) + ' is in.'}</h2>

            <p style={{
              fontFamily: FF,
              fontSize: 'clamp(15px, 1.3vw, 17px)',
              color: P.TL,
              lineHeight: 1.65,
              margin: '0 0 18px',
            }}>
              Assigned roles: <strong style={{ color: P.BR2, fontWeight: 700 }}>
                {done.roles.map(function(r) {
                  if (r === 'clinician' && done.subroleName) return 'Clinician (' + done.subroleName + ')';
                  return r.charAt(0).toUpperCase() + r.slice(1);
                }).join(', ')}
              </strong>.
            </p>

            <p style={{
              fontFamily: FF,
              fontSize: 'clamp(15px, 1.3vw, 17px)',
              color: P.TL,
              lineHeight: 1.65,
              margin: '0 0 26px',
            }}>
              Share these details directly. We do not email passwords.
            </p>

            <div style={{
              background: P.SAND,
              border: '1px solid ' + P.SAND3,
              padding: 'clamp(18px, 2vw, 24px)',
              marginBottom: 22,
            }}>
              <div style={{ marginBottom: 14 }}>
                <div style={{
                  fontFamily: FF,
                  fontSize: 'clamp(11px, 0.95vw, 12px)',
                  fontWeight: 700,
                  letterSpacing: '0.28em',
                  textTransform: 'uppercase',
                  color: P.BR2,
                  marginBottom: 6,
                }}>Email</div>
                <div style={{
                  fontFamily: FF,
                  fontSize: 'clamp(16px, 1.4vw, 18px)',
                  color: P.INK,
                  fontWeight: 600,
                  wordBreak: 'break-all',
                }}>{done.email}</div>
              </div>

              <div>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'baseline',
                  marginBottom: 6,
                }}>
                  <div style={{
                    fontFamily: FF,
                    fontSize: 'clamp(11px, 0.95vw, 12px)',
                    fontWeight: 700,
                    letterSpacing: '0.28em',
                    textTransform: 'uppercase',
                    color: P.BR2,
                  }}>Password</div>
                  <button type="button" onClick={copyPassword}
                    style={{
                      fontFamily: FS,
                      fontSize: 'clamp(11px, 0.95vw, 12px)',
                      fontWeight: 700,
                      letterSpacing: '0.22em',
                      textTransform: 'uppercase',
                      color: P.BR2,
                      background: 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      padding: 0,
                    }}>Copy</button>
                </div>
                <div style={{
                  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                  fontSize: 'clamp(16px, 1.4vw, 18px)',
                  color: P.INK,
                  fontWeight: 600,
                  background: P.PAP,
                  padding: '12px 16px',
                  border: '1px solid ' + P.SAND3,
                  wordBreak: 'break-all',
                  letterSpacing: '0.02em',
                }}>{done.password}</div>
              </div>
            </div>

            <p style={{
              fontFamily: FF,
              fontSize: 'clamp(13px, 1.1vw, 15px)',
              fontStyle: 'italic',
              color: P.TF,
              lineHeight: 1.6,
              margin: '0 0 22px',
            }}>Encourage them to change the password on first sign-in.</p>

            <button type="button" onClick={onClose}
              style={{
                width: '100%',
                fontFamily: FS,
                fontSize: 'clamp(12px, 1vw, 13px)',
                fontWeight: 700,
                letterSpacing: '0.28em',
                textTransform: 'uppercase',
                padding: '14px',
                background: P.INK,
                color: P.PAP,
                border: 'none',
                cursor: 'pointer',
              }}>Done</button>
          </React.Fragment>
        )}

      </div>
    </div>
  );
}

function modalLabelStyle() {
  return {
    display: 'block',
    fontFamily: FF,
    fontSize: 'clamp(11px, 0.95vw, 12px)',
    fontWeight: 700,
    letterSpacing: '0.28em',
    textTransform: 'uppercase',
    color: P.BR2,
    marginBottom: 8,
  };
}

function modalInputStyle() {
  return {
    width: '100%',
    fontFamily: FF,
    fontSize: 'clamp(15px, 1.3vw, 17px)',
    padding: '14px 16px',
    border: '1px solid ' + P.SAND3,
    background: P.PAP,
    color: P.INK,
    outline: 'none',
    boxSizing: 'border-box',
    transition: 'border-color 0.2s',
  };
}

// ═════════════════════════════════════════════════════════════════════════
// USERS VIEW
// ═════════════════════════════════════════════════════════════════════════
function UsersView() {
  var usersState = useState([]);
  var users = usersState[0];
  var setUsers = usersState[1];

  var loadingState = useState(true);
  var loading = loadingState[0];
  var setLoading = loadingState[1];

  var errorState = useState(null);
  var error = errorState[0];
  var setError = errorState[1];

  var searchState = useState('');
  var search = searchState[0];
  var setSearch = searchState[1];

  var roleState = useState('all');
  var role = roleState[0];
  var setRole = roleState[1];

  var showAddState = useState(false);
  var showAdd = showAddState[0];
  var setShowAdd = showAddState[1];

  var refreshCounterState = useState(0);
  var refreshCounter = refreshCounterState[0];
  var setRefreshCounter = refreshCounterState[1];

  // Debounce search input — wait 300ms after typing stops before fetching
  var debouncedSearchState = useState('');
  var debouncedSearch = debouncedSearchState[0];
  var setDebouncedSearch = debouncedSearchState[1];

  useEffect(function() {
    var t = setTimeout(function() { setDebouncedSearch(search); }, 300);
    return function() { clearTimeout(t); };
  }, [search]);

  // Fetch users when filters change or refresh counter ticks
  useEffect(function() {
    var cancelled = false;
    setLoading(true);
    setError(null);

    fetch('/api/admin-list-users', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ search: debouncedSearch, role: role }),
    })
      .then(function(r) { return r.json(); })
      .then(function(data) {
        if (cancelled) return;
        if (data && data.users) {
          setUsers(data.users);
          setLoading(false);
        } else {
          setError(data && data.error ? data.error : 'Failed to load users');
          setLoading(false);
        }
      })
      .catch(function(err) {
        if (cancelled) return;
        setError('Network error');
        setLoading(false);
      });

    return function() { cancelled = true; };
  }, [debouncedSearch, role, refreshCounter]);

  function roleColor(r) {
    if (r === 'Admin') return { bg: 'rgba(154,116,48,0.18)', fg: P.BR2 };
    if (r === 'Manager') return { bg: 'rgba(201, 169, 97, 0.18)', fg: P.BR2 };
    // Clinician (and all sub-role variants like "GP", "Naturopath", etc.)
    if (r === 'Clinician' || (typeof r === 'string' && r.indexOf('Clinician') === -1 && r !== 'Client' && r !== 'Admin' && r !== 'Manager')) {
      return { bg: 'rgba(212,150,26,0.12)', fg: P.AMBER };
    }
    if (r === 'Client') return { bg: 'rgba(122,96,64,0.1)', fg: P.TX };
    return { bg: P.SAND, fg: P.TF };
  }

  function formatJoinedDate(iso) {
    if (!iso) return '';
    try {
      var d = new Date(iso);
      return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
    } catch (e) { return ''; }
  }

  return (
    <div>
      {/* Controls row */}
      <div style={{
        display: 'flex',
        gap: 12,
        marginBottom: 20,
        flexWrap: 'wrap',
      }}>
        <input
          placeholder={'Search by name or email\u2026'}
          value={search}
          onChange={function(e) { setSearch(e.target.value); }}
          style={{
            flex: 1,
            maxWidth: 360,
            minWidth: 200,
            fontFamily: FF,
            fontSize: 'clamp(15px, 1.3vw, 17px)',
            padding: '14px 18px',
            border: '1px solid ' + P.SAND3,
            background: P.PAP,
            color: P.INK,
            outline: 'none',
          }}
        />
        <select
          value={role}
          onChange={function(e) { setRole(e.target.value); }}
          style={{
            fontFamily: FF,
            fontSize: 'clamp(15px, 1.3vw, 17px)',
            padding: '14px 18px',
            border: '1px solid ' + P.SAND3,
            background: P.PAP,
            color: P.INK,
            outline: 'none',
            cursor: 'pointer',
          }}>
          <option value="all">All roles</option>
          <option value="admin">Admin</option>
          <option value="manager">Manager</option>
          <option value="clinician">Clinician</option>
          <option value="client">Client</option>
        </select>
        <button
          onClick={function() { setShowAdd(true); }}
          style={{
          fontFamily: FS,
          fontSize: 'clamp(13px, 1.1vw, 14px)',
          fontWeight: 700,
          letterSpacing: '0.28em',
          textTransform: 'uppercase',
          padding: '14px 28px',
          background: P.INK,
          color: P.PAP,
          border: 'none',
          cursor: 'pointer',
        }}>Add user</button>
      </div>

      <CardWrap style={{ padding: 0, overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
        {/* Table header */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: '2fr 1fr 1.4fr 1.2fr 100px',
          gap: 16,
          padding: '18px 24px',
          minWidth: 720,
          fontFamily: FF,
          fontSize: 'clamp(12px, 1.05vw, 14px)',
          fontWeight: 700,
          letterSpacing: '0.28em',
          textTransform: 'uppercase',
          color: P.BR2,
          borderBottom: '1px solid ' + P.SAND3,
        }}>
          <span>User</span>
          <span>Role</span>
          <span>Status</span>
          <span>Assigned to</span>
          <span>Actions</span>
        </div>

        {/* Loading state */}
        {loading && (
          <div style={{ padding: 'clamp(48px, 6vw, 80px) 24px', textAlign: 'center' }}>
            <Spinner />
          </div>
        )}

        {/* Error state */}
        {!loading && error && (
          <div style={{
            padding: 'clamp(48px, 6vw, 80px) 24px',
            textAlign: 'center',
            fontFamily: FF,
            fontStyle: 'italic',
            fontSize: 'clamp(16px, 1.4vw, 18px)',
            color: P.RED,
          }}>
            {error}. Try refreshing the page.
          </div>
        )}

        {/* Empty state */}
        {!loading && !error && users.length === 0 && (
          <div style={{
            padding: 'clamp(48px, 6vw, 80px) 24px',
            textAlign: 'center',
          }}>
            <p style={{
              fontFamily: FF,
              fontSize: 'clamp(18px, 1.6vw, 22px)',
              fontStyle: 'italic',
              color: P.BR2,
              marginBottom: 8,
            }}>
              {(debouncedSearch || role !== 'all') ? 'No users match these filters.' : 'No users yet.'}
            </p>
            <p style={{
              fontFamily: FF,
              fontSize: 'clamp(14px, 1.2vw, 16px)',
              color: P.TF,
              margin: 0,
            }}>
              {(debouncedSearch || role !== 'all') ? 'Try a different search or role.' : 'The first patient who signs up will appear here.'}
            </p>
          </div>
        )}

        {/* Rows */}
        {!loading && !error && users.map(function(u, i) {
          var rc = roleColor(u.role);
          return (
            <div key={u.id || i} style={{
              display: 'grid',
              gridTemplateColumns: '2fr 1fr 1.4fr 1.2fr 100px',
              gap: 16,
              padding: '18px 24px',
              minWidth: 720,
              alignItems: 'center',
              borderBottom: i < users.length - 1 ? '1px solid ' + P.SAND3 : 'none',
              cursor: 'pointer',
              transition: 'background 0.2s',
            }}
              onMouseEnter={function(e) { e.currentTarget.style.background = 'rgba(232,221,196,0.4)'; }}
              onMouseLeave={function(e) { e.currentTarget.style.background = 'transparent'; }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                <div style={{
                  width: 44,
                  height: 44,
                  borderRadius: '50%',
                  background: rc.bg,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontFamily: FF,
                  fontSize: 16,
                  fontStyle: 'italic',
                  fontWeight: 600,
                  color: rc.fg,
                  flexShrink: 0,
                }}>{u.initials}</div>
                <div style={{ minWidth: 0 }}>
                  <div style={{
                    fontFamily: FF,
                    fontSize: 'clamp(16px, 1.4vw, 19px)',
                    fontWeight: 600,
                    color: P.INK,
                    letterSpacing: '-0.008em',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}>{u.name}</div>
                  <div style={{
                    fontFamily: FS,
                    fontSize: 'clamp(13px, 1.1vw, 14px)',
                    color: P.TF,
                    marginTop: 4,
                    fontStyle: 'italic',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}>{u.email}</div>
                </div>
              </div>
              <span style={{
                fontFamily: FF,
                fontSize: 'clamp(11px, 0.95vw, 12px)',
                fontWeight: 700,
                letterSpacing: '0.22em',
                textTransform: 'uppercase',
                padding: '7px 13px',
                background: rc.bg,
                color: rc.fg,
                border: '1px solid ' + rc.fg,
                width: 'fit-content',
              }}>{u.role}</span>
              <div style={{
                fontFamily: FF,
                fontSize: 'clamp(14px, 1.2vw, 16px)',
                color: P.TX,
              }}>{u.status}</div>
              <div style={{
                fontFamily: FF,
                fontSize: 'clamp(14px, 1.2vw, 16px)',
                color: u.assignedTo ? P.TX : P.TF,
                fontStyle: u.assignedTo ? 'normal' : 'italic',
              }}>{u.assignedTo || 'Unassigned'}</div>
              <button style={{
                fontFamily: FS,
                fontSize: 'clamp(12px, 1vw, 13px)',
                fontWeight: 700,
                letterSpacing: '0.24em',
                textTransform: 'uppercase',
                padding: '9px 18px',
                border: '1px solid ' + P.GO,
                background: 'transparent',
                color: P.BR2,
                cursor: 'pointer',
              }}>Edit</button>
            </div>
          );
        })}
      </CardWrap>

      {/* Footer count line */}
      {!loading && !error && users.length > 0 && (
        <div style={{
          marginTop: 14,
          fontFamily: FF,
          fontSize: 'clamp(13px, 1.1vw, 15px)',
          fontStyle: 'italic',
          color: P.TF,
          textAlign: 'right',
        }}>
          Showing {users.length} {users.length === 1 ? 'user' : 'users'}
          {(debouncedSearch || role !== 'all') ? ' (filtered)' : ''}.
        </div>
      )}

      {/* Add User Modal */}
      {showAdd && (
        <AddUserModal
          onClose={function() { setShowAdd(false); }}
          onSuccess={function() { setRefreshCounter(refreshCounter + 1); }}
        />
      )}
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════
// SCORING VIEW
// ═════════════════════════════════════════════════════════════════════════
function ScoringView() {
  // Source of truth: api/generate-cleo-score.js. Update both when the
  // prompt evolves. Dimensions render with the long display names used
  // everywhere visible (Hormonal Balance / Metabolic Health /
  // Sleep & Recovery / Vitality & Mood).
  var DIMENSIONS = [
    {
      key: 'hormonal',
      name: 'Hormonal Balance',
      definition: 'Endocrine signalling and reproductive hormone dynamics. Cycle regulation, perimenopausal and menopausal transition, and the symptoms that flow from each.',
      intakeSignals: [
        'menstrual symptoms', 'cycle regularity', 'peri/menopause symptoms',
        'libido', 'breast tenderness', 'mood swings', 'hot flushes',
      ],
      pathologyMarkers: [
        'TSH', 'free T3/T4', 'oestradiol', 'progesterone', 'FSH', 'LH',
        'AMH', 'prolactin', 'SHBG', 'testosterone',
      ],
    },
    {
      key: 'metabolic',
      name: 'Metabolic Health',
      definition: 'Glucose handling, body composition, and the foundational micronutrients that drive energy and weight stability.',
      intakeSignals: [
        'weight stability', 'body composition concerns', 'sugar cravings',
        'energy after meals',
      ],
      pathologyMarkers: [
        'HbA1c', 'fasting glucose', 'insulin', 'ferritin', 'vitamin D',
        'lipid panel',
      ],
    },
    {
      key: 'sleep',
      name: 'Sleep & Recovery',
      definition: 'Sleep architecture, restorativeness, and the body\u2019s ability to recover from exertion and daily stress.',
      intakeSignals: [
        'sleep duration', 'sleep quality', 'waking through the night',
        'morning fatigue', 'naps needed', 'recovery from exercise',
      ],
      pathologyMarkers: null,
    },
    {
      key: 'vitality',
      name: 'Vitality & Mood',
      definition: 'Energy, mood, anxiety, motivation, and overall life satisfaction. The dimension that captures how she experiences her own days.',
      intakeSignals: [
        'energy levels', 'mood patterns', 'anxiety', 'stress capacity',
        'motivation', 'libido', 'life satisfaction', 'brain fog',
      ],
      pathologyMarkers: null,
    },
  ];

  // Patient-facing labels per CleoScorePage.jsx:41-47. Source: prompt
  // SCORING PHILOSOPHY block at api/generate-cleo-score.js:14-19.
  var SCORE_BANDS = [
    { range: '90-100', label: 'Thriving',           meaning: 'thriving, all systems well',                                color: P.GREEN },
    { range: '75-89',  label: 'Good',               meaning: 'generally well with minor optimisation opportunities',      color: P.GO },
    { range: '60-74',  label: 'Moderate',           meaning: 'meaningful symptoms or markers needing attention',          color: P.AMBER },
    { range: '40-59',  label: 'Significant burden', meaning: 'multiple areas need clinical focus',                        color: P.BR },
    { range: '0-39',   label: 'Struggling',         meaning: 'substantial intervention warranted',                        color: P.RED },
  ];

  var PATTERN_ARCHETYPES = [
    { name: 'Thriving',            condition: 'all dimensions 80+' },
    { name: 'Hormonal Shift',      condition: 'hormonal score notably lower than others' },
    { name: 'Energy Drain',        condition: 'vitality and sleep both low' },
    { name: 'Metabolic Imbalance', condition: 'metabolic score notably lower' },
    { name: 'Sleep Struggle',      condition: 'sleep score notably lower' },
    { name: 'Compound Burden',     condition: 'three or more dimensions below 60' },
    { name: 'Foundation Building', condition: 'all dimensions in the 60s to 70s, no specific outlier' },
  ];

  var PHILOSOPHY = [
    'Functional ranges, not just lab normals. A TSH of 3.5 is "normal" but suboptimal for a symptomatic woman; score accordingly.',
    'Generous to subjective wellbeing. A woman who reports feeling great with no major issues earns a high score even without pathology to "prove" it.',
    'Overall score is not a simple average. Weight by what is most affecting her life; if hormonal is dragging vitality, weight hormonal more heavily.',
  ];

  var ENGINE = {
    model: 'claude-sonnet-4-6',
    maxTokens: 2500,
    triggers: ['manual', 'patient_request', 'intake_completed', 'pathology_approved'],
  };

  // Verbatim copy of SCORE_SYSTEM_PROMPT from api/generate-cleo-score.js
  // (lines 3-72). Kept here for in-panel reference; the live scorer
  // reads its own copy. Keep in sync when the prompt is updated.
  var SYSTEM_PROMPT = "You are a women's health clinician calculating a Cleo Score for a patient at Cleo's Secret, a GP-led women's health optimisation clinic in Melbourne. The Cleo Score is a 0-100 composite health metric across four dimensions, designed for women aged 35-55.\n\nYou receive the patient's intake conversation summary and (optionally) their approved pathology results. You calculate:\n\n1. An OVERALL CLEO SCORE (0-100) reflecting current state of wellbeing\n2. Four SUB-DIMENSION scores (0-100 each)\n3. A pattern/archetype label describing their dominant picture\n4. Reasoning for each sub-dimension\n5. The one dimension with greatest improvement potential\n6. 3-5 concrete actions to lift the score\n\nSCORING PHILOSOPHY:\n- 90-100: Optimal \u2014 thriving, all systems well\n- 75-89: Good \u2014 generally well with minor optimisation opportunities\n- 60-74: Moderate \u2014 meaningful symptoms or markers needing attention\n- 40-59: Significant burden \u2014 multiple areas need clinical focus\n- 0-39: Struggling \u2014 substantial intervention warranted\n\nUse functional/optimal ranges, not just lab \"normal\" ranges. A TSH of 3.5 is \"normal\" but suboptimal for a symptomatic woman \u2014 score accordingly.\n\nFor women without pathology data, score based on intake alone. Be generous to subjective wellbeing \u2014 if someone reports feeling great with no major issues, give them a high score even without pathology to \"prove\" it.\n\nTHE FOUR DIMENSIONS:\n\n1. HORMONAL BALANCE (0-100)\n   Inputs: menstrual symptoms, cycle regularity, perimenopause/menopause symptoms, libido, breast tenderness, mood swings, hot flushes; pathology of TSH, free T3/T4, oestradiol, progesterone, FSH, LH, AMH, prolactin, SHBG, testosterone\n\n2. METABOLIC HEALTH (0-100)\n   Inputs: weight stability, body composition concerns, sugar cravings, energy after meals; pathology of HbA1c, fasting glucose, insulin, ferritin, vitamin D, lipid panel\n\n3. SLEEP & RECOVERY (0-100)\n   Inputs: sleep duration, sleep quality, waking through the night, morning fatigue, naps needed, recovery from exercise\n\n4. VITALITY & MOOD (0-100)\n   Inputs: energy levels, mood patterns, anxiety, stress capacity, motivation, libido (also influences this), life satisfaction, brain fog\n\nPATTERN/ARCHETYPE LABELS (pick the most accurate, you can invent your own):\n- \"Thriving\" \u2014 all dimensions 80+\n- \"Hormonal Shift\" \u2014 hormonal score notably lower than others\n- \"Energy Drain\" \u2014 vitality + sleep both low\n- \"Metabolic Imbalance\" \u2014 metabolic score notably lower\n- \"Sleep Struggle\" \u2014 sleep score notably lower\n- \"Compound Burden\" \u2014 three or more dimensions below 60\n- \"Foundation Building\" \u2014 all dimensions in the 60s-70s, no specific outlier\n- Or create a more specific archetype if appropriate\n\nOUTPUT: A single JSON object with no markdown, no preamble, no backticks. Format:\n\n{\n  \"overall_score\": 67,\n  \"pattern\": \"Hormonal Shift\",\n  \"pattern_description\": \"Your overall picture suggests early perimenopausal changes are driving fatigue and mood symptoms. Your metabolic and sleep foundations are reasonable, which gives us strong ground to work from.\",\n  \"hormonal_score\": 52,\n  \"hormonal_reasoning\": \"Heavy, irregular periods with PMS mood symptoms plus borderline ferritin of 38 suggest the hormonal axis is under pressure. Your TSH of 2.8 is within range but on the higher side for symptomatic women.\",\n  \"metabolic_score\": 78,\n  \"metabolic_reasoning\": \"Stable weight, no glucose dysregulation, vitamin D adequate. Slight ferritin gap is the only notable finding.\",\n  \"sleep_score\": 71,\n  \"sleep_reasoning\": \"You're getting 7 hours most nights and feel reasonably rested, though noted waking once or twice \u2014 likely connected to the hormonal picture.\",\n  \"vitality_score\": 61,\n  \"vitality_reasoning\": \"Fatigue and afternoon slumps come through clearly. Your stress tolerance has dropped over the last few months which often accompanies the changes we're seeing hormonally.\",\n  \"improvement_focus\": \"hormonal\",\n  \"improvement_actions\": [\n    \"Discuss iron supplementation with your GP to lift ferritin into optimal range (>70)\",\n    \"Consider asking your GP about thyroid antibody testing given borderline TSH and symptoms\",\n    \"Track your cycle for 2-3 months in a symptom journal to identify patterns\",\n    \"Discuss whether progesterone therapy or HRT is appropriate for your stage\"\n  ]\n}\n\nThe overall_score should reflect the holistic picture, weighted by what's most affecting their life \u2014 NOT a simple average. If hormonal is dragging vitality, weight hormonal more heavily.";

  // Small uppercase tag pill, used for intake/pathology signals + triggers.
  function Chip(props) {
    return (
      <span style={{
        display: 'inline-block',
        fontFamily: FF,
        fontSize: 'clamp(12px, 1vw, 14px)',
        fontWeight: 600,
        color: P.TX,
        background: 'rgba(232, 221, 196, 0.55)',
        border: '1px solid ' + P.SAND3,
        padding: '6px 12px',
        marginRight: 8,
        marginBottom: 8,
      }}>{props.children}</span>
    );
  }

  return (
    <div>
      {/* \u2500\u2500\u2500 Intro banner \u2014 gold-edged inset \u2500\u2500\u2500 */}
      <div style={{
        padding: 'clamp(24px, 2.6vw, 32px) clamp(24px, 2.6vw, 36px)',
        background: 'rgba(201, 169, 97, 0.06)',
        borderLeft: '4px solid ' + P.GO,
        marginBottom: 'clamp(20px, 2.2vw, 28px)',
      }}>
        <div style={{
          fontFamily: FF,
          fontSize: 'clamp(12px, 1.05vw, 14px)',
          fontWeight: 700,
          letterSpacing: '0.28em',
          textTransform: 'uppercase',
          color: P.BR2,
          marginBottom: 14,
        }}>Scoring framework</div>
        <p style={{
          fontFamily: FF,
          fontSize: 'clamp(16px, 1.4vw, 19px)',
          color: P.TX,
          lineHeight: 1.7,
          margin: 0,
        }}>
          Cleo Score is generated by Claude Sonnet 4.6 from each patient&apos;s intake conversation and approved pathology. This view documents the current scoring framework: the dimensions, bands, archetypes, and clinical reasoning the model is calibrated against.
        </p>
      </div>

      {/* \u2500\u2500\u2500 The four dimensions \u2500\u2500\u2500 */}
      <CardWrap title="The four dimensions">
        {DIMENSIONS.map(function(d, di) {
          return (
            <div key={d.key} style={{
              padding: di === 0
                ? '0 0 clamp(22px, 2.4vw, 30px)'
                : 'clamp(22px, 2.4vw, 30px) 0',
              borderBottom: di < DIMENSIONS.length - 1 ? '1px solid ' + P.SAND3 : 'none',
            }}>
              <h4 style={{
                fontFamily: FF,
                fontSize: 'clamp(20px, 1.8vw, 24px)',
                fontWeight: 500,
                color: P.INK,
                margin: '0 0 10px',
                letterSpacing: '-0.005em',
              }}>{d.name}</h4>
              <p style={{
                fontFamily: FF,
                fontSize: 'clamp(15px, 1.3vw, 17px)',
                fontStyle: 'italic',
                color: P.BR2,
                lineHeight: 1.55,
                margin: '0 0 18px',
              }}>{d.definition}</p>

              <div style={{
                fontFamily: FF,
                fontSize: 'clamp(11px, 0.95vw, 12px)',
                fontWeight: 700,
                letterSpacing: '0.28em',
                textTransform: 'uppercase',
                color: P.TF,
                marginBottom: 10,
              }}>Intake signals</div>
              <div style={{ marginBottom: d.pathologyMarkers ? 18 : 0 }}>
                {d.intakeSignals.map(function(s, si) {
                  return <Chip key={si}>{s}</Chip>;
                })}
              </div>

              {d.pathologyMarkers && (
                <React.Fragment>
                  <div style={{
                    fontFamily: FF,
                    fontSize: 'clamp(11px, 0.95vw, 12px)',
                    fontWeight: 700,
                    letterSpacing: '0.28em',
                    textTransform: 'uppercase',
                    color: P.TF,
                    marginBottom: 10,
                  }}>Pathology markers</div>
                  <div>
                    {d.pathologyMarkers.map(function(m, mi) {
                      return <Chip key={mi}>{m}</Chip>;
                    })}
                  </div>
                </React.Fragment>
              )}
            </div>
          );
        })}
      </CardWrap>

      {/* \u2500\u2500\u2500 Score bands \u2500\u2500\u2500 */}
      <CardWrap title="Score bands">
        {SCORE_BANDS.map(function(b, bi) {
          return (
            <div key={b.range} style={{
              display: 'grid',
              gridTemplateColumns: '14px 96px 1fr',
              gap: 'clamp(14px, 1.6vw, 22px)',
              alignItems: 'center',
              padding: 'clamp(14px, 1.6vw, 20px) 0',
              borderBottom: bi < SCORE_BANDS.length - 1 ? '1px solid ' + P.SAND3 : 'none',
            }}>
              <div style={{
                width: 14,
                height: 14,
                background: b.color,
                transform: 'rotate(45deg)',
              }} />
              <div style={{
                fontFamily: FF,
                fontSize: 'clamp(15px, 1.3vw, 17px)',
                fontWeight: 700,
                color: P.INK,
                fontVariantNumeric: 'tabular-nums',
              }}>{b.range}</div>
              <div>
                <div style={{
                  fontFamily: FF,
                  fontSize: 'clamp(16px, 1.4vw, 18px)',
                  fontWeight: 600,
                  color: P.INK,
                  marginBottom: 4,
                  letterSpacing: '-0.005em',
                }}>{b.label}</div>
                <div style={{
                  fontFamily: FF,
                  fontSize: 'clamp(14px, 1.2vw, 16px)',
                  color: P.TL,
                  lineHeight: 1.5,
                }}>{b.meaning}</div>
              </div>
            </div>
          );
        })}
      </CardWrap>

      {/* \u2500\u2500\u2500 Pattern archetypes \u2500\u2500\u2500 */}
      <CardWrap title="Pattern archetypes">
        {PATTERN_ARCHETYPES.map(function(a, ai) {
          return (
            <div key={a.name} style={{
              display: 'grid',
              gridTemplateColumns: 'minmax(180px, 1fr) 2fr',
              gap: 'clamp(16px, 1.8vw, 28px)',
              alignItems: 'baseline',
              padding: 'clamp(14px, 1.6vw, 20px) 0',
              borderBottom: ai < PATTERN_ARCHETYPES.length - 1 ? '1px solid ' + P.SAND3 : 'none',
            }}>
              <div style={{
                fontFamily: FF,
                fontSize: 'clamp(17px, 1.5vw, 20px)',
                fontStyle: 'italic',
                fontWeight: 500,
                color: P.BR2,
                letterSpacing: '-0.003em',
              }}>{a.name}</div>
              <div style={{
                fontFamily: FF,
                fontSize: 'clamp(14px, 1.2vw, 16px)',
                color: P.TX,
                lineHeight: 1.65,
              }}>{a.condition}</div>
            </div>
          );
        })}
        <p style={{
          fontFamily: FF,
          fontSize: 'clamp(14px, 1.2vw, 16px)',
          fontStyle: 'italic',
          color: P.TF,
          lineHeight: 1.6,
          margin: 'clamp(18px, 2vw, 24px) 0 0',
        }}>
          The model may also create a more specific archetype where appropriate.
        </p>
      </CardWrap>

      {/* \u2500\u2500\u2500 Scoring philosophy \u2500\u2500\u2500 */}
      <CardWrap title="Scoring philosophy">
        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
          {PHILOSOPHY.map(function(item, i) {
            return (
              <li key={i} style={{
                position: 'relative',
                paddingLeft: 32,
                marginBottom: i === PHILOSOPHY.length - 1 ? 0 : 18,
                fontFamily: FF,
                fontSize: 'clamp(16px, 1.4vw, 19px)',
                lineHeight: 1.7,
                color: P.TX,
              }}>
                <span style={{
                  position: 'absolute',
                  left: 10,
                  top: '0.65em',
                  width: 9,
                  height: 9,
                  background: P.BR,
                  transform: 'rotate(45deg)',
                }} />
                {item}
              </li>
            );
          })}
        </ul>
      </CardWrap>

      {/* \u2500\u2500\u2500 Scoring engine \u2500\u2500\u2500 */}
      <CardWrap title="Scoring engine">
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
          gap: 'clamp(18px, 2vw, 28px)',
          marginBottom: 'clamp(22px, 2.4vw, 30px)',
        }}>
          <div>
            <div style={{
              fontFamily: FF,
              fontSize: 'clamp(11px, 0.95vw, 12px)',
              fontWeight: 700,
              letterSpacing: '0.28em',
              textTransform: 'uppercase',
              color: P.TF,
              marginBottom: 8,
            }}>Model</div>
            <div style={{
              fontFamily: FF,
              fontSize: 'clamp(16px, 1.4vw, 18px)',
              fontWeight: 600,
              color: P.INK,
              fontVariantNumeric: 'tabular-nums',
            }}>{ENGINE.model}</div>
          </div>
          <div>
            <div style={{
              fontFamily: FF,
              fontSize: 'clamp(11px, 0.95vw, 12px)',
              fontWeight: 700,
              letterSpacing: '0.28em',
              textTransform: 'uppercase',
              color: P.TF,
              marginBottom: 8,
            }}>Max tokens</div>
            <div style={{
              fontFamily: FF,
              fontSize: 'clamp(16px, 1.4vw, 18px)',
              fontWeight: 600,
              color: P.INK,
              fontVariantNumeric: 'tabular-nums',
            }}>{ENGINE.maxTokens.toLocaleString()}</div>
          </div>
          <div>
            <div style={{
              fontFamily: FF,
              fontSize: 'clamp(11px, 0.95vw, 12px)',
              fontWeight: 700,
              letterSpacing: '0.28em',
              textTransform: 'uppercase',
              color: P.TF,
              marginBottom: 8,
            }}>Trigger events</div>
            <div>
              {ENGINE.triggers.map(function(t, ti) {
                return <Chip key={ti}>{t}</Chip>;
              })}
            </div>
          </div>
        </div>

        <details style={{
          borderTop: '1px solid ' + P.SAND3,
          paddingTop: 'clamp(18px, 2vw, 24px)',
        }}>
          <summary style={{
            fontFamily: FF,
            fontSize: 'clamp(13px, 1.15vw, 15px)',
            fontWeight: 700,
            letterSpacing: '0.28em',
            textTransform: 'uppercase',
            color: P.BR2,
            cursor: 'pointer',
            padding: '6px 0',
          }}>View full system prompt</summary>
          <pre style={{
            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
            fontSize: 'clamp(12px, 1vw, 14px)',
            lineHeight: 1.65,
            color: P.TX,
            background: P.PAP,
            border: '1px solid ' + P.SAND3,
            padding: 'clamp(18px, 2vw, 24px)',
            marginTop: 'clamp(14px, 1.6vw, 20px)',
            maxHeight: 480,
            overflow: 'auto',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
          }}>{SYSTEM_PROMPT}</pre>
        </details>
      </CardWrap>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════
// BILLING VIEW
// ═════════════════════════════════════════════════════════════════════════
function BillingView() {
  var dataState = useState(null);
  var data = dataState[0];
  var setData = dataState[1];

  var loadingState = useState(true);
  var loading = loadingState[0];
  var setLoading = loadingState[1];

  var errorState = useState(null);
  var error = errorState[0];
  var setError = errorState[1];

  useEffect(function() {
    var cancelled = false;
    setLoading(true);
    setError(null);

    fetch('/api/admin-billing-summary', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({}),
    })
      .then(function(r) { return r.json(); })
      .then(function(result) {
        if (cancelled) return;
        if (result && result.kpis) {
          setData(result);
          setLoading(false);
        } else {
          setError((result && result.error) || 'Failed to load billing data');
          setLoading(false);
        }
      })
      .catch(function() {
        if (cancelled) return;
        setError('Network error');
        setLoading(false);
      });

    return function() { cancelled = true; };
  }, []);

  // ── Formatters ────────────────────────────────────────────────────────
  function dollarsFromCents(cents) {
    if (cents == null) return '—';
    var dollars = Math.round(cents / 100);
    return '$' + dollars.toLocaleString();
  }
  function dollarsFromCentsDetailed(cents) {
    if (cents == null) return '—';
    return '$' + (cents / 100).toLocaleString(undefined, {
      minimumFractionDigits: 2, maximumFractionDigits: 2,
    });
  }
  function formatDate(iso) {
    if (!iso) return '—';
    return new Date(iso).toLocaleDateString('en-AU', {
      day: 'numeric', month: 'short', year: 'numeric',
    });
  }
  function formatDateTime(iso) {
    if (!iso) return '—';
    var d = new Date(iso);
    return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short' }) +
           ' ' + d.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' });
  }

  // ── Status badge palettes ─────────────────────────────────────────────
  function txnBadge(status) {
    if (status === 'paid')      return { bg: P.GREEN_BG, fg: P.GREEN, border: P.GREEN };
    if (status === 'refunded')  return { bg: P.AMBER_BG, fg: P.AMBER, border: P.AMBER };
    if (status === 'failed')    return { bg: P.RED_BG,   fg: P.RED,   border: P.RED };
    if (status === 'cancelled') return { bg: 'rgba(122,96,64,0.10)',  fg: P.TX,  border: P.SAND3 };
    if (status === 'charging')  return { bg: 'rgba(201,169,97,0.18)', fg: P.BR2, border: P.GO };
    return { bg: 'rgba(232,221,196,0.55)', fg: P.TF, border: P.SAND3 }; // pending
  }
  function webhookBadge(status) {
    if (status === 'resolved')  return { bg: P.GREEN_BG, fg: P.GREEN, border: P.GREEN };
    if (status === 'orphan')    return { bg: P.AMBER_BG, fg: P.AMBER, border: P.AMBER };
    if (status === 'invalid')   return { bg: P.RED_BG,   fg: P.RED,   border: P.RED };
    if (status === 'unhandled') return { bg: 'rgba(122,96,64,0.10)',  fg: P.TF,  border: P.SAND3 };
    return { bg: 'rgba(232,221,196,0.55)', fg: P.TF, border: P.SAND3 }; // received
  }
  function txnRowBorder(status) {
    return status === 'failed' ? '3px solid ' + P.RED : 'none';
  }
  function webhookRowBorder(status) {
    if (status === 'orphan')  return '3px solid ' + P.AMBER;
    if (status === 'invalid') return '3px solid ' + P.RED;
    return 'none';
  }

  // ── Loading state ─────────────────────────────────────────────────────
  if (loading) {
    return (
      <div style={{
        padding: 'clamp(80px, 12vw, 160px) 24px',
        textAlign: 'center',
        background: P.PAP,
        border: '1px solid ' + P.SAND3,
      }}>
        <Spinner />
      </div>
    );
  }

  // ── Error state ───────────────────────────────────────────────────────
  if (error || !data) {
    return (
      <div style={{
        padding: 'clamp(48px, 6vw, 80px) 24px',
        textAlign: 'center',
        background: P.PAP,
        border: '1px solid ' + P.SAND3,
      }}>
        <p style={{
          fontFamily: FF,
          fontStyle: 'italic',
          fontSize: 'clamp(16px, 1.4vw, 18px)',
          color: P.RED,
          margin: 0,
        }}>
          {error || 'Could not load billing data'}. Try refreshing the page.
        </p>
      </div>
    );
  }

  var kpis = data.kpis;
  var transactions = data.transactions || [];
  var webhookEvents = data.webhookEvents || [];

  // KPI sub-line strings
  var mrrSubLine = MEMBERSHIP_TIER_DISPLAY.silver + ' ' + kpis.mrrTierBreakdown.silver +
                   ', ' + MEMBERSHIP_TIER_DISPLAY.gold  + ' ' + kpis.mrrTierBreakdown.gold +
                   ', ' + MEMBERSHIP_TIER_DISPLAY.black + ' ' + kpis.mrrTierBreakdown.black;
  var planSubLine = kpis.planStatusBreakdown.enrolled + ' enrolled, ' +
                    kpis.planStatusBreakdown.paused   + ' paused, ' +
                    kpis.planStatusBreakdown.completed + ' completed';
  var failedSubLine = kpis.failedInstalments30d === 0
    ? 'All instalments succeeding'
    : 'Cron will retry up to 3 times';
  var pendingSubLine = kpis.pendingRevenue30dCount + ' instalment' +
                       (kpis.pendingRevenue30dCount === 1 ? '' : 's') + ' due';
  var revenueTrend;
  if (kpis.revenueDeltaPercent === null) {
    revenueTrend = 'No prior month data yet';
  } else {
    var sign = kpis.revenueDeltaPercent > 0 ? '+' : '';
    revenueTrend = sign + kpis.revenueDeltaPercent + '% vs last month';
  }

  return (
    <div>
      {/* ─── KPI grid ─── */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
        gap: 12,
        marginBottom: 'clamp(16px, 1.8vw, 22px)',
      }}>
        <KPI
          label="Revenue this month"
          value={dollarsFromCents(kpis.revenueThisMonthCents)}
          trend={revenueTrend}
          trendGood={kpis.revenueDeltaPercent !== null && kpis.revenueDeltaPercent > 0}
        />
        <KPI
          label="MRR"
          value={dollarsFromCents(kpis.mrrCents)}
          trend={mrrSubLine}
        />
        <KPI
          label="Refunded this month"
          value={dollarsFromCents(kpis.refundedThisMonthCents)}
          trend={kpis.refundedThisMonthCents === 0 ? 'No refunds this month' : null}
        />
        <KPI
          label="Active plans"
          value={String(kpis.activePlans)}
          trend={planSubLine}
        />
        <KPI
          label="Failed instalments 30d"
          value={String(kpis.failedInstalments30d)}
          trend={failedSubLine}
          trendGood={kpis.failedInstalments30d === 0}
        />
        <KPI
          label="Pending revenue 30d"
          value={dollarsFromCents(kpis.pendingRevenue30dCents)}
          trend={pendingSubLine}
        />
      </div>

      {/* ─── Recent transactions ─── */}
      <CardWrap title="Recent transactions">
        {transactions.length === 0 ? (
          <div style={{ padding: 'clamp(40px, 5vw, 60px)', textAlign: 'center' }}>
            <p style={{
              fontFamily: FF,
              fontStyle: 'italic',
              fontSize: 'clamp(16px, 1.4vw, 18px)',
              color: P.BR2,
              margin: 0,
            }}>No transactions yet.</p>
          </div>
        ) : (
          <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
            <div style={{
              display: 'grid',
              gridTemplateColumns: '1.1fr 2fr 1.4fr 0.8fr 1fr 110px',
              gap: 16,
              padding: '14px 16px',
              minWidth: 880,
              fontFamily: FF,
              fontSize: 'clamp(12px, 1.05vw, 14px)',
              fontWeight: 700,
              letterSpacing: '0.28em',
              textTransform: 'uppercase',
              color: P.BR2,
              borderBottom: '1px solid ' + P.SAND3,
              marginBottom: 6,
            }}>
              <span>Date</span>
              <span>Patient</span>
              <span>Package</span>
              <span>Instalment</span>
              <span>Amount</span>
              <span>Status</span>
            </div>

            {transactions.map(function(t, i) {
              var b = txnBadge(t.status);
              var rowDate = t.paidAt || t.dueAt;
              return (
                <div key={t.id} style={{
                  display: 'grid',
                  gridTemplateColumns: '1.1fr 2fr 1.4fr 0.8fr 1fr 110px',
                  gap: 16,
                  padding: '16px',
                  minWidth: 880,
                  alignItems: 'center',
                  borderBottom: i < transactions.length - 1 ? '1px solid ' + P.SAND3 : 'none',
                  borderLeft: txnRowBorder(t.status),
                }}>
                  <div style={{
                    fontFamily: FF,
                    fontSize: 'clamp(14px, 1.2vw, 16px)',
                    fontStyle: 'italic',
                    color: P.BR2,
                  }}>{formatDate(rowDate)}</div>
                  <div style={{ minWidth: 0 }}>
                    <div style={{
                      fontFamily: FF,
                      fontSize: 'clamp(15px, 1.3vw, 17px)',
                      fontWeight: 600,
                      color: P.INK,
                      letterSpacing: '-0.005em',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}>{t.patientName || '—'}</div>
                    {t.patientEmail && (
                      <div style={{
                        fontFamily: FS,
                        fontSize: 'clamp(12px, 1vw, 13px)',
                        color: P.TF,
                        marginTop: 2,
                        fontStyle: 'italic',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}>{t.patientEmail}</div>
                    )}
                  </div>
                  <div style={{
                    fontFamily: FF,
                    fontSize: 'clamp(14px, 1.2vw, 16px)',
                    color: P.TX,
                  }}>{t.packageName || '—'}</div>
                  <div style={{
                    fontFamily: FF,
                    fontSize: 'clamp(14px, 1.2vw, 16px)',
                    color: P.TL,
                    fontVariantNumeric: 'tabular-nums',
                  }}>{t.instalmentNumber} of 3</div>
                  <div style={{
                    fontFamily: FF,
                    fontSize: 'clamp(16px, 1.4vw, 18px)',
                    fontWeight: 600,
                    color: (t.status === 'failed' || t.status === 'refunded') ? P.RED : P.INK,
                    fontVariantNumeric: 'tabular-nums',
                  }}>{dollarsFromCentsDetailed(t.amountCents)}</div>
                  <span style={{
                    fontFamily: FF,
                    fontSize: 'clamp(11px, 0.95vw, 12px)',
                    fontWeight: 700,
                    letterSpacing: '0.22em',
                    textTransform: 'uppercase',
                    padding: '7px 13px',
                    background: b.bg,
                    color: b.fg,
                    border: '1px solid ' + b.border,
                    width: 'fit-content',
                  }}>{t.status}</span>
                </div>
              );
            })}
          </div>
        )}
      </CardWrap>

      {/* ─── Webhook reconciliation ─── */}
      <CardWrap title="Webhook reconciliation">
        {webhookEvents.length === 0 ? (
          <div style={{ padding: 'clamp(40px, 5vw, 60px)', textAlign: 'center' }}>
            <p style={{
              fontFamily: FF,
              fontStyle: 'italic',
              fontSize: 'clamp(16px, 1.4vw, 18px)',
              color: P.BR2,
              margin: 0,
            }}>No webhook events yet.</p>
          </div>
        ) : (
          <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
            <div style={{
              display: 'grid',
              gridTemplateColumns: '1.2fr 1.8fr 1fr 1.4fr 1.6fr',
              gap: 16,
              padding: '14px 16px',
              minWidth: 880,
              fontFamily: FF,
              fontSize: 'clamp(12px, 1.05vw, 14px)',
              fontWeight: 700,
              letterSpacing: '0.28em',
              textTransform: 'uppercase',
              color: P.BR2,
              borderBottom: '1px solid ' + P.SAND3,
              marginBottom: 6,
            }}>
              <span>Timestamp</span>
              <span>Event type</span>
              <span>Status</span>
              <span>Patient</span>
              <span>Notes</span>
            </div>

            {webhookEvents.map(function(w, i) {
              var b = webhookBadge(w.status);
              return (
                <div key={w.id} style={{
                  display: 'grid',
                  gridTemplateColumns: '1.2fr 1.8fr 1fr 1.4fr 1.6fr',
                  gap: 16,
                  padding: '16px',
                  minWidth: 880,
                  alignItems: 'center',
                  borderBottom: i < webhookEvents.length - 1 ? '1px solid ' + P.SAND3 : 'none',
                  borderLeft: webhookRowBorder(w.status),
                }}>
                  <div style={{
                    fontFamily: FF,
                    fontSize: 'clamp(13px, 1.1vw, 15px)',
                    fontStyle: 'italic',
                    color: P.BR2,
                  }}>{formatDateTime(w.receivedAt)}</div>
                  <div style={{
                    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
                    fontSize: 'clamp(12px, 1vw, 14px)',
                    color: P.TX,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}>{w.stripeEventType}</div>
                  <span style={{
                    fontFamily: FF,
                    fontSize: 'clamp(11px, 0.95vw, 12px)',
                    fontWeight: 700,
                    letterSpacing: '0.22em',
                    textTransform: 'uppercase',
                    padding: '7px 13px',
                    background: b.bg,
                    color: b.fg,
                    border: '1px solid ' + b.border,
                    width: 'fit-content',
                  }}>{w.status}</span>
                  <div style={{
                    fontFamily: FF,
                    fontSize: 'clamp(14px, 1.2vw, 16px)',
                    color: w.patientName ? P.TX : P.TF,
                    fontStyle: w.patientName ? 'normal' : 'italic',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}>{w.patientName || 'No patient linked'}</div>
                  <div style={{
                    fontFamily: FF,
                    fontSize: 'clamp(13px, 1.1vw, 15px)',
                    color: P.TL,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}>{w.resolutionNotes || '—'}</div>
                </div>
              );
            })}
          </div>
        )}
      </CardWrap>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════
// AUTOMATIONS VIEW
// ═════════════════════════════════════════════════════════════════════════
function AutomationsView() {
  var autosInit = [
    { trigger: 'Audit completed',                  action: 'Send result summary and book CTA email',                timing: '2 min delay',     on: true },
    { trigger: 'Diagnostic payment received',      action: 'Confirm booking, unlock portal, notify GP',             timing: 'Immediate',       on: true },
    { trigger: '14 days, no pathology',            action: 'Send pathology reminder email',                         timing: 'Auto-trigger',    on: true },
    { trigger: 'Pathology uploaded',               action: 'Notify admin and GP: results ready',                    timing: 'Immediate',       on: true },
    { trigger: 'Report published',                 action: 'Send client notification: your report is ready',        timing: 'Immediate',       on: true },
    { trigger: 'Week 1 module accessed',           action: 'Send welcome sequence (Days 0, 1, 2)',                  timing: 'Staggered',       on: true },
    { trigger: 'Module not accessed 10 days',      action: 'Send gentle re-engagement email',                       timing: 'Auto-trigger',    on: true },
    { trigger: 'Week 4 review, 24hr before',       action: 'Send appointment reminder',                             timing: 'Scheduled',       on: true },
    { trigger: 'Week 8 review, 24hr before',       action: 'Send appointment reminder',                             timing: 'Scheduled',       on: true },
    { trigger: 'Week 10 module accessed',          action: 'Show membership banner and send offer email',           timing: 'Immediate',       on: true },
    { trigger: 'Week 12 review, 24hr before',      action: 'Send appointment reminder',                             timing: 'Scheduled',       on: true },
    { trigger: 'Membership enrolled',              action: 'Send welcome to membership email',                      timing: 'Immediate',       on: true },
    { trigger: 'Quarterly review due, 7 days',     action: 'Send review booking reminder',                          timing: 'Scheduled',       on: true },
    { trigger: 'No portal login 14 days',          action: 'Send re-engagement email',                              timing: 'Auto-trigger',    on: true },
    { trigger: 'Payment failed',                   action: 'Retry 3 times, notify client and admin',                timing: 'Auto-trigger',    on: true },
    { trigger: 'Anniversary month',                action: 'Send progress summary email',                           timing: 'Monthly',         on: true },
  ];

  var autosState = useState(autosInit);
  var autos = autosState[0];
  var setAutos = autosState[1];

  function toggle(i) {
    setAutos(function(prev) {
      return prev.map(function(item, idx) {
        return idx === i ? Object.assign({}, item, { on: !item.on }) : item;
      });
    });
  }

  var activeCount = autos.filter(function(a) { return a.on; }).length;

  return (
    <div>
      <div style={{
        padding: 'clamp(24px, 2.6vw, 32px) clamp(24px, 2.6vw, 36px)',
        background: 'rgba(201, 169, 97, 0.06)',
        borderLeft: '4px solid ' + P.GO,
        marginBottom: 'clamp(20px, 2.2vw, 28px)',
      }}>
        <p style={{
          fontFamily: FF,
          fontSize: 'clamp(16px, 1.4vw, 19px)',
          color: P.TX,
          lineHeight: 1.7,
          margin: 0,
        }}>
          Toggle automations on or off. All emails use the Cleo branded template. Adjust timing without a developer.{' '}
          <strong style={{ color: P.BR2, fontWeight: 700 }}>{activeCount} of {autos.length} active.</strong>
        </p>
      </div>

      <CardWrap title="Email and trigger automations">
        <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
        {autos.map(function(a, i) {
          return (
            <div key={i} style={{
              display: 'grid',
              gridTemplateColumns: '2fr 2.2fr 150px 100px',
              gap: 16,
              alignItems: 'center',
              padding: 'clamp(16px, 1.8vw, 22px) 0',
              minWidth: 680,
              borderBottom: i < autos.length - 1 ? '1px solid ' + P.SAND3 : 'none',
            }}>
              <div style={{
                fontFamily: FF,
                fontSize: 'clamp(16px, 1.4vw, 18px)',
                fontWeight: 600,
                color: a.on ? P.INK : P.TF,
                letterSpacing: '-0.008em',
                opacity: a.on ? 1 : 0.6,
              }}>{a.trigger}</div>
              <div style={{
                fontFamily: FF,
                fontSize: 'clamp(14px, 1.2vw, 16px)',
                color: a.on ? P.TX : P.TF,
                opacity: a.on ? 1 : 0.6,
                lineHeight: 1.5,
              }}>{a.action}</div>
              <div style={{
                fontFamily: FF,
                fontSize: 'clamp(11px, 0.95vw, 12px)',
                fontWeight: 700,
                letterSpacing: '0.22em',
                textTransform: 'uppercase',
                color: P.TF,
              }}>{a.timing}</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div
                  onClick={function() { toggle(i); }}
                  style={{
                    width: 42,
                    height: 22,
                    background: a.on ? P.GO : P.SAND3,
                    position: 'relative',
                    cursor: 'pointer',
                    transition: 'background 0.2s',
                    flexShrink: 0,
                  }}>
                  <div style={{
                    width: 18,
                    height: 18,
                    background: P.PAP,
                    position: 'absolute',
                    top: 2,
                    left: a.on ? 22 : 2,
                    transition: 'left 0.2s',
                  }} />
                </div>
                <span style={{
                  fontFamily: FF,
                  fontSize: 'clamp(11px, 0.95vw, 12px)',
                  fontWeight: 700,
                  letterSpacing: '0.22em',
                  textTransform: 'uppercase',
                  color: a.on ? P.BR2 : P.TF,
                }}>{a.on ? 'On' : 'Off'}</span>
              </div>
            </div>
          );
        })}
        </div>
      </CardWrap>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════
// CONTENT CMS VIEW
// ═════════════════════════════════════════════════════════════════════════
// ═════════════════════════════════════════════════════════════════════════
// ASSET PICKER MODAL — reusable component for picking/uploading images
// ═════════════════════════════════════════════════════════════════════════
//
// Renders a centered modal overlaying the page. Three states:
//   • Library — grid of existing assets, click one to select
//   • Uploading — progress while uploading new file
//   • Done — closes via onSelect callback with chosen asset
//
// Used by Pattern editor (hero image), Module editor (hero image, downloads),
// Site copy editor (optional). Public bucket so URLs are stable.
//
function AssetPicker(props) {
  var onClose = props.onClose;
  var onSelect = props.onSelect;
  var assetTypeFilter = props.assetTypeFilter || 'all';
  var title = props.title || 'Choose an image or file';

  var assetsState = useState([]);
  var assets = assetsState[0];
  var setAssets = assetsState[1];

  var loadingState = useState(true);
  var loading = loadingState[0];
  var setLoading = loadingState[1];

  var uploadingState = useState(false);
  var uploading = uploadingState[0];
  var setUploading = uploadingState[1];

  var uploadErrorState = useState(null);
  var uploadError = uploadErrorState[0];
  var setUploadError = uploadErrorState[1];

  var refreshState = useState(0);
  var refresh = refreshState[0];
  var setRefresh = refreshState[1];

  // Load asset library
  useEffect(function() {
    var cancelled = false;
    setLoading(true);
    fetch('/api/admin-list-assets?assetType=' + encodeURIComponent(assetTypeFilter))
      .then(function(r) { return r.json(); })
      .then(function(data) {
        if (cancelled) return;
        setAssets(data.assets || []);
        setLoading(false);
      })
      .catch(function() {
        if (cancelled) return;
        setLoading(false);
      });
    return function() { cancelled = true; };
  }, [refresh, assetTypeFilter]);

  function handleFileChange(e) {
    var file = e.target.files && e.target.files[0];
    if (!file) return;

    setUploadError(null);
    setUploading(true);

    var reader = new FileReader();
    reader.onload = function() {
      var base64 = reader.result;
      fetch('/api/admin-upload-asset', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fileName: file.name,
          fileData: base64,
          mimeType: file.type,
        }),
      })
        .then(function(r) { return r.json().then(function(j) { return { ok: r.ok, body: j }; }); })
        .then(function(resp) {
          setUploading(false);
          if (resp.ok && resp.body.success && resp.body.asset) {
            setRefresh(refresh + 1);
            // Auto-select the freshly uploaded asset
            if (onSelect) onSelect(resp.body.asset);
          } else {
            setUploadError((resp.body && resp.body.error) || 'Upload failed');
          }
        })
        .catch(function() {
          setUploading(false);
          setUploadError('Network error during upload');
        });
    };
    reader.onerror = function() {
      setUploading(false);
      setUploadError('Could not read file');
    };
    reader.readAsDataURL(file);
  }

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0,
        background: 'rgba(26,15,8,0.62)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 'clamp(16px, 4vw, 48px)',
        zIndex: 250,
      }}>
      <div
        onClick={function(e) { e.stopPropagation(); }}
        style={{
          background: P.PAP,
          border: '1px solid ' + P.SAND3,
          maxWidth: 920,
          width: '100%',
          maxHeight: '92vh',
          overflowY: 'auto',
          padding: 'clamp(24px, 2.8vw, 36px)',
        }}>

        <div style={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          marginBottom: 18,
        }}>
          <h2 style={{
            fontFamily: FF,
            fontSize: 'clamp(22px, 2.2vw, 28px)',
            fontWeight: 500,
            color: P.INK,
            margin: 0,
          }}>{title}</h2>
          <button
            type="button"
            onClick={onClose}
            style={{
              fontFamily: FS,
              fontSize: 'clamp(11px, 0.95vw, 12px)',
              fontWeight: 700,
              letterSpacing: '0.22em',
              textTransform: 'uppercase',
              color: P.BR2,
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              padding: 0,
            }}>Cancel</button>
        </div>

        {/* Upload area */}
        <label style={{
          display: 'block',
          border: '2px dashed ' + P.SAND3,
          padding: 'clamp(24px, 3vw, 36px)',
          textAlign: 'center',
          cursor: uploading ? 'wait' : 'pointer',
          background: uploading ? P.SAND : 'transparent',
          marginBottom: 24,
          transition: 'all 0.15s',
        }}>
          <input
            type="file"
            accept="image/*,application/pdf"
            disabled={uploading}
            onChange={handleFileChange}
            style={{ display: 'none' }}
          />
          {uploading ? (
            <div>
              <Spinner />
              <div style={{
                fontFamily: FF,
                fontSize: 'clamp(15px, 1.3vw, 17px)',
                fontStyle: 'italic',
                color: P.BR2,
                marginTop: 14,
              }}>Uploading...</div>
            </div>
          ) : (
            <div>
              <div style={{
                fontFamily: FF,
                fontSize: 'clamp(12px, 1.05vw, 14px)',
                fontWeight: 700,
                letterSpacing: '0.28em',
                textTransform: 'uppercase',
                color: P.BR2,
                marginBottom: 8,
              }}>Upload new</div>
              <div style={{
                fontFamily: FF,
                fontSize: 'clamp(15px, 1.3vw, 17px)',
                color: P.TX,
              }}>Click to choose an image or PDF. Max 10 MB.</div>
            </div>
          )}
        </label>

        {uploadError && (
          <p style={{
            fontFamily: FF,
            fontSize: 'clamp(14px, 1.2vw, 16px)',
            fontStyle: 'italic',
            color: P.RED,
            margin: '0 0 18px',
          }}>{uploadError}</p>
        )}

        {/* Asset library grid */}
        <div style={{
          fontFamily: FF,
          fontSize: 'clamp(12px, 1.05vw, 14px)',
          fontWeight: 700,
          letterSpacing: '0.28em',
          textTransform: 'uppercase',
          color: P.BR2,
          marginBottom: 14,
        }}>Or choose from library</div>

        {loading ? (
          <div style={{ padding: 'clamp(40px, 5vw, 60px)', textAlign: 'center' }}>
            <Spinner />
          </div>
        ) : assets.length === 0 ? (
          <div style={{ padding: 'clamp(40px, 5vw, 60px)', textAlign: 'center' }}>
            <p style={{
              fontFamily: FF,
              fontSize: 'clamp(15px, 1.3vw, 17px)',
              fontStyle: 'italic',
              color: P.TF,
              margin: 0,
            }}>No assets yet. Upload your first above.</p>
          </div>
        ) : (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
            gap: 12,
          }}>
            {assets.map(function(asset) {
              return (
                <div
                  key={asset.id}
                  onClick={function() { if (onSelect) onSelect(asset); }}
                  style={{
                    border: '1px solid ' + P.SAND3,
                    padding: 8,
                    cursor: 'pointer',
                    background: P.PAP,
                    transition: 'border-color 0.15s',
                  }}
                  onMouseEnter={function(e) { e.currentTarget.style.borderColor = P.BR; }}
                  onMouseLeave={function(e) { e.currentTarget.style.borderColor = P.SAND3; }}>
                  <div style={{
                    width: '100%',
                    aspectRatio: '1',
                    background: P.SAND,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'hidden',
                    marginBottom: 8,
                  }}>
                    {asset.asset_type === 'image' ? (
                      <img
                        src={asset.publicUrl}
                        alt={asset.alt_text || asset.filename}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                        }}
                      />
                    ) : (
                      <div style={{
                        fontFamily: FF,
                        fontSize: 'clamp(10px, 0.85vw, 11px)',
                        fontWeight: 700,
                        letterSpacing: '0.22em',
                        textTransform: 'uppercase',
                        color: P.TF,
                      }}>{asset.asset_type}</div>
                    )}
                  </div>
                  <div style={{
                    fontFamily: FS,
                    fontSize: 'clamp(11px, 0.95vw, 12px)',
                    color: P.TX,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}>{asset.filename}</div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════
// PATTERN NARRATIVE EDITOR — full-screen editor for a clinical pattern
// ═════════════════════════════════════════════════════════════════════════
//
// Renders inside the ContentView when admin clicks Edit on a pattern card.
// Eight narrative fields plus hero image picker. Save calls
// /api/admin-save-pattern and bubbles up via onSaved.
//
function PatternNarrativeEditor(props) {
  var pattern = props.pattern;
  var onClose = props.onClose;
  var onSaved = props.onSaved;

  var shortDescState = useState(pattern.short_descriptor || '');
  var profileDescState = useState(pattern.profile_description || '');
  var commonPresState = useState(pattern.common_presentation || '');
  var overviewState = useState(pattern.pattern_overview || '');
  var significanceState = useState(pattern.clinical_significance || '');
  var lifestyleState = useState(pattern.lifestyle_recommendations || '');
  var nutritionState = useState(pattern.nutritional_guidance || '');
  var supplementsState = useState(pattern.supplements_to_consider || '');
  var specialistState = useState(pattern.when_to_seek_specialist || '');
  var timelineState = useState(pattern.expected_timeline || '');
  var heroImagePathState = useState(pattern.hero_image_path || '');
  var heroImageUrlState = useState(pattern.heroImageUrl || '');

  var savingState = useState(false);
  var saving = savingState[0]; var setSaving = savingState[1];

  var errorState = useState(null);
  var error = errorState[0]; var setError = errorState[1];

  var savedState = useState(false);
  var saved = savedState[0]; var setSaved = savedState[1];

  var showPickerState = useState(false);
  var showPicker = showPickerState[0]; var setShowPicker = showPickerState[1];

  function handleSave() {
    setSaving(true);
    setError(null);
    setSaved(false);

    fetch('/api/admin-save-pattern', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        id: pattern.id,
        short_descriptor: shortDescState[0],
        profile_description: profileDescState[0],
        common_presentation: commonPresState[0],
        pattern_overview: overviewState[0],
        clinical_significance: significanceState[0],
        lifestyle_recommendations: lifestyleState[0],
        nutritional_guidance: nutritionState[0],
        supplements_to_consider: supplementsState[0],
        when_to_seek_specialist: specialistState[0],
        expected_timeline: timelineState[0],
        hero_image_path: heroImagePathState[0] || null,
      }),
    })
      .then(function(r) { return r.json().then(function(j) { return { ok: r.ok, body: j }; }); })
      .then(function(resp) {
        setSaving(false);
        if (resp.ok && resp.body.success) {
          setSaved(true);
          if (onSaved) onSaved(resp.body.pattern);
          setTimeout(function() { setSaved(false); }, 2500);
        } else {
          setError((resp.body && resp.body.error) || 'Save failed');
        }
      })
      .catch(function() {
        setSaving(false);
        setError('Network error');
      });
  }

  function handleAssetPicked(asset) {
    heroImagePathState[1](asset.storage_path);
    heroImageUrlState[1](asset.publicUrl);
    setShowPicker(false);
  }

  function field(label, hint, valueState, multiline, rows) {
    return (
      <div style={{ marginBottom: 22 }}>
        <label style={{
          display: 'block',
          fontFamily: FF,
          fontSize: 'clamp(11px, 0.95vw, 12px)',
          fontWeight: 700,
          letterSpacing: '0.28em',
          textTransform: 'uppercase',
          color: P.BR2,
          marginBottom: 6,
        }}>{label}</label>
        {hint && (
          <p style={{
            fontFamily: FF,
            fontSize: 'clamp(13px, 1.1vw, 14px)',
            fontStyle: 'italic',
            color: P.TF,
            margin: '0 0 10px',
            lineHeight: 1.55,
          }}>{hint}</p>
        )}
        {multiline ? (
          <textarea
            value={valueState[0]}
            onChange={function(e) { valueState[1](e.target.value); }}
            disabled={saving}
            rows={rows || 4}
            style={{
              width: '100%',
              fontFamily: FF,
              fontSize: 'clamp(15px, 1.3vw, 16px)',
              lineHeight: 1.65,
              padding: '14px 16px',
              border: '1px solid ' + P.SAND3,
              background: P.PAP,
              color: P.INK,
              outline: 'none',
              resize: 'vertical',
              boxSizing: 'border-box',
            }}
          />
        ) : (
          <input
            type="text"
            value={valueState[0]}
            onChange={function(e) { valueState[1](e.target.value); }}
            disabled={saving}
            style={{
              width: '100%',
              fontFamily: FF,
              fontSize: 'clamp(15px, 1.3vw, 17px)',
              padding: '14px 16px',
              border: '1px solid ' + P.SAND3,
              background: P.PAP,
              color: P.INK,
              outline: 'none',
              boxSizing: 'border-box',
            }}
          />
        )}
      </div>
    );
  }

  return (
    <div>
      {/* Sticky header bar */}
      <div style={{
        display: 'flex',
        alignItems: 'baseline',
        justifyContent: 'space-between',
        gap: 14,
        marginBottom: 'clamp(20px, 2.2vw, 28px)',
        paddingBottom: 14,
        borderBottom: '1px solid ' + P.SAND3,
        flexWrap: 'wrap',
      }}>
        <div>
          <div style={{
            fontFamily: FF,
            fontSize: 'clamp(11px, 0.95vw, 12px)',
            fontWeight: 700,
            letterSpacing: '0.32em',
            textTransform: 'uppercase',
            color: P.BR2,
            marginBottom: 6,
          }}>Editing pattern</div>
          <h2 style={{
            fontFamily: FF,
            fontSize: 'clamp(26px, 2.6vw, 34px)',
            fontWeight: 500,
            color: P.INK,
            margin: 0,
            letterSpacing: '-0.012em',
          }}>{pattern.name}</h2>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <button
            onClick={onClose}
            disabled={saving}
            style={{
              fontFamily: FS,
              fontSize: 'clamp(12px, 1vw, 13px)',
              fontWeight: 700,
              letterSpacing: '0.28em',
              textTransform: 'uppercase',
              padding: '12px 22px',
              background: 'transparent',
              color: P.BR2,
              border: '1px solid ' + P.SAND3,
              cursor: saving ? 'wait' : 'pointer',
            }}>Back to list</button>
          <button
            onClick={handleSave}
            disabled={saving}
            style={{
              fontFamily: FS,
              fontSize: 'clamp(12px, 1vw, 13px)',
              fontWeight: 700,
              letterSpacing: '0.28em',
              textTransform: 'uppercase',
              padding: '12px 28px',
              background: saving ? P.TL : (saved ? P.GREEN : P.INK),
              color: P.PAP,
              border: 'none',
              cursor: saving ? 'wait' : 'pointer',
              transition: 'background 0.2s',
            }}>{saving ? 'Saving...' : saved ? 'Saved' : 'Save changes'}</button>
        </div>
      </div>

      {error && (
        <p style={{
          fontFamily: FF,
          fontSize: 'clamp(15px, 1.3vw, 17px)',
          fontStyle: 'italic',
          color: P.RED,
          marginBottom: 20,
        }}>{error}</p>
      )}

      {/* Hero image */}
      <div style={{ marginBottom: 28 }}>
        <label style={{
          display: 'block',
          fontFamily: FF,
          fontSize: 'clamp(11px, 0.95vw, 12px)',
          fontWeight: 700,
          letterSpacing: '0.28em',
          textTransform: 'uppercase',
          color: P.BR2,
          marginBottom: 10,
        }}>Hero image</label>
        {heroImageUrlState[0] ? (
          <div style={{
            display: 'flex',
            gap: 16,
            alignItems: 'flex-start',
            flexWrap: 'wrap',
          }}>
            <img
              src={heroImageUrlState[0]}
              alt="Pattern hero"
              style={{
                maxWidth: 240,
                maxHeight: 160,
                objectFit: 'cover',
                border: '1px solid ' + P.SAND3,
              }}
            />
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <button
                onClick={function() { setShowPicker(true); }}
                disabled={saving}
                style={{
                  fontFamily: FS,
                  fontSize: 'clamp(11px, 0.95vw, 12px)',
                  fontWeight: 700,
                  letterSpacing: '0.24em',
                  textTransform: 'uppercase',
                  padding: '10px 18px',
                  background: 'transparent',
                  color: P.BR2,
                  border: '1px solid ' + P.GO,
                  cursor: saving ? 'wait' : 'pointer',
                }}>Replace image</button>
              <button
                onClick={function() {
                  heroImagePathState[1]('');
                  heroImageUrlState[1]('');
                }}
                disabled={saving}
                style={{
                  fontFamily: FS,
                  fontSize: 'clamp(11px, 0.95vw, 12px)',
                  fontWeight: 600,
                  letterSpacing: '0.22em',
                  textTransform: 'uppercase',
                  padding: '10px 18px',
                  background: 'transparent',
                  color: P.TF,
                  border: 'none',
                  cursor: saving ? 'wait' : 'pointer',
                }}>Remove</button>
            </div>
          </div>
        ) : (
          <button
            onClick={function() { setShowPicker(true); }}
            disabled={saving}
            style={{
              fontFamily: FS,
              fontSize: 'clamp(12px, 1vw, 13px)',
              fontWeight: 700,
              letterSpacing: '0.28em',
              textTransform: 'uppercase',
              padding: '14px 24px',
              background: 'transparent',
              color: P.BR2,
              border: '1px dashed ' + P.SAND3,
              cursor: saving ? 'wait' : 'pointer',
            }}>+ Add hero image</button>
        )}
      </div>

      {/* Narrative fields */}
      {field('Short descriptor', 'One sentence describing the pattern in plain language.', shortDescState, true, 2)}
      {field('Profile description', 'Two to three sentences expanding on the pattern profile.', profileDescState, true, 3)}
      {field('Common presentation', 'What this pattern typically looks like clinically.', commonPresState, true, 4)}
      {field('Pattern overview', 'Two to three paragraphs explaining what this pattern means physiologically. This appears prominently in the diagnostic PDF.', overviewState, true, 8)}
      {field('Clinical significance', 'Why this pattern matters medically and what the long-term risks are if untreated.', significanceState, true, 6)}
      {field('Lifestyle recommendations', 'Practical lifestyle changes for this pattern. Use plain markdown bullets if helpful.', lifestyleState, true, 6)}
      {field('Nutritional guidance', 'Dietary considerations specific to this pattern.', nutritionState, true, 6)}
      {field('Supplements to consider', 'Supplement options with brief rationale. Always frame as discussion points, not prescriptions.', supplementsState, true, 6)}
      {field('When to seek specialist input', 'Red flags or scenarios that warrant referral.', specialistState, true, 4)}
      {field('Expected timeline', 'What recovery or improvement typically looks like over weeks and months.', timelineState, true, 4)}

      {/* Bottom action bar — sticky save button */}
      <div style={{
        display: 'flex',
        justifyContent: 'flex-end',
        gap: 12,
        marginTop: 32,
        paddingTop: 20,
        borderTop: '1px solid ' + P.SAND3,
        flexWrap: 'wrap',
      }}>
        <button
          onClick={onClose}
          disabled={saving}
          style={{
            fontFamily: FS,
            fontSize: 'clamp(12px, 1vw, 13px)',
            fontWeight: 700,
            letterSpacing: '0.28em',
            textTransform: 'uppercase',
            padding: '14px 24px',
            background: 'transparent',
            color: P.BR2,
            border: '1px solid ' + P.SAND3,
            cursor: saving ? 'wait' : 'pointer',
          }}>Done</button>
        <button
          onClick={handleSave}
          disabled={saving}
          style={{
            fontFamily: FS,
            fontSize: 'clamp(12px, 1vw, 13px)',
            fontWeight: 700,
            letterSpacing: '0.28em',
            textTransform: 'uppercase',
            padding: '14px 32px',
            background: saving ? P.TL : (saved ? P.GREEN : P.INK),
            color: P.PAP,
            border: 'none',
            cursor: saving ? 'wait' : 'pointer',
            transition: 'background 0.2s',
          }}>{saving ? 'Saving...' : saved ? 'Saved' : 'Save changes'}</button>
      </div>

      {/* Asset picker modal */}
      {showPicker && (
        <AssetPicker
          assetTypeFilter="image"
          title="Choose hero image"
          onClose={function() { setShowPicker(false); }}
          onSelect={handleAssetPicked}
        />
      )}
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════
// CONTENT VIEW — tabbed CMS for patterns, prep modules, program, copy
// ═════════════════════════════════════════════════════════════════════════
function ContentView(props) {
  var actorEmail = (props && props.email) || '';
  var tabState = useState('patterns');
  var tab = tabState[0]; var setTab = tabState[1];

  var TABS = [
    { id: 'patterns',     label: 'Pattern narratives' },
    { id: 'prep',         label: 'Prep curriculum' },
    { id: 'program',      label: '12-week program' },
    { id: 'copy',         label: 'Site copy' },
  ];

  return (
    <div>
      {/* Tab bar */}
      <div style={{
        display: 'flex',
        gap: 0,
        marginBottom: 'clamp(20px, 2.2vw, 28px)',
        borderBottom: '1px solid ' + P.SAND3,
        flexWrap: 'wrap',
      }}>
        {TABS.map(function(t) {
          var active = tab === t.id;
          return (
            <button
              key={t.id}
              onClick={function() { setTab(t.id); }}
              style={{
                fontFamily: FF,
                fontSize: 'clamp(12px, 1.05vw, 14px)',
                fontWeight: 700,
                letterSpacing: '0.28em',
                textTransform: 'uppercase',
                padding: '14px 24px',
                marginBottom: -1,
                background: 'transparent',
                color: active ? P.INK : P.TF,
                border: 'none',
                borderBottom: active ? '2px solid ' + P.GO : '2px solid transparent',
                cursor: 'pointer',
                transition: 'all 0.15s',
              }}>{t.label}</button>
          );
        })}
      </div>

      {/* Tab content */}
      {tab === 'patterns' && <PatternsTab />}
      {tab === 'prep' && <PrepCurriculumTab />}
      {tab === 'program' && <ProgramTab email={actorEmail} />}
      {tab === 'copy' && <SiteCopyTab />}
    </div>
  );
}

// ─── Patterns tab 
function PatternsTab() {
  var patternsState = useState([]);
  var patterns = patternsState[0]; var setPatterns = patternsState[1];

  var loadingState = useState(true);
  var loading = loadingState[0]; var setLoading = loadingState[1];

  var errorState = useState(null);
  var error = errorState[0]; var setError = errorState[1];

  var editingState = useState(null);
  var editing = editingState[0]; var setEditing = editingState[1];

  var refreshState = useState(0);
  var refresh = refreshState[0]; var setRefresh = refreshState[1];

  useEffect(function() {
    var cancelled = false;
    setLoading(true);
    setError(null);
    fetch('/api/admin-list-patterns')
      .then(function(r) { return r.json(); })
      .then(function(data) {
        if (cancelled) return;
        if (data && data.patterns) {
          setPatterns(data.patterns);
          setLoading(false);
          // If we were editing one, refresh its data from the list
          if (editing) {
            var fresh = data.patterns.find(function(p) { return p.id === editing.id; });
            if (fresh) setEditing(fresh);
          }
        } else {
          setError((data && data.error) || 'Failed to load');
          setLoading(false);
        }
      })
      .catch(function() {
        if (cancelled) return;
        setError('Network error');
        setLoading(false);
      });
    return function() { cancelled = true; };
  }, [refresh]);

  if (editing) {
    return (
      <PatternNarrativeEditor
        pattern={editing}
        onClose={function() { setEditing(null); }}
        onSaved={function() { setRefresh(refresh + 1); }}
      />
    );
  }

  if (loading) {
    return (
      <div style={{ padding: 'clamp(60px, 8vw, 100px)', textAlign: 'center' }}>
        <Spinner />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: 'clamp(40px, 5vw, 60px)', textAlign: 'center' }}>
        <p style={{
          fontFamily: FF,
          fontStyle: 'italic',
          fontSize: 'clamp(16px, 1.4vw, 18px)',
          color: P.RED,
        }}>{error}. Try refreshing the page.</p>
      </div>
    );
  }

  return (
    <div>
      <p style={{
        fontFamily: FF,
        fontSize: 'clamp(15px, 1.3vw, 17px)',
        color: P.TL,
        lineHeight: 1.65,
        margin: '0 0 24px',
        fontStyle: 'italic',
      }}>
        These narratives appear in every patient's diagnostic PDF when a pattern is assigned. Click any pattern to edit its content.
      </p>

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
        gap: 14,
      }}>
        {patterns.map(function(p) {
          var pct = Math.round((p.narrativeCompleteness / p.narrativeTotal) * 100);
          return (
            <div
              key={p.id}
              onClick={function() { setEditing(p); }}
              style={{
                background: P.PAP,
                border: '1px solid ' + P.SAND3,
                borderLeft: '4px solid ' + (p.isComplete ? P.GREEN : pct > 0 ? P.AMBER : P.SAND3),
                padding: 'clamp(20px, 2.2vw, 26px)',
                cursor: 'pointer',
                transition: 'border-color 0.15s',
              }}
              onMouseEnter={function(e) { e.currentTarget.style.borderColor = P.BR; e.currentTarget.style.borderLeftColor = p.isComplete ? P.GREEN : pct > 0 ? P.AMBER : P.BR; }}
              onMouseLeave={function(e) { e.currentTarget.style.borderColor = P.SAND3; e.currentTarget.style.borderLeftColor = p.isComplete ? P.GREEN : pct > 0 ? P.AMBER : P.SAND3; }}>
              <div style={{
                display: 'flex',
                gap: 14,
                alignItems: 'flex-start',
                marginBottom: 12,
              }}>
                {p.heroImageUrl ? (
                  <img
                    src={p.heroImageUrl}
                    alt={p.name}
                    style={{
                      width: 60,
                      height: 60,
                      objectFit: 'cover',
                      border: '1px solid ' + P.SAND3,
                      flexShrink: 0,
                    }}
                  />
                ) : null}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    fontFamily: FF,
                    fontSize: 'clamp(18px, 1.5vw, 20px)',
                    fontWeight: 600,
                    color: P.INK,
                    letterSpacing: '-0.008em',
                    marginBottom: 4,
                  }}>{p.name}</div>
                  <div style={{
                    fontFamily: FF,
                    fontSize: 'clamp(10px, 0.85vw, 11px)',
                    fontWeight: 700,
                    letterSpacing: '0.28em',
                    textTransform: 'uppercase',
                    color: P.TF,
                  }}>{p.code}</div>
                </div>
              </div>

              <p style={{
                fontFamily: FF,
                fontSize: 'clamp(14px, 1.2vw, 15px)',
                color: p.short_descriptor ? P.TX : P.TF,
                fontStyle: p.short_descriptor ? 'normal' : 'italic',
                lineHeight: 1.55,
                margin: '0 0 14px',
              }}>{p.short_descriptor || 'No descriptor yet.'}</p>

              {/* Completion */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                fontFamily: FF,
                fontSize: 'clamp(10px, 0.85vw, 11px)',
                fontWeight: 700,
                letterSpacing: '0.22em',
                textTransform: 'uppercase',
                color: p.isComplete ? P.GREEN : (pct > 0 ? P.AMBER : P.TF),
              }}>
                <div style={{
                  flex: 1,
                  height: 4,
                  background: P.SAND2,
                  position: 'relative',
                }}>
                  <div style={{
                    height: 4,
                    background: p.isComplete ? P.GREEN : (pct > 0 ? P.AMBER : 'transparent'),
                    width: pct + '%',
                  }} />
                </div>
                <span>{p.narrativeCompleteness}/{p.narrativeTotal}</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Prep curriculum tab — placeholder for now ───
// ─── Prep module editor — full screen editor for a single module 
function PrepModuleEditor(props) {
  var mod = props.module;
  var onClose = props.onClose;
  var onSaved = props.onSaved;

  var titleState = useState(mod.title || '');
  var subtitleState = useState(mod.subtitle || '');
  var sequenceState = useState(mod.sequence_number);
  var unlockDayState = useState(mod.unlock_day);
  var videoUrlState = useState(mod.video_url || '');
  var videoDurationState = useState(mod.video_duration_seconds || 0);
  var explainerState = useState(mod.explainer_markdown || '');
  var reflectionState = useState(mod.reflection_prompt || '');
  var isActiveState = useState(mod.is_active !== false);

  // pattern_focus comes back from postgres as either an array, or a JSON string.
  // Normalise to array.
  var initialPatternFocus = mod.pattern_focus;
  if (typeof initialPatternFocus === 'string') {
    try { initialPatternFocus = JSON.parse(initialPatternFocus); }
    catch (e) { initialPatternFocus = []; }
  }
  if (!Array.isArray(initialPatternFocus)) initialPatternFocus = [];
  var patternFocusState = useState(initialPatternFocus);

  var savingState = useState(false);
  var saving = savingState[0]; var setSaving = savingState[1];
  var errorState = useState(null);
  var error = errorState[0]; var setError = errorState[1];
  var savedState = useState(false);
  var saved = savedState[0]; var setSaved = savedState[1];

  var ALL_PATTERNS = [
    { code: 'stress',    label: 'Stress' },
    { code: 'metabolic', label: 'Metabolic' },
    { code: 'hormonal',  label: 'Hormonal' },
    { code: 'vitality',  label: 'Vitality' },
    { code: 'composite', label: 'Composite' },
  ];

  function togglePattern(code) {
    var current = patternFocusState[0];
    if (current.indexOf(code) !== -1) {
      patternFocusState[1](current.filter(function(c) { return c !== code; }));
    } else {
      patternFocusState[1](current.concat([code]));
    }
  }

  function handleSave() {
    setSaving(true);
    setError(null);
    setSaved(false);

    fetch('/api/admin-save-prep-module', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        id: mod.id,
        title: titleState[0],
        subtitle: subtitleState[0],
        sequence_number: sequenceState[0],
        unlock_day: unlockDayState[0],
        video_url: videoUrlState[0],
        video_duration_seconds: videoDurationState[0],
        explainer_markdown: explainerState[0],
        reflection_prompt: reflectionState[0],
        pattern_focus: patternFocusState[0],
        is_active: isActiveState[0],
      }),
    })
      .then(function(r) { return r.json().then(function(j) { return { ok: r.ok, body: j }; }); })
      .then(function(resp) {
        setSaving(false);
        if (resp.ok && resp.body.success) {
          setSaved(true);
          if (onSaved) onSaved(resp.body.module);
          setTimeout(function() { setSaved(false); }, 2500);
        } else {
          var msg = (resp.body && resp.body.error) || 'Save failed';
          var detail = (resp.body && resp.body.detail) ? ' ' + resp.body.detail : '';
          setError(msg + detail);
        }
      })
      .catch(function() {
        setSaving(false);
        setError('Network error');
      });
  }

  function field(label, hint, valueState, multiline, rows) {
    return (
      <div style={{ marginBottom: 22 }}>
        <label style={{
          display: 'block',
          fontFamily: FF,
          fontSize: 'clamp(11px, 0.95vw, 12px)',
          fontWeight: 700,
          letterSpacing: '0.28em',
          textTransform: 'uppercase',
          color: P.BR2,
          marginBottom: 6,
        }}>{label}</label>
        {hint && (
          <p style={{
            fontFamily: FF,
            fontSize: 'clamp(13px, 1.1vw, 14px)',
            fontStyle: 'italic',
            color: P.TF,
            margin: '0 0 10px',
            lineHeight: 1.55,
          }}>{hint}</p>
        )}
        {multiline ? (
          <textarea
            value={valueState[0] || ''}
            onChange={function(e) { valueState[1](e.target.value); }}
            disabled={saving}
            rows={rows || 4}
            style={{
              width: '100%',
              fontFamily: FF,
              fontSize: 'clamp(15px, 1.3vw, 16px)',
              lineHeight: 1.65,
              padding: '14px 16px',
              border: '1px solid ' + P.SAND3,
              background: P.PAP,
              color: P.INK,
              outline: 'none',
              resize: 'vertical',
              boxSizing: 'border-box',
            }}
          />
        ) : (
          <input
            type="text"
            value={valueState[0] || ''}
            onChange={function(e) { valueState[1](e.target.value); }}
            disabled={saving}
            style={{
              width: '100%',
              fontFamily: FF,
              fontSize: 'clamp(15px, 1.3vw, 17px)',
              padding: '14px 16px',
              border: '1px solid ' + P.SAND3,
              background: P.PAP,
              color: P.INK,
              outline: 'none',
              boxSizing: 'border-box',
            }}
          />
        )}
      </div>
    );
  }

  function numberField(label, hint, valueState, max) {
    return (
      <div>
        <label style={{
          display: 'block',
          fontFamily: FF,
          fontSize: 'clamp(11px, 0.95vw, 12px)',
          fontWeight: 700,
          letterSpacing: '0.28em',
          textTransform: 'uppercase',
          color: P.BR2,
          marginBottom: 6,
        }}>{label}</label>
        {hint && (
          <p style={{
            fontFamily: FF,
            fontSize: 'clamp(12px, 1.05vw, 13px)',
            fontStyle: 'italic',
            color: P.TF,
            margin: '0 0 8px',
            lineHeight: 1.5,
          }}>{hint}</p>
        )}
        <input
          type="number"
          min={1}
          max={max || 999}
          value={valueState[0] || ''}
          onChange={function(e) { valueState[1](e.target.value); }}
          disabled={saving}
          style={{
            width: '100%',
            fontFamily: FF,
            fontSize: 'clamp(15px, 1.3vw, 17px)',
            padding: '14px 16px',
            border: '1px solid ' + P.SAND3,
            background: P.PAP,
            color: P.INK,
            outline: 'none',
            boxSizing: 'border-box',
          }}
        />
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div style={{
        display: 'flex',
        alignItems: 'baseline',
        justifyContent: 'space-between',
        gap: 14,
        marginBottom: 'clamp(20px, 2.2vw, 28px)',
        paddingBottom: 14,
        borderBottom: '1px solid ' + P.SAND3,
        flexWrap: 'wrap',
      }}>
        <div>
          <div style={{
            fontFamily: FF,
            fontSize: 'clamp(11px, 0.95vw, 12px)',
            fontWeight: 700,
            letterSpacing: '0.32em',
            textTransform: 'uppercase',
            color: P.BR2,
            marginBottom: 6,
          }}>Editing module {String(mod.sequence_number).padStart(2, '0')}</div>
          <h2 style={{
            fontFamily: FF,
            fontSize: 'clamp(26px, 2.6vw, 34px)',
            fontWeight: 500,
            color: P.INK,
            margin: 0,
            letterSpacing: '-0.012em',
          }}>{mod.title || 'Untitled module'}</h2>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <button
            onClick={onClose}
            disabled={saving}
            style={{
              fontFamily: FS,
              fontSize: 'clamp(12px, 1vw, 13px)',
              fontWeight: 700,
              letterSpacing: '0.28em',
              textTransform: 'uppercase',
              padding: '12px 22px',
              background: 'transparent',
              color: P.BR2,
              border: '1px solid ' + P.SAND3,
              cursor: saving ? 'wait' : 'pointer',
            }}>Back to list</button>
          <button
            onClick={handleSave}
            disabled={saving}
            style={{
              fontFamily: FS,
              fontSize: 'clamp(12px, 1vw, 13px)',
              fontWeight: 700,
              letterSpacing: '0.28em',
              textTransform: 'uppercase',
              padding: '12px 28px',
              background: saving ? P.TL : (saved ? P.GREEN : P.INK),
              color: P.PAP,
              border: 'none',
              cursor: saving ? 'wait' : 'pointer',
              transition: 'background 0.2s',
            }}>{saving ? 'Saving...' : saved ? 'Saved' : 'Save changes'}</button>
        </div>
      </div>

      {error && (
        <p style={{
          fontFamily: FF,
          fontSize: 'clamp(15px, 1.3vw, 17px)',
          fontStyle: 'italic',
          color: P.RED,
          marginBottom: 20,
        }}>{error}</p>
      )}

      {/* Two-column meta row */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: 14,
        marginBottom: 22,
      }}>
        {numberField('Sequence', 'Display order in the curriculum.', sequenceState, 99)}
        {numberField('Unlock day', 'Day in the prep window when this becomes available.', unlockDayState, 30)}
        <div>
          <label style={{
            display: 'block',
            fontFamily: FF,
            fontSize: 'clamp(11px, 0.95vw, 12px)',
            fontWeight: 700,
            letterSpacing: '0.28em',
            textTransform: 'uppercase',
            color: P.BR2,
            marginBottom: 6,
          }}>Status</label>
          <p style={{
            fontFamily: FF,
            fontSize: 'clamp(12px, 1.05vw, 13px)',
            fontStyle: 'italic',
            color: P.TF,
            margin: '0 0 8px',
            lineHeight: 1.5,
          }}>Active modules show to patients.</p>
          <button
            type="button"
            onClick={function() { isActiveState[1](!isActiveState[0]); }}
            disabled={saving}
            style={{
              width: '100%',
              fontFamily: FS,
              fontSize: 'clamp(13px, 1.1vw, 14px)',
              fontWeight: 700,
              letterSpacing: '0.24em',
              textTransform: 'uppercase',
              padding: '14px',
              background: isActiveState[0] ? P.GREEN : 'transparent',
              color: isActiveState[0] ? P.PAP : P.TF,
              border: '1px solid ' + (isActiveState[0] ? P.GREEN : P.SAND3),
              cursor: saving ? 'wait' : 'pointer',
              transition: 'all 0.15s',
            }}>{isActiveState[0] ? 'Active' : 'Inactive'}</button>
        </div>
      </div>

      {/* Title + subtitle */}
      {field('Title', 'Short, evocative. Appears at the top of the patient module.', titleState, false)}
      {field('Subtitle', 'One sentence that frames the module. Italic in the patient view.', subtitleState, true, 2)}

      {/* Video */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '2fr 1fr',
        gap: 14,
        marginBottom: 22,
      }}>
        <div style={{ marginBottom: 0 }}>
          {field('Video URL', 'Direct link to the hosted video. Leave blank if not ready.', videoUrlState, false)}
        </div>
        {numberField('Duration (seconds)', 'Video length in seconds.', videoDurationState, 9999)}
      </div>

      {/* Explainer markdown */}
      {field(
        'Explainer (markdown)',
        'The science. Use ## for headings, ** for bold, and blank lines between paragraphs. Markdown bullets with - also supported.',
        explainerState, true, 16
      )}

      {/* Reflection prompt */}
      {field(
        'Reflection prompt',
        'One question to leave the patient with. They reflect, save their answer, and bring it to the consultation.',
        reflectionState, true, 3
      )}

      {/* Pattern focus */}
      <div style={{ marginBottom: 28 }}>
        <label style={{
          display: 'block',
          fontFamily: FF,
          fontSize: 'clamp(11px, 0.95vw, 12px)',
          fontWeight: 700,
          letterSpacing: '0.28em',
          textTransform: 'uppercase',
          color: P.BR2,
          marginBottom: 6,
        }}>Pattern focus</label>
        <p style={{
          fontFamily: FF,
          fontSize: 'clamp(13px, 1.1vw, 14px)',
          fontStyle: 'italic',
          color: P.TF,
          margin: '0 0 12px',
          lineHeight: 1.55,
        }}>
          Which patterns this module is most relevant for. Leave none selected if it applies to everyone.
        </p>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {ALL_PATTERNS.map(function(opt) {
            var checked = patternFocusState[0].indexOf(opt.code) !== -1;
            return (
              <button
                key={opt.code}
                type="button"
                onClick={function() { if (!saving) togglePattern(opt.code); }}
                disabled={saving}
                style={{
                  fontFamily: FF,
                  fontSize: 'clamp(11px, 0.95vw, 12px)',
                  fontWeight: 700,
                  letterSpacing: '0.22em',
                  textTransform: 'uppercase',
                  padding: '10px 16px',
                  background: checked ? P.BR : 'transparent',
                  color: checked ? P.PAP : P.TF,
                  border: '1px solid ' + (checked ? P.BR : P.SAND3),
                  cursor: saving ? 'wait' : 'pointer',
                  transition: 'all 0.15s',
                }}>{opt.label}</button>
            );
          })}
        </div>
      </div>

      {/* Bottom action bar */}
      <div style={{
        display: 'flex',
        justifyContent: 'flex-end',
        gap: 12,
        marginTop: 32,
        paddingTop: 20,
        borderTop: '1px solid ' + P.SAND3,
        flexWrap: 'wrap',
      }}>
        <button
          onClick={onClose}
          disabled={saving}
          style={{
            fontFamily: FS,
            fontSize: 'clamp(12px, 1vw, 13px)',
            fontWeight: 700,
            letterSpacing: '0.28em',
            textTransform: 'uppercase',
            padding: '14px 24px',
            background: 'transparent',
            color: P.BR2,
            border: '1px solid ' + P.SAND3,
            cursor: saving ? 'wait' : 'pointer',
          }}>Done</button>
        <button
          onClick={handleSave}
          disabled={saving}
          style={{
            fontFamily: FS,
            fontSize: 'clamp(12px, 1vw, 13px)',
            fontWeight: 700,
            letterSpacing: '0.28em',
            textTransform: 'uppercase',
            padding: '14px 32px',
            background: saving ? P.TL : (saved ? P.GREEN : P.INK),
            color: P.PAP,
            border: 'none',
            cursor: saving ? 'wait' : 'pointer',
            transition: 'background 0.2s',
          }}>{saving ? 'Saving...' : saved ? 'Saved' : 'Save changes'}</button>
      </div>
    </div>
  );
}

// ─── Prep curriculum tab ─
function PrepCurriculumTab() {
  var modulesState = useState([]);
  var modules = modulesState[0]; var setModules = modulesState[1];

  var loadingState = useState(true);
  var loading = loadingState[0]; var setLoading = loadingState[1];

  var errorState = useState(null);
  var error = errorState[0]; var setError = errorState[1];

  var editingState = useState(null);
  var editing = editingState[0]; var setEditing = editingState[1];

  var refreshState = useState(0);
  var refresh = refreshState[0]; var setRefresh = refreshState[1];

  useEffect(function() {
    var cancelled = false;
    setLoading(true);
    setError(null);
    fetch('/api/admin-list-prep-modules')
      .then(function(r) { return r.json(); })
      .then(function(data) {
        if (cancelled) return;
        if (data && data.modules) {
          setModules(data.modules);
          setLoading(false);
          if (editing) {
            var fresh = data.modules.find(function(m) { return m.id === editing.id; });
            if (fresh) setEditing(fresh);
          }
        } else {
          setError((data && data.error) || 'Failed to load');
          setLoading(false);
        }
      })
      .catch(function() {
        if (cancelled) return;
        setError('Network error');
        setLoading(false);
      });
    return function() { cancelled = true; };
  }, [refresh]);

  if (editing) {
    return (
      <PrepModuleEditor
        module={editing}
        onClose={function() { setEditing(null); }}
        onSaved={function() { setRefresh(refresh + 1); }}
      />
    );
  }

  if (loading) {
    return (
      <div style={{ padding: 'clamp(60px, 8vw, 100px)', textAlign: 'center' }}>
        <Spinner />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: 'clamp(40px, 5vw, 60px)', textAlign: 'center' }}>
        <p style={{
          fontFamily: FF,
          fontStyle: 'italic',
          fontSize: 'clamp(16px, 1.4vw, 18px)',
          color: P.RED,
        }}>{error}. Try refreshing the page.</p>
      </div>
    );
  }

  return (
    <div>
      <p style={{
        fontFamily: FF,
        fontSize: 'clamp(15px, 1.3vw, 17px)',
        color: P.TL,
        lineHeight: 1.65,
        margin: '0 0 24px',
        fontStyle: 'italic',
      }}>
        Modules patients watch in the days leading up to their consultation. Click any module to edit.
      </p>

      {modules.length === 0 ? (
        <div style={{ padding: 'clamp(40px, 5vw, 60px)', textAlign: 'center' }}>
          <p style={{
            fontFamily: FF,
            fontStyle: 'italic',
            fontSize: 'clamp(16px, 1.4vw, 18px)',
            color: P.TF,
          }}>No prep modules in the database yet.</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {modules.map(function(m) {
            var patternFocus = m.pattern_focus;
            if (typeof patternFocus === 'string') {
              try { patternFocus = JSON.parse(patternFocus); }
              catch (e) { patternFocus = []; }
            }
            if (!Array.isArray(patternFocus)) patternFocus = [];

            return (
              <div
                key={m.id}
                onClick={function() { setEditing(m); }}
                style={{
                  background: P.PAP,
                  border: '1px solid ' + P.SAND3,
                  borderLeft: '4px solid ' + (m.is_active ? (m.isComplete ? P.GREEN : P.AMBER) : P.SAND3),
                  padding: 'clamp(18px, 2vw, 24px)',
                  cursor: 'pointer',
                  transition: 'border-color 0.15s',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 18,
                  flexWrap: 'wrap',
                }}
                onMouseEnter={function(e) { e.currentTarget.style.borderColor = P.BR; }}
                onMouseLeave={function(e) { e.currentTarget.style.borderColor = P.SAND3; }}>
                {/* Sequence number */}
                <div style={{
                  width: 52,
                  height: 52,
                  borderRadius: '50%',
                  background: 'rgba(201, 169, 97, 0.1)',
                  border: '1px solid ' + P.GO,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontFamily: FF,
                  fontSize: 22,
                  fontWeight: 500,
                  color: P.BR2,
                  flexShrink: 0,
                }}>{String(m.sequence_number).padStart(2, '0')}</div>

                {/* Content */}
                <div style={{ flex: 1, minWidth: 200 }}>
                  <div style={{
                    fontFamily: FF,
                    fontSize: 'clamp(17px, 1.45vw, 19px)',
                    fontWeight: 600,
                    color: P.INK,
                    letterSpacing: '-0.008em',
                    marginBottom: 4,
                  }}>{m.title || 'Untitled module'}</div>
                  <div style={{
                    fontFamily: FF,
                    fontSize: 'clamp(14px, 1.2vw, 15px)',
                    fontStyle: 'italic',
                    color: P.TF,
                    lineHeight: 1.5,
                    marginBottom: 6,
                  }}>{m.subtitle || 'No subtitle'}</div>
                  <div style={{
                    fontFamily: FF,
                    fontSize: 'clamp(10px, 0.85vw, 11px)',
                    fontWeight: 700,
                    letterSpacing: '0.22em',
                    textTransform: 'uppercase',
                    color: P.TF,
                  }}>
                    Day {m.unlock_day || '?'}
                    {m.video_duration_seconds ? ' \u00b7 ' + Math.round(m.video_duration_seconds / 60) + ' min' : ''}
                    {patternFocus.length > 0 ? ' \u00b7 ' + patternFocus.join(', ') : ''}
                  </div>
                </div>

                {/* Status pill */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'flex-end' }}>
                  <span style={{
                    fontFamily: FF,
                    fontSize: 'clamp(10px, 0.85vw, 11px)',
                    fontWeight: 700,
                    letterSpacing: '0.22em',
                    textTransform: 'uppercase',
                    padding: '5px 10px',
                    background: m.is_active ? 'rgba(74,124,63,0.12)' : 'rgba(122,96,64,0.08)',
                    color: m.is_active ? P.GREEN : P.TF,
                    border: '1px solid ' + (m.is_active ? P.GREEN : P.SAND3),
                  }}>{m.is_active ? 'Active' : 'Inactive'}</span>
                  <span style={{
                    fontFamily: FF,
                    fontSize: 'clamp(9px, 0.78vw, 10px)',
                    fontWeight: 700,
                    letterSpacing: '0.22em',
                    color: m.isComplete ? P.GREEN : (m.completeness > 0 ? P.AMBER : P.TF),
                  }}>{m.completeness}/{m.completenessTotal}</span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Program module helpers (shared by ProgramTab list + ProgramModuleEditor) ───

var PROGRAM_PHASE_LABELS = {
  foundations:   'Foundations',
  recalibration: 'Recalibration',
  optimisation:  'Optimisation',
};

// ─── v2 display-label maps for Catalogue + Billing (cosmetic-only rename) ─────
// The Membership page v2 introduced new canonical names. Database slugs and
// display_name columns are intentionally NOT migrated; admin-facing labels are
// rewritten on the frontend via these maps. Note the deliberate cross-naming:
// the OLD program slug 'sovereignty' renders as 'Mastery', while the OLD tier
// slug 'gold' renders as 'Sovereignty'. Two different objects, two different
// maps. Do not consolidate.
var MEMBERSHIP_TIER_DISPLAY = {
  silver: 'Knowledge',
  gold:   'Sovereignty',
  black:  'Ritual',
};
var PACKAGE_SLUG_DISPLAY = {
  foundations:   'Initiation',
  recalibration: 'Realignment',
  sovereignty:   'Mastery',
};

var PROGRAM_PUBLISH_LABELS = {
  draft:            'Draft',
  editorial_review: 'Editorial review',
  clinical_review:  'Clinical review',
  published:        'Published',
  archived:         'Archived',
};

var PROGRAM_PUBLISH_FORWARD = {
  draft:            ['editorial_review'],
  editorial_review: ['clinical_review'],
  clinical_review:  ['published'],
  published:        ['archived'],
  archived:         [],
};

function programPhasePill(phase) {
  if (phase === 'foundations')   return { bg: 'rgba(201, 169, 97, 0.10)', fg: P.BR2, border: 'rgba(201, 169, 97, 0.35)' };
  if (phase === 'recalibration') return { bg: 'rgba(154, 116, 48, 0.12)', fg: P.BR2, border: 'rgba(154, 116, 48, 0.40)' };
  if (phase === 'optimisation')  return { bg: 'rgba(90, 63, 24, 0.10)',   fg: P.BR2, border: 'rgba(90, 63, 24, 0.40)' };
  return { bg: P.SAND, fg: P.TF, border: P.SAND3 };
}

function programStatusPill(status) {
  if (status === 'draft')            return { bg: 'rgba(122,96,64,0.08)',  fg: P.TF,    border: P.SAND3 };
  if (status === 'editorial_review') return { bg: P.AMBER_BG,              fg: P.AMBER, border: P.AMBER };
  if (status === 'clinical_review')  return { bg: 'rgba(201,169,97,0.18)', fg: P.BR2,   border: P.GO };
  if (status === 'published')        return { bg: P.GREEN_BG,              fg: P.GREEN, border: P.GREEN };
  if (status === 'archived')         return { bg: 'rgba(122,96,64,0.05)',  fg: P.TF,    border: P.SAND3 };
  return { bg: P.SAND, fg: P.TF, border: P.SAND3 };
}

// Dropdown options for the publish_status field. Always allow same→same and
// revert-to-draft. Plus the single forward edge from current state. The server
// is the canonical enforcer — client-side filtering is UX, not security.
function programValidTransitions(current) {
  var options = [current];
  (PROGRAM_PUBLISH_FORWARD[current] || []).forEach(function(s) { options.push(s); });
  if (current !== 'draft') options.push('draft');
  // Dedupe
  return options.filter(function(s, i) { return options.indexOf(s) === i; });
}

// Mirrors validateResources in api/admin-save-program-module.js. Client check
// is defensive — server is canonical. Returns { ok, value? , error? }.
function programValidateResources(text) {
  if (text == null || (typeof text === 'string' && text.trim() === '')) {
    return { ok: true, value: null };
  }
  var parsed;
  try { parsed = JSON.parse(text); }
  catch (e) { return { ok: false, error: 'Resources is not valid JSON: ' + e.message }; }

  if (parsed === null) return { ok: true, value: null };
  if (!Array.isArray(parsed)) return { ok: false, error: 'Resources must be a JSON array or null' };

  for (var i = 0; i < parsed.length; i++) {
    var item = parsed[i];
    if (item === null || typeof item !== 'object' || Array.isArray(item)) {
      return { ok: false, error: 'Resources item ' + (i + 1) + ' must be an object' };
    }
    var required = ['type', 'name', 'url'];
    for (var j = 0; j < required.length; j++) {
      var f = required[j];
      if (typeof item[f] !== 'string' || item[f].trim() === '') {
        return { ok: false, error: 'Resources item ' + (i + 1) + ' missing required field: ' + f };
      }
    }
  }
  return { ok: true, value: parsed };
}


// ─── Program module editor — inline editor for a single program_modules row ───
// Mirrors PrepModuleEditor's inline replace-tab pattern. Renders inside
// ProgramTab when editingState is non-null. Closes via onClose; refreshes
// list via onSaved.
function ProgramModuleEditor(props) {
  var mod = props.module;
  var onClose = props.onClose;
  var onSaved = props.onSaved;
  var actorEmail = props.email || '';

  // Editable fields
  var subtitleState         = useState(mod.subtitle || '');
  var heroImagePathState    = useState(mod.hero_image_path || '');
  var leadVideoUrlState     = useState(mod.lead_video_url || '');
  var leadVideoDurationState = useState(mod.lead_video_duration_seconds == null ? '' : String(mod.lead_video_duration_seconds));
  var theScienceState       = useState(mod.the_science || '');
  var theProtocolState      = useState(mod.the_protocol || '');
  var reflectionPromptState = useState(mod.reflection_prompt || '');
  var milestoneLabelState   = useState(mod.milestone_label || '');
  var publishStatusState    = useState(mod.publish_status || 'draft');

  // resources lives as JSON text in the editor; parsed on save
  var initialResourcesText = mod.resources == null
    ? ''
    : JSON.stringify(mod.resources, null, 2);
  var resourcesTextState = useState(initialResourcesText);

  // Save / error state
  var savingState = useState(false);
  var saving = savingState[0]; var setSaving = savingState[1];
  var errorState = useState(null);
  var error = errorState[0]; var setError = errorState[1];
  var savedState = useState(false);
  var saved = savedState[0]; var setSaved = savedState[1];

  function handleSave() {
    setError(null);
    setSaved(false);

    // Validate resources client-side before submit
    var resourcesCheck = programValidateResources(resourcesTextState[0]);
    if (!resourcesCheck.ok) {
      setError(resourcesCheck.error);
      return;
    }

    setSaving(true);

    fetch('/api/admin-save-program-module', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        id: mod.id,
        actor_email: actorEmail,
        subtitle:                    subtitleState[0],
        hero_image_path:             heroImagePathState[0],
        lead_video_url:              leadVideoUrlState[0],
        lead_video_duration_seconds: leadVideoDurationState[0],
        the_science:                 theScienceState[0],
        the_protocol:                theProtocolState[0],
        reflection_prompt:           reflectionPromptState[0],
        resources:                   resourcesCheck.value,
        milestone_label:             mod.is_mandatory_milestone ? milestoneLabelState[0] : null,
        publish_status:              publishStatusState[0],
      }),
    })
      .then(function(r) { return r.json().then(function(j) { return { ok: r.ok, body: j }; }); })
      .then(function(resp) {
        setSaving(false);
        if (resp.ok && resp.body.success) {
          setSaved(true);
          if (onSaved) onSaved(resp.body.module);
          setTimeout(function() { setSaved(false); }, 2500);
        } else {
          var msg = (resp.body && resp.body.error) || 'Save failed';
          var detail = (resp.body && resp.body.detail) ? ' ' + resp.body.detail : '';
          setError(msg + detail);
        }
      })
      .catch(function() {
        setSaving(false);
        setError('Network error');
      });
  }

  // ── Inline field renderers (label + hint + control) ──────────────────
  function fieldLabel(text) {
    return (
      <label style={{
        display: 'block',
        fontFamily: FF,
        fontSize: 'clamp(11px, 0.95vw, 12px)',
        fontWeight: 700,
        letterSpacing: '0.28em',
        textTransform: 'uppercase',
        color: P.BR2,
        marginBottom: 6,
      }}>{text}</label>
    );
  }
  function fieldHint(text) {
    return (
      <p style={{
        fontFamily: FF,
        fontSize: 'clamp(13px, 1.1vw, 14px)',
        fontStyle: 'italic',
        color: P.TF,
        margin: '0 0 10px',
        lineHeight: 1.55,
      }}>{text}</p>
    );
  }
  function textInput(valueState, opts) {
    var disabled = saving || (opts && opts.disabled);
    return (
      <input
        type={(opts && opts.type) || 'text'}
        value={valueState[0] || ''}
        onChange={function(e) { valueState[1](e.target.value); }}
        disabled={disabled}
        placeholder={(opts && opts.placeholder) || ''}
        style={{
          width: '100%',
          fontFamily: FF,
          fontSize: 'clamp(15px, 1.3vw, 17px)',
          padding: '14px 16px',
          border: '1px solid ' + P.SAND3,
          background: disabled ? 'rgba(232,221,196,0.45)' : P.PAP,
          color: disabled ? P.TF : P.INK,
          outline: 'none',
          boxSizing: 'border-box',
        }}
      />
    );
  }
  function textArea(valueState, rows) {
    return (
      <textarea
        value={valueState[0] || ''}
        onChange={function(e) { valueState[1](e.target.value); }}
        disabled={saving}
        rows={rows || 4}
        style={{
          width: '100%',
          fontFamily: FF,
          fontSize: 'clamp(15px, 1.3vw, 16px)',
          lineHeight: 1.65,
          padding: '14px 16px',
          border: '1px solid ' + P.SAND3,
          background: P.PAP,
          color: P.INK,
          outline: 'none',
          resize: 'vertical',
          boxSizing: 'border-box',
        }}
      />
    );
  }

  var phasePillStyle = programPhasePill(mod.phase);
  var publishOptions = programValidTransitions(mod.publish_status || 'draft');

  return (
    <div>
      {/* Top bar: back + week badge + save */}
      <div style={{
        display: 'flex',
        alignItems: 'baseline',
        justifyContent: 'space-between',
        gap: 14,
        marginBottom: 'clamp(20px, 2.2vw, 28px)',
        paddingBottom: 14,
        borderBottom: '1px solid ' + P.SAND3,
        flexWrap: 'wrap',
      }}>
        <div>
          <div style={{
            fontFamily: FF,
            fontSize: 'clamp(11px, 0.95vw, 12px)',
            fontWeight: 700,
            letterSpacing: '0.32em',
            textTransform: 'uppercase',
            color: P.BR2,
            marginBottom: 6,
          }}>Editing Week {String(mod.week_number).padStart(2, '0')}</div>
          <h2 style={{
            fontFamily: FF,
            fontSize: 'clamp(26px, 2.6vw, 34px)',
            fontWeight: 500,
            color: P.INK,
            margin: 0,
            letterSpacing: '-0.012em',
          }}>{mod.title}</h2>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <button
            onClick={onClose}
            disabled={saving}
            style={{
              fontFamily: FS,
              fontSize: 'clamp(12px, 1vw, 13px)',
              fontWeight: 700,
              letterSpacing: '0.28em',
              textTransform: 'uppercase',
              padding: '12px 22px',
              background: 'transparent',
              color: P.BR2,
              border: '1px solid ' + P.SAND3,
              cursor: saving ? 'wait' : 'pointer',
            }}>Back to all weeks</button>
          <button
            onClick={handleSave}
            disabled={saving}
            style={{
              fontFamily: FS,
              fontSize: 'clamp(12px, 1vw, 13px)',
              fontWeight: 700,
              letterSpacing: '0.28em',
              textTransform: 'uppercase',
              padding: '12px 28px',
              background: saving ? P.TL : (saved ? P.GREEN : P.INK),
              color: P.PAP,
              border: 'none',
              cursor: saving ? 'wait' : 'pointer',
              transition: 'background 0.2s',
            }}>{saving ? 'Saving...' : saved ? 'Saved' : 'Save changes'}</button>
        </div>
      </div>

      {error && (
        <p style={{
          fontFamily: FF,
          fontSize: 'clamp(15px, 1.3vw, 17px)',
          fontStyle: 'italic',
          color: P.RED,
          marginBottom: 20,
          lineHeight: 1.55,
        }}>{error}</p>
      )}

      {/* Locked-fields header row */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
        gap: 16,
        padding: 'clamp(18px, 2vw, 24px)',
        background: 'rgba(232,221,196,0.40)',
        border: '1px solid ' + P.SAND3,
        marginBottom: 'clamp(24px, 2.6vw, 32px)',
      }}>
        <div>
          <div style={{
            fontFamily: FF, fontSize: 'clamp(10px, 0.85vw, 11px)', fontWeight: 700,
            letterSpacing: '0.28em', textTransform: 'uppercase', color: P.TF,
            marginBottom: 6,
          }}>Week</div>
          <div style={{
            fontFamily: FF, fontSize: 'clamp(20px, 1.8vw, 24px)', fontWeight: 500,
            color: P.INK, lineHeight: 1.1, letterSpacing: '-0.005em',
          }}>{String(mod.week_number).padStart(2, '0')} of 12</div>
        </div>
        <div>
          <div style={{
            fontFamily: FF, fontSize: 'clamp(10px, 0.85vw, 11px)', fontWeight: 700,
            letterSpacing: '0.28em', textTransform: 'uppercase', color: P.TF,
            marginBottom: 6,
          }}>Phase</div>
          <span style={{
            display: 'inline-block',
            fontFamily: FF, fontSize: 'clamp(11px, 0.95vw, 12px)', fontWeight: 700,
            letterSpacing: '0.22em', textTransform: 'uppercase',
            padding: '6px 12px',
            background: phasePillStyle.bg,
            color: phasePillStyle.fg,
            border: '1px solid ' + phasePillStyle.border,
          }}>{PROGRAM_PHASE_LABELS[mod.phase] || mod.phase}</span>
        </div>
        {mod.is_mandatory_milestone && (
          <div>
            <div style={{
              fontFamily: FF, fontSize: 'clamp(10px, 0.85vw, 11px)', fontWeight: 700,
              letterSpacing: '0.28em', textTransform: 'uppercase', color: P.TF,
              marginBottom: 6,
            }}>Milestone</div>
            <span style={{
              display: 'inline-block',
              fontFamily: FF, fontSize: 'clamp(11px, 0.95vw, 12px)', fontWeight: 700,
              letterSpacing: '0.22em', textTransform: 'uppercase',
              padding: '6px 12px',
              background: P.GO,
              color: P.INK,
              border: '1px solid ' + P.GO,
            }}>Mandatory</span>
          </div>
        )}
      </div>

      <p style={{
        fontFamily: FF, fontSize: 'clamp(13px, 1.1vw, 14px)',
        fontStyle: 'italic', color: P.TF,
        margin: '0 0 clamp(28px, 3vw, 36px)',
        lineHeight: 1.6,
      }}>
        Week number, phase, title, and milestone status are seed-locked. The twelve weeks are canonical.
      </p>

      {/* Subtitle */}
      <div style={{ marginBottom: 22 }}>
        {fieldLabel('Subtitle')}
        {fieldHint('One sentence that frames the week. Italic in the patient view.')}
        {textInput(subtitleState)}
      </div>

      {/* Hero image path */}
      <div style={{ marginBottom: 22 }}>
        {fieldLabel('Hero image path')}
        {fieldHint('Supabase Storage path or full public URL. Leave blank if not ready.')}
        {textInput(heroImagePathState, { placeholder: 'e.g. program-modules/week-01-hero.jpg' })}
      </div>

      {/* Video URL + duration (2-col) */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '2fr 1fr',
        gap: 14,
        marginBottom: 22,
      }}>
        <div>
          {fieldLabel('Lead video URL')}
          {fieldHint('Vimeo, YouTube, or Supabase Storage public URL.')}
          {textInput(leadVideoUrlState, { placeholder: 'https://...' })}
        </div>
        <div>
          {fieldLabel('Duration (seconds)')}
          {fieldHint('Video length in seconds.')}
          {textInput(leadVideoDurationState, { type: 'number', placeholder: '0' })}
        </div>
      </div>

      {/* The science */}
      <div style={{ marginBottom: 22 }}>
        {fieldLabel('The science (markdown)')}
        {fieldHint('Why this matters for her physiology. Use ## for headings, ** for bold, blank lines between paragraphs.')}
        {textArea(theScienceState, 16)}
      </div>

      {/* The protocol */}
      <div style={{ marginBottom: 22 }}>
        {fieldLabel('The protocol (markdown)')}
        {fieldHint('Actionable steps for this week. Same markdown conventions.')}
        {textArea(theProtocolState, 16)}
      </div>

      {/* Reflection prompt */}
      <div style={{ marginBottom: 22 }}>
        {fieldLabel('Reflection prompt')}
        {fieldHint('One question for the weekly check-in free-text field.')}
        {textArea(reflectionPromptState, 3)}
      </div>

      {/* Resources (JSON) */}
      <div style={{ marginBottom: 22 }}>
        {fieldLabel('Resources (JSON)')}
        {fieldHint('Array of {type, name, url} objects. Example: [{"type":"supplement","name":"Magnesium glycinate","url":"https://..."}]')}
        {textArea(resourcesTextState, 8)}
      </div>

      {/* Milestone label (conditional) */}
      <div style={{ marginBottom: 22 }}>
        {fieldLabel('Milestone label')}
        {fieldHint(
          mod.is_mandatory_milestone
            ? 'Label shown next to the week badge on the patient timeline.'
            : 'Only editable on mandatory-milestone weeks (7 and 12). This week is not a milestone.'
        )}
        {textInput(milestoneLabelState, { disabled: !mod.is_mandatory_milestone })}
      </div>

      {/* Publish status */}
      <div style={{ marginBottom: 32 }}>
        {fieldLabel('Publish status')}
        {fieldHint('Editorial workflow. Server enforces the canonical state machine: draft → editorial → clinical → published, with archive and revert-to-draft.')}
        <select
          value={publishStatusState[0]}
          onChange={function(e) { publishStatusState[1](e.target.value); }}
          disabled={saving}
          style={{
            width: '100%',
            fontFamily: FF,
            fontSize: 'clamp(15px, 1.3vw, 17px)',
            padding: '14px 16px',
            border: '1px solid ' + P.SAND3,
            background: P.PAP,
            color: P.INK,
            outline: 'none',
            cursor: 'pointer',
            boxSizing: 'border-box',
          }}>
          {publishOptions.map(function(s) {
            return <option key={s} value={s}>{PROGRAM_PUBLISH_LABELS[s] || s}</option>;
          })}
        </select>
      </div>

      {/* Bottom action bar */}
      <div style={{
        display: 'flex',
        justifyContent: 'flex-end',
        gap: 12,
        marginTop: 32,
        paddingTop: 20,
        borderTop: '1px solid ' + P.SAND3,
        flexWrap: 'wrap',
      }}>
        <button
          onClick={onClose}
          disabled={saving}
          style={{
            fontFamily: FS,
            fontSize: 'clamp(12px, 1vw, 13px)',
            fontWeight: 700,
            letterSpacing: '0.28em',
            textTransform: 'uppercase',
            padding: '14px 24px',
            background: 'transparent',
            color: P.BR2,
            border: '1px solid ' + P.SAND3,
            cursor: saving ? 'wait' : 'pointer',
          }}>Done</button>
        <button
          onClick={handleSave}
          disabled={saving}
          style={{
            fontFamily: FS,
            fontSize: 'clamp(12px, 1vw, 13px)',
            fontWeight: 700,
            letterSpacing: '0.28em',
            textTransform: 'uppercase',
            padding: '14px 32px',
            background: saving ? P.TL : (saved ? P.GREEN : P.INK),
            color: P.PAP,
            border: 'none',
            cursor: saving ? 'wait' : 'pointer',
            transition: 'background 0.2s',
          }}>{saving ? 'Saving...' : saved ? 'Saved' : 'Save changes'}</button>
      </div>
    </div>
  );
}


// ─── Program tab — list of 12 modules, click to edit ───
function ProgramTab(props) {
  var actorEmail = (props && props.email) || '';

  var modulesState = useState([]);
  var modules = modulesState[0]; var setModules = modulesState[1];

  var loadingState = useState(true);
  var loading = loadingState[0]; var setLoading = loadingState[1];

  var errorState = useState(null);
  var error = errorState[0]; var setError = errorState[1];

  var editingState = useState(null);
  var editing = editingState[0]; var setEditing = editingState[1];

  var refreshState = useState(0);
  var refresh = refreshState[0]; var setRefresh = refreshState[1];

  useEffect(function() {
    var cancelled = false;
    setLoading(true);
    setError(null);
    fetch('/api/admin-list-program-modules')
      .then(function(r) { return r.json(); })
      .then(function(data) {
        if (cancelled) return;
        if (data && data.modules) {
          setModules(data.modules);
          setLoading(false);
          // If currently editing one, refresh its data from the list
          if (editing) {
            var fresh = data.modules.find(function(m) { return m.id === editing.id; });
            if (fresh) setEditing(fresh);
          }
        } else {
          setError((data && data.error) || 'Failed to load');
          setLoading(false);
        }
      })
      .catch(function() {
        if (cancelled) return;
        setError('Network error');
        setLoading(false);
      });
    return function() { cancelled = true; };
  }, [refresh]);

  if (editing) {
    return (
      <ProgramModuleEditor
        module={editing}
        email={actorEmail}
        onClose={function() { setEditing(null); }}
        onSaved={function() { setRefresh(refresh + 1); }}
      />
    );
  }

  if (loading) {
    return (
      <div style={{ padding: 'clamp(60px, 8vw, 100px)', textAlign: 'center' }}>
        <Spinner />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: 'clamp(40px, 5vw, 60px)', textAlign: 'center' }}>
        <p style={{
          fontFamily: FF,
          fontStyle: 'italic',
          fontSize: 'clamp(16px, 1.4vw, 18px)',
          color: P.RED,
        }}>{error}. Try refreshing the page.</p>
      </div>
    );
  }

  return (
    <div>
      <p style={{
        fontFamily: FF,
        fontSize: 'clamp(15px, 1.3vw, 17px)',
        color: P.TL,
        lineHeight: 1.65,
        margin: '0 0 24px',
        fontStyle: 'italic',
      }}>
        The 12-week Transformation Journey. Click any week to author its lead video, science, protocol, reflection prompt, and resources. Empty fields render to patients as &ldquo;Content in development&rdquo;.
      </p>

      {modules.length === 0 ? (
        <div style={{ padding: 'clamp(40px, 5vw, 60px)', textAlign: 'center' }}>
          <p style={{
            fontFamily: FF,
            fontStyle: 'italic',
            fontSize: 'clamp(16px, 1.4vw, 18px)',
            color: P.TF,
          }}>No program modules in the database. Run migration 007 first.</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {modules.map(function(m) {
            var phasePillStyle = programPhasePill(m.phase);
            var statusPillStyle = programStatusPill(m.publish_status);
            var completenessColor = m.isComplete
              ? P.GREEN
              : (m.completenessFilled > 0 ? P.AMBER : P.SAND3);

            return (
              <div
                key={m.id}
                onClick={function() { setEditing(m); }}
                style={{
                  background: P.PAP,
                  border: '1px solid ' + P.SAND3,
                  borderLeft: '4px solid ' + completenessColor,
                  padding: 'clamp(18px, 2vw, 24px)',
                  cursor: 'pointer',
                  transition: 'border-color 0.15s, background 0.15s',
                  display: 'grid',
                  gridTemplateColumns: '76px minmax(120px, 1fr) 2fr auto',
                  gap: 18,
                  alignItems: 'center',
                }}
                onMouseEnter={function(e) { e.currentTarget.style.borderColor = P.BR; e.currentTarget.style.background = 'rgba(232,221,196,0.35)'; }}
                onMouseLeave={function(e) { e.currentTarget.style.borderColor = P.SAND3; e.currentTarget.style.background = P.PAP; }}>

                {/* Week badge */}
                <div style={{
                  width: 64,
                  height: 64,
                  borderRadius: '50%',
                  background: 'rgba(201, 169, 97, 0.10)',
                  border: '1px solid ' + P.GO,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}>
                  <div style={{
                    fontFamily: FF,
                    fontSize: 'clamp(9px, 0.78vw, 10px)',
                    fontWeight: 700,
                    letterSpacing: '0.28em',
                    textTransform: 'uppercase',
                    color: P.BR2,
                    marginBottom: 2,
                  }}>Week</div>
                  <div style={{
                    fontFamily: FF,
                    fontSize: 'clamp(20px, 1.7vw, 24px)',
                    fontWeight: 500,
                    color: P.INK,
                    lineHeight: 1,
                    letterSpacing: '-0.005em',
                  }}>{String(m.week_number).padStart(2, '0')}</div>
                </div>

                {/* Phase pill column */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, minWidth: 0 }}>
                  <span style={{
                    display: 'inline-block',
                    fontFamily: FF,
                    fontSize: 'clamp(10px, 0.85vw, 11px)',
                    fontWeight: 700,
                    letterSpacing: '0.22em',
                    textTransform: 'uppercase',
                    padding: '5px 10px',
                    background: phasePillStyle.bg,
                    color: phasePillStyle.fg,
                    border: '1px solid ' + phasePillStyle.border,
                    width: 'fit-content',
                  }}>{PROGRAM_PHASE_LABELS[m.phase] || m.phase}</span>
                  {m.is_mandatory_milestone && (
                    <span style={{
                      display: 'inline-block',
                      fontFamily: FF,
                      fontSize: 'clamp(10px, 0.85vw, 11px)',
                      fontWeight: 700,
                      letterSpacing: '0.22em',
                      textTransform: 'uppercase',
                      padding: '5px 10px',
                      background: P.GO,
                      color: P.INK,
                      border: '1px solid ' + P.GO,
                      width: 'fit-content',
                    }}>{m.milestone_label || 'Milestone'}</span>
                  )}
                </div>

                {/* Title + subtitle */}
                <div style={{ minWidth: 0 }}>
                  <div style={{
                    fontFamily: FF,
                    fontSize: 'clamp(17px, 1.45vw, 19px)',
                    fontWeight: 600,
                    color: P.INK,
                    letterSpacing: '-0.008em',
                    marginBottom: 4,
                  }}>{m.title}</div>
                  <div style={{
                    fontFamily: FF,
                    fontSize: 'clamp(14px, 1.2vw, 16px)',
                    fontStyle: 'italic',
                    color: m.subtitle ? P.TF : P.SAND3,
                    lineHeight: 1.5,
                  }}>{m.subtitle || 'No subtitle yet'}</div>
                </div>

                {/* Status + completeness column */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'flex-end' }}>
                  <span style={{
                    fontFamily: FF,
                    fontSize: 'clamp(10px, 0.85vw, 11px)',
                    fontWeight: 700,
                    letterSpacing: '0.22em',
                    textTransform: 'uppercase',
                    padding: '5px 10px',
                    background: statusPillStyle.bg,
                    color: statusPillStyle.fg,
                    border: '1px solid ' + statusPillStyle.border,
                  }}>{PROGRAM_PUBLISH_LABELS[m.publish_status] || m.publish_status}</span>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                  }}>
                    <div style={{
                      width: 9,
                      height: 9,
                      background: completenessColor,
                      transform: 'rotate(45deg)',
                    }} />
                    <span style={{
                      fontFamily: FF,
                      fontSize: 'clamp(11px, 0.95vw, 12px)',
                      fontWeight: 700,
                      letterSpacing: '0.22em',
                      color: m.isComplete ? P.GREEN : (m.completenessFilled > 0 ? P.AMBER : P.TF),
                    }}>{m.completenessFilled}/{m.completenessTotal}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Site copy tab — placeholder for now ─
// ═════════════════════════════════════════════════════════════════════════
// CATALOGUE VIEW — Services / Practitioners / Packages / Membership tiers
// (Phase 1 of the Transformation Journey: service catalogue + admin tooling)
// ═════════════════════════════════════════════════════════════════════════

const CATEGORY_LABELS = {
  gp_consult:        'GP consult',
  allied_health:     'Allied health',
  nurse:             'Nurse',
  pathology:         'Pathology',
  supplement:        'Supplement',
  group:             'Group',
  workshop:          'Workshop',
  external_referral: 'External referral',
};
const CATEGORY_OPTIONS = Object.keys(CATEGORY_LABELS);
const DELIVERY_OPTIONS = ['in_house', 'external'];
const FLAG_OPTIONS = ['fixed', 'customisable', 'removable'];
const TIER_OPTIONS = ['silver', 'gold', 'black'];

function dollarsFromCents(c) {
  if (c == null) return '';
  return '$' + (c / 100).toLocaleString('en-AU', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
}
function centsFromDollars(s) {
  if (s == null || s === '') return 0;
  var cleaned = String(s).replace(/[^0-9.]/g, '');
  var n = parseFloat(cleaned);
  if (isNaN(n)) return 0;
  return Math.round(n * 100);
}

// ─── small reusable atoms 

function Field({ label, children, hint }) {
  return (
    <div style={{ marginBottom: 18 }}>
      <label style={{
        display: 'block',
        fontFamily: FS, fontSize: 12, fontWeight: 700,
        letterSpacing: '0.18em', textTransform: 'uppercase',
        color: P.TF, marginBottom: 8,
      }}>{label}</label>
      {children}
      {hint && (
        <div style={{ fontFamily: FF, fontSize: 13, fontStyle: 'italic', color: P.TF, marginTop: 6 }}>
          {hint}
        </div>
      )}
    </div>
  );
}

function TextInput(props) {
  return (
    <input
      type={props.type || 'text'}
      value={props.value == null ? '' : props.value}
      onChange={function(e) { props.onChange(e.target.value); }}
      placeholder={props.placeholder || ''}
      style={{
        width: '100%', boxSizing: 'border-box',
        padding: '10px 12px',
        fontFamily: FF, fontSize: 16, color: P.INK,
        background: P.PAP, border: '1px solid ' + P.SAND3,
        outline: 'none',
      }}
    />
  );
}
function TextArea(props) {
  return (
    <textarea
      value={props.value == null ? '' : props.value}
      onChange={function(e) { props.onChange(e.target.value); }}
      rows={props.rows || 3}
      style={{
        width: '100%', boxSizing: 'border-box',
        padding: '10px 12px',
        fontFamily: FF, fontSize: 16, color: P.INK,
        background: P.PAP, border: '1px solid ' + P.SAND3,
        outline: 'none', resize: 'vertical',
      }}
    />
  );
}
function Select(props) {
  return (
    <select
      value={props.value == null ? '' : props.value}
      onChange={function(e) { props.onChange(e.target.value); }}
      style={{
        width: '100%', boxSizing: 'border-box',
        padding: '10px 12px',
        fontFamily: FF, fontSize: 16, color: P.INK,
        background: P.PAP, border: '1px solid ' + P.SAND3,
        outline: 'none',
      }}>
      {props.children}
    </select>
  );
}
function Toggle({ value, onChange, label }) {
  return (
    <label style={{ display: 'inline-flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
      <input
        type="checkbox"
        checked={!!value}
        onChange={function(e) { onChange(e.target.checked); }}
        style={{ width: 18, height: 18 }}
      />
      <span style={{ fontFamily: FF, fontSize: 16, color: P.INK }}>{label}</span>
    </label>
  );
}

// ─── Top-level Catalogue view with four tabs ─────

function CatalogueView() {
  var tabState = useState('services');
  var tab = tabState[0]; var setTab = tabState[1];

  var TABS = [
    { id: 'services',      label: 'Services' },
    { id: 'practitioners', label: 'Practitioners' },
    { id: 'packages',      label: 'Packages' },
    { id: 'tiers',         label: 'Membership tiers' },
  ];

  return (
    <div>
      <div style={{
        display: 'flex', gap: 0,
        marginBottom: 'clamp(20px, 2.2vw, 28px)',
        borderBottom: '1px solid ' + P.SAND3,
        flexWrap: 'wrap',
      }}>
        {TABS.map(function(t) {
          var active = tab === t.id;
          return (
            <button
              key={t.id}
              onClick={function() { setTab(t.id); }}
              style={{
                fontFamily: FF,
                fontSize: 'clamp(12px, 1.05vw, 14px)',
                fontWeight: 700,
                letterSpacing: '0.28em',
                textTransform: 'uppercase',
                padding: '14px 24px',
                marginBottom: -1,
                background: 'transparent',
                color: active ? P.INK : P.TF,
                border: 'none',
                borderBottom: active ? '2px solid ' + P.GO : '2px solid transparent',
                cursor: 'pointer',
                transition: 'all 0.15s',
              }}>{t.label}</button>
          );
        })}
      </div>

      {tab === 'services'      && <ServicesTab />}
      {tab === 'practitioners' && <PractitionersTab />}
      {tab === 'packages'      && <PackagesTab />}
      {tab === 'tiers'         && <MembershipTiersTab />}
    </div>
  );
}

// ─── Services tab 

function ServicesTab() {
  var servicesState = useState([]);
  var services = servicesState[0]; var setServices = servicesState[1];

  var practitionersState = useState([]);
  var practitioners = practitionersState[0]; var setPractitioners = practitionersState[1];

  var loadingState = useState(true);
  var loading = loadingState[0]; var setLoading = loadingState[1];

  var errorState = useState(null);
  var error = errorState[0]; var setError = errorState[1];

  var editingState = useState(null); // null | { ...service } | { __new: true }
  var editing = editingState[0]; var setEditing = editingState[1];

  var refreshState = useState(0);
  var refresh = refreshState[0]; var setRefresh = refreshState[1];

  useEffect(function() {
    var cancelled = false;
    setLoading(true); setError(null);

    Promise.all([
      fetch('/api/admin-list-services').then(function(r) { return r.json(); }),
      fetch('/api/admin-list-practitioners').then(function(r) { return r.json(); }),
    ]).then(function(results) {
      if (cancelled) return;
      var s = results[0]; var p = results[1];
      if (s && s.services && p && p.practitioners) {
        setServices(s.services);
        setPractitioners(p.practitioners);
      } else {
        setError((s && s.error) || (p && p.error) || 'Failed to load');
      }
      setLoading(false);
    }).catch(function() {
      if (cancelled) return;
      setError('Network error');
      setLoading(false);
    });

    return function() { cancelled = true; };
  }, [refresh]);

  if (editing) {
    return (
      <ServiceEditor
        service={editing.__new ? null : editing}
        practitioners={practitioners}
        onClose={function() { setEditing(null); }}
        onSaved={function() { setEditing(null); setRefresh(refresh + 1); }}
      />
    );
  }

  if (loading) return <div style={{ padding: 60, textAlign: 'center' }}><Spinner /></div>;
  if (error) return <CardWrap><div style={{ color: P.RED, fontFamily: FF }}>{error}</div></CardWrap>;

  // Group by category
  var grouped = {};
  services.forEach(function(s) {
    if (!grouped[s.category]) grouped[s.category] = [];
    grouped[s.category].push(s);
  });

  return (
    <div>
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        marginBottom: 24,
      }}>
        <div style={{
          fontFamily: FF, fontSize: 'clamp(18px, 1.6vw, 22px)',
          color: P.INK, fontStyle: 'italic',
        }}>{services.length} services in catalogue</div>
        <BtnPrimary onClick={function() { setEditing({ __new: true }); }}>
          + New service
        </BtnPrimary>
      </div>

      {CATEGORY_OPTIONS.map(function(cat) {
        var rows = grouped[cat] || [];
        if (rows.length === 0) return null;
        return (
          <CardWrap key={cat} title={CATEGORY_LABELS[cat]}>
            <div>
              {rows.map(function(s) {
                return (
                  <div
                    key={s.id}
                    onClick={function() { setEditing(s); }}
                    style={{
                      display: 'flex', alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '14px 4px',
                      borderBottom: '1px solid ' + P.SAND3,
                      cursor: 'pointer',
                      opacity: s.is_active ? 1 : 0.55,
                    }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontFamily: FF, fontSize: 17, color: P.INK }}>
                        {s.name}
                        {!s.is_active && (
                          <span style={{
                            marginLeft: 10, fontSize: 11, fontFamily: FS,
                            letterSpacing: '0.18em', textTransform: 'uppercase',
                            color: P.TF,
                          }}>· inactive</span>
                        )}
                      </div>
                      <div style={{
                        fontFamily: FS, fontSize: 12, color: P.TF, marginTop: 4,
                      }}>
                        {s.delivery_mode === 'in_house' ? 'in-house' : 'external'}
                        {s.duration_minutes != null && ' · ' + s.duration_minutes + ' min'}
                        {' · '}{s.slug}
                      </div>
                    </div>
                    <div style={{
                      fontFamily: FF, fontSize: 18, color: P.INK,
                      minWidth: 100, textAlign: 'right',
                    }}>{dollarsFromCents(s.price_cents)}</div>
                  </div>
                );
              })}
            </div>
          </CardWrap>
        );
      })}
    </div>
  );
}

function ServiceEditor(props) {
  var isNew = !props.service;
  var initial = props.service || {
    name: '', slug: '', category: 'gp_consult', delivery_mode: 'in_house',
    duration_minutes: 30, price_cents: 0,
    patient_selectable: true, default_flex_window_weeks: 1,
    description: '', is_active: true, in_house_provider_ids: [],
  };

  var formState = useState(initial);
  var form = formState[0]; var setForm = formState[1];

  var savingState = useState(false);
  var saving = savingState[0]; var setSaving = savingState[1];

  var errorState = useState(null);
  var error = errorState[0]; var setError = errorState[1];

  function update(k, v) { setForm({ ...form, [k]: v }); }

  function toggleProvider(pid) {
    var current = form.in_house_provider_ids || [];
    var next = current.indexOf(pid) === -1
      ? current.concat(pid)
      : current.filter(function(x) { return x !== pid; });
    update('in_house_provider_ids', next);
  }

  function save() {
    setSaving(true); setError(null);
    var body = { ...form };
    if (isNew) delete body.id;
    fetch('/api/admin-save-service', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })
      .then(function(r) { return r.json(); })
      .then(function(d) {
        setSaving(false);
        if (d && d.success) props.onSaved();
        else setError((d && d.error) || 'Save failed');
      })
      .catch(function() { setSaving(false); setError('Network error'); });
  }

  return (
    <CardWrap title={isNew ? 'New service' : 'Edit service'}>
      <Field label="Name">
        <TextInput value={form.name} onChange={function(v) { update('name', v); }} />
      </Field>
      <Field label="Slug" hint="URL-safe identifier; lowercase, hyphens only.">
        <TextInput value={form.slug} onChange={function(v) { update('slug', v); }} placeholder="e.g. gp-follow-up-30" />
      </Field>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 18 }}>
        <Field label="Category">
          <Select value={form.category} onChange={function(v) { update('category', v); }}>
            {CATEGORY_OPTIONS.map(function(c) {
              return <option key={c} value={c}>{CATEGORY_LABELS[c]}</option>;
            })}
          </Select>
        </Field>
        <Field label="Delivery mode">
          <Select value={form.delivery_mode} onChange={function(v) { update('delivery_mode', v); }}>
            {DELIVERY_OPTIONS.map(function(d) {
              return <option key={d} value={d}>{d === 'in_house' ? 'In-house' : 'External referral'}</option>;
            })}
          </Select>
        </Field>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 18 }}>
        <Field label="Duration (min)">
          <TextInput
            type="number"
            value={form.duration_minutes}
            onChange={function(v) { update('duration_minutes', v === '' ? null : parseInt(v, 10)); }}
          />
        </Field>
        <Field label="Price (AUD)">
          <TextInput
            value={form.price_cents != null ? (form.price_cents / 100).toString() : ''}
            onChange={function(v) { update('price_cents', centsFromDollars(v)); }}
            placeholder="e.g. 295"
          />
        </Field>
        <Field label="Default flex (weeks)">
          <TextInput
            type="number"
            value={form.default_flex_window_weeks}
            onChange={function(v) { update('default_flex_window_weeks', v === '' ? null : parseInt(v, 10)); }}
          />
        </Field>
      </div>

      <Field label="Description">
        <TextArea value={form.description} onChange={function(v) { update('description', v); }} rows={3} />
      </Field>

      <div style={{ display: 'flex', gap: 28, marginBottom: 18 }}>
        <Toggle
          label="Patient selectable"
          value={form.patient_selectable}
          onChange={function(v) { update('patient_selectable', v); }}
        />
        <Toggle
          label="Active"
          value={form.is_active}
          onChange={function(v) { update('is_active', v); }}
        />
      </div>

      {form.delivery_mode === 'in_house' && (
        <Field label="In-house providers">
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {props.practitioners.length === 0 && (
              <div style={{ fontFamily: FF, fontStyle: 'italic', color: P.TF }}>
                No practitioners yet. Add one in the Practitioners tab.
              </div>
            )}
            {props.practitioners.map(function(p) {
              var on = (form.in_house_provider_ids || []).indexOf(p.id) !== -1;
              return (
                <button
                  key={p.id}
                  onClick={function() { toggleProvider(p.id); }}
                  style={{
                    padding: '8px 14px',
                    fontFamily: FS, fontSize: 13,
                    background: on ? P.GO : P.PAP,
                    color: on ? P.PAP : P.INK,
                    border: '1px solid ' + (on ? P.GO : P.SAND3),
                    cursor: 'pointer',
                  }}>{p.display_name}</button>
              );
            })}
          </div>
        </Field>
      )}

      {error && (
        <div style={{
          fontFamily: FF, fontStyle: 'italic', color: P.RED,
          marginTop: 12, marginBottom: 12,
        }}>{error}</div>
      )}

      <div style={{ display: 'flex', gap: 12, marginTop: 24 }}>
        <BtnPrimary onClick={save} disabled={saving}>
          {saving ? 'Saving…' : (isNew ? 'Create service' : 'Save changes')}
        </BtnPrimary>
        <button
          onClick={props.onClose}
          disabled={saving}
          style={{
            padding: '14px 28px',
            fontFamily: FS, fontSize: 13, fontWeight: 700,
            letterSpacing: '0.2em', textTransform: 'uppercase',
            background: 'transparent', color: P.TF,
            border: '1px solid ' + P.SAND3,
            cursor: 'pointer',
          }}>Cancel</button>
      </div>
    </CardWrap>
  );
}

// ─── Practitioners tab ───

function PractitionersTab() {
  var listState = useState([]);
  var list = listState[0]; var setList = listState[1];
  var loadingState = useState(true);
  var loading = loadingState[0]; var setLoading = loadingState[1];
  var errorState = useState(null);
  var error = errorState[0]; var setError = errorState[1];
  var editingState = useState(null);
  var editing = editingState[0]; var setEditing = editingState[1];
  var refreshState = useState(0);
  var refresh = refreshState[0]; var setRefresh = refreshState[1];

  useEffect(function() {
    var cancelled = false;
    setLoading(true); setError(null);
    fetch('/api/admin-list-practitioners')
      .then(function(r) { return r.json(); })
      .then(function(d) {
        if (cancelled) return;
        if (d && d.practitioners) setList(d.practitioners);
        else setError((d && d.error) || 'Failed to load');
        setLoading(false);
      })
      .catch(function() { if (!cancelled) { setError('Network error'); setLoading(false); } });
    return function() { cancelled = true; };
  }, [refresh]);

  if (editing) {
    return (
      <PractitionerEditor
        practitioner={editing.__new ? null : editing}
        onClose={function() { setEditing(null); }}
        onSaved={function() { setEditing(null); setRefresh(refresh + 1); }}
      />
    );
  }

  if (loading) return <div style={{ padding: 60, textAlign: 'center' }}><Spinner /></div>;
  if (error) return <CardWrap><div style={{ color: P.RED, fontFamily: FF }}>{error}</div></CardWrap>;

  return (
    <div>
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        marginBottom: 24,
      }}>
        <div style={{
          fontFamily: FF, fontSize: 'clamp(18px, 1.6vw, 22px)',
          color: P.INK, fontStyle: 'italic',
        }}>{list.length} practitioners</div>
        <BtnPrimary onClick={function() { setEditing({ __new: true }); }}>
          + New practitioner
        </BtnPrimary>
      </div>

      <CardWrap>
        {list.length === 0 && (
          <div style={{ fontFamily: FF, fontStyle: 'italic', color: P.TF, padding: 20 }}>
            No practitioners yet.
          </div>
        )}
        {list.map(function(p) {
          return (
            <div
              key={p.id}
              onClick={function() { setEditing(p); }}
              style={{
                display: 'flex', alignItems: 'center',
                justifyContent: 'space-between',
                padding: '16px 4px',
                borderBottom: '1px solid ' + P.SAND3,
                cursor: 'pointer',
                opacity: p.is_active ? 1 : 0.55,
              }}>
              <div>
                <div style={{ fontFamily: FF, fontSize: 18, color: P.INK }}>
                  {p.display_name}
                  {!p.is_active && (
                    <span style={{
                      marginLeft: 10, fontSize: 11, fontFamily: FS,
                      letterSpacing: '0.18em', textTransform: 'uppercase',
                      color: P.TF,
                    }}>· inactive</span>
                  )}
                </div>
                <div style={{ fontFamily: FS, fontSize: 12, color: P.TF, marginTop: 4 }}>
                  {p.role}{p.full_name !== p.display_name && ' · ' + p.full_name}
                </div>
              </div>
              <div style={{
                fontFamily: FS, fontSize: 12, color: P.TF,
                letterSpacing: '0.18em', textTransform: 'uppercase',
              }}>{p.user_id ? 'Linked' : 'Unlinked'}</div>
            </div>
          );
        })}
      </CardWrap>
    </div>
  );
}

function PractitionerEditor(props) {
  var isNew = !props.practitioner;
  var initial = props.practitioner || {
    full_name: '', display_name: '', role: '', bio: '', photo_url: '', is_active: true,
  };

  var formState = useState(initial);
  var form = formState[0]; var setForm = formState[1];

  var savingState = useState(false);
  var saving = savingState[0]; var setSaving = savingState[1];

  var errorState = useState(null);
  var error = errorState[0]; var setError = errorState[1];

  function update(k, v) { setForm({ ...form, [k]: v }); }

  function save() {
    setSaving(true); setError(null);
    var body = { ...form };
    if (isNew) delete body.id;
    fetch('/api/admin-save-practitioner', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })
      .then(function(r) { return r.json(); })
      .then(function(d) {
        setSaving(false);
        if (d && d.success) props.onSaved();
        else setError((d && d.error) || 'Save failed');
      })
      .catch(function() { setSaving(false); setError('Network error'); });
  }

  return (
    <CardWrap title={isNew ? 'New practitioner' : 'Edit practitioner'}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 18 }}>
        <Field label="Full name">
          <TextInput value={form.full_name} onChange={function(v) { update('full_name', v); }} />
        </Field>
        <Field label="Display name" hint="Shown to patients.">
          <TextInput value={form.display_name} onChange={function(v) { update('display_name', v); }} />
        </Field>
      </div>
      <Field label="Role" hint="e.g. gp, naturopath, dietitian, psychologist, nurse.">
        <TextInput value={form.role} onChange={function(v) { update('role', v); }} />
      </Field>
      <Field label="Bio">
        <TextArea value={form.bio} onChange={function(v) { update('bio', v); }} rows={3} />
      </Field>
      <Field label="Photo URL">
        <TextInput value={form.photo_url} onChange={function(v) { update('photo_url', v); }} />
      </Field>
      <Toggle label="Active" value={form.is_active} onChange={function(v) { update('is_active', v); }} />

      {error && (
        <div style={{
          fontFamily: FF, fontStyle: 'italic', color: P.RED,
          marginTop: 12, marginBottom: 12,
        }}>{error}</div>
      )}

      <div style={{ display: 'flex', gap: 12, marginTop: 24 }}>
        <BtnPrimary onClick={save} disabled={saving}>
          {saving ? 'Saving…' : (isNew ? 'Create practitioner' : 'Save changes')}
        </BtnPrimary>
        <button
          onClick={props.onClose}
          disabled={saving}
          style={{
            padding: '14px 28px',
            fontFamily: FS, fontSize: 13, fontWeight: 700,
            letterSpacing: '0.2em', textTransform: 'uppercase',
            background: 'transparent', color: P.TF,
            border: '1px solid ' + P.SAND3,
            cursor: 'pointer',
          }}>Cancel</button>
      </div>
    </CardWrap>
  );
}

// ─── Packages tab 

function PackagesTab() {
  var packagesState = useState([]);
  var packages = packagesState[0]; var setPackages = packagesState[1];

  var servicesState = useState([]);
  var services = servicesState[0]; var setServices = servicesState[1];

  var loadingState = useState(true);
  var loading = loadingState[0]; var setLoading = loadingState[1];

  var errorState = useState(null);
  var error = errorState[0]; var setError = errorState[1];

  var editingState = useState(null);
  var editing = editingState[0]; var setEditing = editingState[1];

  var refreshState = useState(0);
  var refresh = refreshState[0]; var setRefresh = refreshState[1];

  useEffect(function() {
    var cancelled = false;
    setLoading(true); setError(null);

    Promise.all([
      fetch('/api/admin-list-packages').then(function(r) { return r.json(); }),
      fetch('/api/admin-list-services').then(function(r) { return r.json(); }),
    ]).then(function(r) {
      if (cancelled) return;
      var pk = r[0]; var sv = r[1];
      if (pk && pk.packages && sv && sv.services) {
        setPackages(pk.packages);
        setServices(sv.services.filter(function(s) { return s.is_active; }));
      } else {
        setError((pk && pk.error) || (sv && sv.error) || 'Failed to load');
      }
      setLoading(false);
    }).catch(function() {
      if (cancelled) return;
      setError('Network error'); setLoading(false);
    });

    return function() { cancelled = true; };
  }, [refresh]);

  if (editing) {
    return (
      <PackageEditor
        pkg={editing}
        services={services}
        onClose={function() { setEditing(null); }}
        onSaved={function() { setEditing(null); setRefresh(refresh + 1); }}
      />
    );
  }

  if (loading) return <div style={{ padding: 60, textAlign: 'center' }}><Spinner /></div>;
  if (error) return <CardWrap><div style={{ color: P.RED, fontFamily: FF }}>{error}</div></CardWrap>;

  return (
    <div style={{
      display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 20,
    }}>
      {packages.map(function(p) {
        var svcCount = (p.package_services || []).length;
        return (
          <div
            key={p.id}
            onClick={function() { setEditing(p); }}
            style={{
              background: P.PAP,
              border: '1px solid ' + P.SAND3,
              borderTop: '3px solid ' + P.GO,
              padding: 'clamp(24px, 2.4vw, 32px)',
              cursor: 'pointer',
              opacity: p.is_active ? 1 : 0.55,
            }}>
            <div style={{
              fontFamily: FF, fontSize: 12, fontWeight: 700,
              letterSpacing: '0.32em', textTransform: 'uppercase',
              color: P.BR2, marginBottom: 14,
            }}>{PACKAGE_SLUG_DISPLAY[p.slug] || p.slug}</div>
            <div style={{
              fontFamily: FF, fontSize: 'clamp(28px, 3vw, 36px)',
              color: P.INK, fontStyle: 'italic',
              marginBottom: 6, lineHeight: 1.1,
            }}>{PACKAGE_SLUG_DISPLAY[p.slug] || p.display_name}</div>
            <div style={{
              fontFamily: FF, fontSize: 28, color: P.INK,
              marginBottom: 20, letterSpacing: '-0.01em',
            }}>{dollarsFromCents(p.total_price_cents)}</div>
            <div style={{
              fontFamily: FS, fontSize: 12, color: P.TF,
              letterSpacing: '0.16em', textTransform: 'uppercase',
              lineHeight: 1.8,
            }}>
              <div>Bundled: {MEMBERSHIP_TIER_DISPLAY[p.bundled_membership_tier] || p.bundled_membership_tier}</div>
              <div>{svcCount} service{svcCount === 1 ? '' : 's'}</div>
              <div>{(p.payment_options || []).join(' / ')}</div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function PackageEditor(props) {
  var formState = useState({
    id: props.pkg.id,
    slug: props.pkg.slug,
    display_name: props.pkg.display_name,
    description: props.pkg.description || '',
    total_price_cents: props.pkg.total_price_cents,
    bundled_membership_tier: props.pkg.bundled_membership_tier,
    payment_options: props.pkg.payment_options || ['upfront'],
    is_active: props.pkg.is_active,
  });
  var form = formState[0]; var setForm = formState[1];

  var servicesListState = useState(
    (props.pkg.package_services || []).map(function(it) {
      return {
        service_id: it.service_id,
        target_week: it.target_week,
        flex_window_weeks: it.flex_window_weeks == null ? 1 : it.flex_window_weeks,
        flag: it.flag,
        sort_order: it.sort_order,
      };
    })
  );
  var servicesList = servicesListState[0]; var setServicesList = servicesListState[1];

  var addServiceIdState = useState(props.services[0] ? props.services[0].id : '');
  var addServiceId = addServiceIdState[0]; var setAddServiceId = addServiceIdState[1];

  var addWeekState = useState(1);
  var addWeek = addWeekState[0]; var setAddWeek = addWeekState[1];

  var savingState = useState(false);
  var saving = savingState[0]; var setSaving = savingState[1];

  var errorState = useState(null);
  var error = errorState[0]; var setError = errorState[1];

  function update(k, v) { setForm({ ...form, [k]: v }); }

  function addService() {
    if (!addServiceId) return;
    setServicesList(servicesList.concat({
      service_id: addServiceId,
      target_week: parseInt(addWeek, 10),
      flex_window_weeks: 1,
      flag: 'customisable',
      sort_order: servicesList.length,
    }));
  }
  function removeServiceAt(idx) {
    setServicesList(servicesList.filter(function(_, i) { return i !== idx; }));
  }
  function updateItemAt(idx, k, v) {
    setServicesList(servicesList.map(function(it, i) {
      return i === idx ? { ...it, [k]: v } : it;
    }));
  }

  function togglePaymentOption(opt) {
    var current = form.payment_options || [];
    var next = current.indexOf(opt) === -1
      ? current.concat(opt)
      : current.filter(function(x) { return x !== opt; });
    if (next.length === 0) next = ['upfront'];
    update('payment_options', next);
  }

  function save() {
    setSaving(true); setError(null);
    var body = { ...form, services: servicesList };
    fetch('/api/admin-save-package', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })
      .then(function(r) { return r.json(); })
      .then(function(d) {
        setSaving(false);
        if (d && d.success) props.onSaved();
        else setError((d && d.error) || 'Save failed');
      })
      .catch(function() { setSaving(false); setError('Network error'); });
  }

  // Quick service lookup
  var serviceById = {};
  props.services.forEach(function(s) { serviceById[s.id] = s; });

  // Group services list by week for display
  var byWeek = {};
  servicesList.forEach(function(it, idx) {
    if (!byWeek[it.target_week]) byWeek[it.target_week] = [];
    byWeek[it.target_week].push({ ...it, __idx: idx });
  });
  var weeks = Object.keys(byWeek).map(Number).sort(function(a, b) { return a - b; });

  return (
    <CardWrap title={'Edit package · ' + form.slug}>
      <Field label="Display name">
        <TextInput value={form.display_name} onChange={function(v) { update('display_name', v); }} />
      </Field>
      <Field label="Description">
        <TextArea value={form.description} onChange={function(v) { update('description', v); }} rows={3} />
      </Field>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 18 }}>
        <Field label="Total price (AUD)">
          <TextInput
            value={form.total_price_cents != null ? (form.total_price_cents / 100).toString() : ''}
            onChange={function(v) { update('total_price_cents', centsFromDollars(v)); }}
          />
        </Field>
        <Field label="Bundled tier">
          <Select value={form.bundled_membership_tier} onChange={function(v) { update('bundled_membership_tier', v); }}>
            {TIER_OPTIONS.map(function(t) {
              return <option key={t} value={t}>{MEMBERSHIP_TIER_DISPLAY[t] || t}</option>;
            })}
          </Select>
        </Field>
      </div>
      <Field label="Payment options">
        <div style={{ display: 'flex', gap: 8 }}>
          {['upfront', 'three_instalments'].map(function(opt) {
            var on = (form.payment_options || []).indexOf(opt) !== -1;
            return (
              <button
                key={opt}
                onClick={function() { togglePaymentOption(opt); }}
                style={{
                  padding: '8px 14px',
                  fontFamily: FS, fontSize: 13,
                  background: on ? P.GO : P.PAP,
                  color: on ? P.PAP : P.INK,
                  border: '1px solid ' + (on ? P.GO : P.SAND3),
                  cursor: 'pointer',
                }}>{opt}</button>
            );
          })}
        </div>
      </Field>
      <Toggle label="Active" value={form.is_active} onChange={function(v) { update('is_active', v); }} />

      <div style={{
        marginTop: 28, paddingTop: 20, borderTop: '1px solid ' + P.SAND3,
      }}>
        <div style={{
          fontFamily: FF, fontSize: 14, fontWeight: 700,
          letterSpacing: '0.32em', textTransform: 'uppercase',
          color: P.BR2, marginBottom: 16,
        }}>Services in this package</div>

        {weeks.length === 0 && (
          <div style={{ fontFamily: FF, fontStyle: 'italic', color: P.TF, marginBottom: 16 }}>
            No services attached yet.
          </div>
        )}

        {weeks.map(function(w) {
          return (
            <div key={w} style={{
              marginBottom: 14, padding: '12px 14px',
              background: P.SAND, border: '1px solid ' + P.SAND3,
            }}>
              <div style={{
                fontFamily: FS, fontSize: 11, fontWeight: 700,
                letterSpacing: '0.24em', textTransform: 'uppercase',
                color: P.TF, marginBottom: 10,
              }}>Week {w}</div>
              {byWeek[w].map(function(it) {
                var svc = serviceById[it.service_id];
                return (
                  <div key={it.__idx} style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    padding: '8px 0',
                  }}>
                    <div style={{ flex: 1, fontFamily: FF, fontSize: 15, color: P.INK }}>
                      {svc ? svc.name : '(unknown service)'}
                    </div>
                    <Select value={it.flag} onChange={function(v) { updateItemAt(it.__idx, 'flag', v); }}>
                      {FLAG_OPTIONS.map(function(f) {
                        return <option key={f} value={f}>{f}</option>;
                      })}
                    </Select>
                    <button
                      onClick={function() { removeServiceAt(it.__idx); }}
                      style={{
                        padding: '6px 12px', fontFamily: FS, fontSize: 12,
                        background: 'transparent', color: P.RED,
                        border: '1px solid ' + P.SAND3,
                        cursor: 'pointer',
                      }}>Remove</button>
                  </div>
                );
              })}
            </div>
          );
        })}

        <div style={{
          marginTop: 20, padding: 14,
          background: P.PAP, border: '1px dashed ' + P.SAND3,
          display: 'flex', alignItems: 'flex-end', gap: 10,
        }}>
          <div style={{ flex: 2 }}>
            <Field label="Add service">
              <Select value={addServiceId} onChange={setAddServiceId}>
                {props.services.map(function(s) {
                  return <option key={s.id} value={s.id}>{s.name}</option>;
                })}
              </Select>
            </Field>
          </div>
          <div style={{ width: 110 }}>
            <Field label="Week">
              <Select value={String(addWeek)} onChange={function(v) { setAddWeek(parseInt(v, 10)); }}>
                {[1,2,3,4,5,6,7,8,9,10,11,12].map(function(n) {
                  return <option key={n} value={String(n)}>{n}</option>;
                })}
              </Select>
            </Field>
          </div>
          <button
            onClick={addService}
            style={{
              padding: '10px 18px', marginBottom: 18,
              fontFamily: FS, fontSize: 13, fontWeight: 700,
              letterSpacing: '0.2em', textTransform: 'uppercase',
              background: P.GO, color: P.PAP,
              border: '1px solid ' + P.GO,
              cursor: 'pointer',
            }}>Add</button>
        </div>
      </div>

      {error && (
        <div style={{
          fontFamily: FF, fontStyle: 'italic', color: P.RED,
          marginTop: 12, marginBottom: 12,
        }}>{error}</div>
      )}

      <div style={{ display: 'flex', gap: 12, marginTop: 24 }}>
        <BtnPrimary onClick={save} disabled={saving}>
          {saving ? 'Saving…' : 'Save changes'}
        </BtnPrimary>
        <button
          onClick={props.onClose}
          disabled={saving}
          style={{
            padding: '14px 28px',
            fontFamily: FS, fontSize: 13, fontWeight: 700,
            letterSpacing: '0.2em', textTransform: 'uppercase',
            background: 'transparent', color: P.TF,
            border: '1px solid ' + P.SAND3,
            cursor: 'pointer',
          }}>Cancel</button>
      </div>
    </CardWrap>
  );
}

// ─── Membership tiers tab 

function MembershipTiersTab() {
  var listState = useState([]);
  var list = listState[0]; var setList = listState[1];
  var loadingState = useState(true);
  var loading = loadingState[0]; var setLoading = loadingState[1];
  var errorState = useState(null);
  var error = errorState[0]; var setError = errorState[1];
  var refreshState = useState(0);
  var refresh = refreshState[0]; var setRefresh = refreshState[1];

  useEffect(function() {
    var cancelled = false;
    setLoading(true); setError(null);
    fetch('/api/admin-list-membership-tiers')
      .then(function(r) { return r.json(); })
      .then(function(d) {
        if (cancelled) return;
        if (d && d.tiers) setList(d.tiers);
        else setError((d && d.error) || 'Failed to load');
        setLoading(false);
      })
      .catch(function() { if (!cancelled) { setError('Network error'); setLoading(false); } });
    return function() { cancelled = true; };
  }, [refresh]);

  if (loading) return <div style={{ padding: 60, textAlign: 'center' }}><Spinner /></div>;
  if (error) return <CardWrap><div style={{ color: P.RED, fontFamily: FF }}>{error}</div></CardWrap>;

  return (
    <div>
      <div style={{
        fontFamily: FF, fontSize: 'clamp(18px, 1.6vw, 22px)',
        color: P.INK, fontStyle: 'italic', marginBottom: 24,
      }}>Three locked tiers. Edit price, SLA, and message cap.</div>

      {list.map(function(t) {
        return (
          <MembershipTierRow
            key={t.slug}
            tier={t}
            onSaved={function() { setRefresh(refresh + 1); }}
          />
        );
      })}
    </div>
  );
}

function MembershipTierRow(props) {
  var formState = useState({ ...props.tier });
  var form = formState[0]; var setForm = formState[1];

  var savingState = useState(false);
  var saving = savingState[0]; var setSaving = savingState[1];

  var errorState = useState(null);
  var error = errorState[0]; var setError = errorState[1];

  function update(k, v) { setForm({ ...form, [k]: v }); }

  function save() {
    setSaving(true); setError(null);
    var body = {
      slug: form.slug,
      display_name: form.display_name,
      monthly_price_cents: form.monthly_price_cents,
      sla_hours: form.sla_hours,
      message_cap_per_month: form.message_cap_per_month,
      available_during_program: form.available_during_program,
      available_post_program: form.available_post_program,
    };
    fetch('/api/admin-save-membership-tier', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })
      .then(function(r) { return r.json(); })
      .then(function(d) {
        setSaving(false);
        if (d && d.success) props.onSaved();
        else setError((d && d.error) || 'Save failed');
      })
      .catch(function() { setSaving(false); setError('Network error'); });
  }

  return (
    <CardWrap title={MEMBERSHIP_TIER_DISPLAY[form.slug] || form.display_name}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 18 }}>
        <Field label="Monthly price (AUD)">
          <TextInput
            value={form.monthly_price_cents != null ? (form.monthly_price_cents / 100).toString() : ''}
            onChange={function(v) { update('monthly_price_cents', centsFromDollars(v)); }}
          />
        </Field>
        <Field label="SLA (hours)">
          <TextInput
            type="number"
            value={form.sla_hours}
            onChange={function(v) { update('sla_hours', v === '' ? 0 : parseInt(v, 10)); }}
          />
        </Field>
        <Field label="Message cap / month" hint="Empty = unlimited.">
          <TextInput
            value={form.message_cap_per_month == null ? '' : form.message_cap_per_month}
            onChange={function(v) { update('message_cap_per_month', v === '' ? null : parseInt(v, 10)); }}
          />
        </Field>
      </div>
      <div style={{ display: 'flex', gap: 28, marginBottom: 8 }}>
        <Toggle
          label="Available during program"
          value={form.available_during_program}
          onChange={function(v) { update('available_during_program', v); }}
        />
        <Toggle
          label="Available post-program"
          value={form.available_post_program}
          onChange={function(v) { update('available_post_program', v); }}
        />
      </div>

      {error && (
        <div style={{
          fontFamily: FF, fontStyle: 'italic', color: P.RED,
          marginTop: 12, marginBottom: 12,
        }}>{error}</div>
      )}

      <div style={{ marginTop: 16 }}>
        <BtnPrimary onClick={save} disabled={saving}>
          {saving ? 'Saving…' : 'Save'}
        </BtnPrimary>
      </div>
    </CardWrap>
  );
}

function SiteCopyTab() {
  return (
    <div style={{ padding: 'clamp(40px, 5vw, 60px)', textAlign: 'center' }}>
      <p style={{
        fontFamily: FF,
        fontStyle: 'italic',
        fontSize: 'clamp(16px, 1.4vw, 18px)',
        color: P.TF,
      }}>Site copy editor coming in Phase 6.</p>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════
// ADMIN SIGN-IN GATE — inline email + password form
// ═════════════════════════════════════════════════════════════════════════
//
// Renders on /admin when no session is present. Admins (Harpinder + Robyn)
// are seeded manually in Supabase Auth with passwords; there is no public
// admin sign-up. On success, supabase emits SIGNED_IN which the parent
// App.jsx onAuthStateChange listener catches and re-renders with email set.
//
// Editorial form pattern matches EmailGate / MagicLinkLogin: AnkhSm flourish,
// italic premise + roman title, inline error in italic Cormorant RED.
//
function AdminSignInGate() {
  var emailState = useState('');
  var email = emailState[0];
  var setEmail = emailState[1];

  var passwordState = useState('');
  var password = passwordState[0];
  var setPassword = passwordState[1];

  var submittingState = useState(false);
  var submitting = submittingState[0];
  var setSubmitting = submittingState[1];

  var errorState = useState(null);
  var error = errorState[0];
  var setError = errorState[1];

  function handleSubmit(e) {
    if (e) e.preventDefault();
    setError(null);

    var cleanEmail = email.trim().toLowerCase();
    var emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!emailRegex.test(cleanEmail)) {
      setError('Please enter a valid email address.');
      return;
    }
    if (!password || password.length < 6) {
      setError('Enter your password.');
      return;
    }

    setSubmitting(true);
    signInWithPassword(cleanEmail, password)
      .then(function() {
        // Supabase auth state change will fire in App.jsx and
        // re-render this component with email set. As a defensive
        // fallback (in case App.jsx isn't listening) we reload.
        setTimeout(function() {
          if (typeof window !== 'undefined' && !window.__cleoAuthHandled) {
            window.location.reload();
          }
        }, 300);
      })
      .catch(function(err) {
        setSubmitting(false);
        var msg = (err && err.message) ? err.message : 'Sign-in failed.';
        if (/invalid login credentials/i.test(msg)) {
          setError('That email and password combination did not match.');
        } else if (/email not confirmed/i.test(msg)) {
          setError('Your email has not been confirmed yet. Check your inbox.');
        } else {
          setError(msg);
        }
      });
  }

  return (
    <div style={{
      background: P.PAP, minHeight: '100vh',
      padding: 'clamp(64px, 10vh, 120px) 24px',
      display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
    }}>
      <div style={{ maxWidth: 520, width: '100%', textAlign: 'center' }}>
        <div style={{ display: "flex", justifyContent: "center", marginBottom: 32 }}><Ankh responsive="opener" color={P.GO} /></div>

        <p style={{
          fontFamily: FF, fontSize: 'clamp(22px, 2.6vw, 32px)',
          fontStyle: 'italic', color: P.BR2,
          margin: '0 0 22px', lineHeight: 1.35,
        }}>
          Sign in to continue.
        </p>

        <h1 style={{
          fontFamily: FF, fontWeight: 400,
          color: P.INK, marginTop: 0, marginBottom: 24,
          fontSize: 'clamp(34px, 4.4vw, 52px)',
          lineHeight: 1.1, letterSpacing: '-0.014em',
        }}>
          The admin panel is private.
        </h1>

        <p style={{
          fontFamily: FF, fontSize: 'clamp(16px, 1.8vw, 19px)',
          color: P.TL, lineHeight: 1.65,
          maxWidth: 420, margin: '0 auto 36px',
        }}>
          Cleo's Secret staff only. Sign in with your work email and password.
        </p>

        <form onSubmit={handleSubmit} style={{ textAlign: 'left' }}>
          {/* Email field */}
          <div style={{ marginBottom: 18 }}>
            <label style={{
              display: 'block',
              fontFamily: FF,
              fontSize: 'clamp(10px, 0.85vw, 11px)',
              fontWeight: 700,
              letterSpacing: '0.28em',
              textTransform: 'uppercase',
              color: P.BR2,
              marginBottom: 8,
            }}>Work email</label>
            <input
              type="email"
              autoComplete="email"
              value={email}
              onChange={function(e) { setEmail(e.target.value); }}
              placeholder="you@cleossecret.com.au"
              disabled={submitting}
              style={{
                width: '100%',
                fontFamily: FF,
                fontSize: 'clamp(15px, 1.3vw, 17px)',
                padding: '14px 16px',
                border: '1px solid ' + P.SAND3,
                background: P.PAP,
                color: P.INK,
                outline: 'none',
                boxSizing: 'border-box',
                transition: 'border-color 0.2s',
              }}
              onFocus={function(e) { e.currentTarget.style.borderColor = P.BR; }}
              onBlur={function(e) { e.currentTarget.style.borderColor = P.SAND3; }}
            />
          </div>

          {/* Password field */}
          <div style={{ marginBottom: 22 }}>
            <label style={{
              display: 'block',
              fontFamily: FF,
              fontSize: 'clamp(10px, 0.85vw, 11px)',
              fontWeight: 700,
              letterSpacing: '0.28em',
              textTransform: 'uppercase',
              color: P.BR2,
              marginBottom: 8,
            }}>Password</label>
            <input
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={function(e) { setPassword(e.target.value); }}
              disabled={submitting}
              style={{
                width: '100%',
                fontFamily: FF,
                fontSize: 'clamp(15px, 1.3vw, 17px)',
                padding: '14px 16px',
                border: '1px solid ' + P.SAND3,
                background: P.PAP,
                color: P.INK,
                outline: 'none',
                boxSizing: 'border-box',
                transition: 'border-color 0.2s',
              }}
              onFocus={function(e) { e.currentTarget.style.borderColor = P.BR; }}
              onBlur={function(e) { e.currentTarget.style.borderColor = P.SAND3; }}
            />
          </div>

          {/* Inline error */}
          {error && (
            <p style={{
              fontFamily: FF,
              fontSize: 'clamp(14px, 1.2vw, 16px)',
              fontStyle: 'italic',
              color: P.RED,
              lineHeight: 1.5,
              margin: '0 0 18px',
            }}>
              {error}
            </p>
          )}

          {/* Submit */}
          <button
            type="submit"
            disabled={submitting}
            style={{
              width: '100%',
              fontFamily: FS,
              fontSize: 'clamp(11px, 1vw, 13px)',
              fontWeight: 600,
              letterSpacing: '0.32em',
              textTransform: 'uppercase',
              padding: '16px',
              background: submitting ? P.TL : P.INK,
              color: P.PAP,
              border: 'none',
              cursor: submitting ? 'wait' : 'pointer',
              transition: 'background 0.2s',
            }}
            onMouseEnter={function(e) {
              if (!submitting) e.currentTarget.style.background = P.BR;
            }}
            onMouseLeave={function(e) {
              if (!submitting) e.currentTarget.style.background = P.INK;
            }}>
            {submitting ? 'Signing in...' : 'Sign in'}
          </button>
        </form>

        <p style={{
          fontFamily: FF,
          fontSize: 'clamp(12px, 1.05vw, 14px)',
          fontStyle: 'italic',
          color: P.TF,
          marginTop: 28,
          lineHeight: 1.6,
        }}>
          If you have forgotten your password, contact Harpinder.
        </p>
      </div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════
// MAIN ADMIN PANEL
// ═════════════════════════════════════════════════════════════════════════
export default function AdminPanel(props) {
  var onNavigate = props.onNavigate;
  var email = props.email;
  var isAdminProp = props.isAdmin;

  var viewState = useState('overview');
  var view = viewState[0];
  var setView = viewState[1];

  // Auth-wait loop — gate behind login.
  var emailWaitState = useState(0);
  var emailWaitMs = emailWaitState[0];
  var setEmailWaitMs = emailWaitState[1];

  useEffect(function() {
    if (email) return;
    if (emailWaitMs >= 3000) return;
    var t = setTimeout(function() { setEmailWaitMs(emailWaitMs + 500); }, 500);
    return function() { clearTimeout(t); };
  }, [email, emailWaitMs]);

  // Admin role check — fetch is_admin status from server.
  // Parent App.jsx also resolves this and passes via isAdmin prop, but we
  // double-check here so /admin can be navigated to directly without parent
  // having pre-resolved it.
  var adminCheckState = useState({ checking: false, isAdmin: null, hasRecord: null });
  var adminCheck = adminCheckState[0];
  var setAdminCheck = adminCheckState[1];

  useEffect(function() {
    if (!email) return;
    // If parent already told us isAdmin, trust that and skip the fetch
    if (typeof isAdminProp === 'boolean') {
      setAdminCheck({ checking: false, isAdmin: isAdminProp, hasRecord: true });
      return;
    }
    setAdminCheck({ checking: true, isAdmin: null, hasRecord: null });
    fetch('/api/check-admin', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: email }),
    })
      .then(function(r) { return r.json(); })
      .then(function(data) {
        setAdminCheck({
          checking: false,
          isAdmin: (data && data.isAdmin === true),
          hasRecord: (data && data.hasRecord === true),
        });
      })
      .catch(function() {
        setAdminCheck({ checking: false, isAdmin: false, hasRecord: false });
      });
  }, [email, isAdminProp]);

  // Waiting for email to resolve
  if (!email && emailWaitMs < 3000) {
    return (
      <div style={{
        background: P.PAP, minHeight: '100vh',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <Spinner />
      </div>
    );
  }

  // Email resolution timed out — show inline sign-in form
  if (!email) {
    return <AdminSignInGate />;
  }

  // Admin role check in progress
  if (adminCheck.checking || adminCheck.isAdmin === null) {
    return (
      <div style={{
        background: P.PAP, minHeight: '100vh',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <Spinner />
      </div>
    );
  }

  // Signed in but not an admin — show access denied
  if (adminCheck.isAdmin !== true) {
    return (
      <div style={{
        background: P.PAP, minHeight: '100vh',
        padding: 'clamp(64px, 10vh, 120px) 24px',
        display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
      }}>
        <div style={{ maxWidth: 520, width: '100%', textAlign: 'center' }}>
          <div style={{ display: "flex", justifyContent: "center", marginBottom: 32 }}><Ankh responsive="opener" color={P.GO} /></div>
          <p style={{
            fontFamily: FF, fontSize: 'clamp(22px, 2.6vw, 32px)',
            fontStyle: 'italic', color: P.BR2,
            margin: '0 0 22px', lineHeight: 1.35,
          }}>
            This area is for staff.
          </p>
          <h1 style={{
            fontFamily: FF, fontWeight: 400,
            color: P.INK, marginTop: 0, marginBottom: 24,
            fontSize: 'clamp(34px, 4.4vw, 52px)',
            lineHeight: 1.1, letterSpacing: '-0.014em',
          }}>
            You don't have access to the admin panel.
          </h1>
          <p style={{
            fontFamily: FF, fontSize: 'clamp(16px, 1.8vw, 19px)',
            color: P.TL, lineHeight: 1.65,
            maxWidth: 420, margin: '0 auto 36px',
          }}>
            If you think this is a mistake, contact Harpinder.
          </p>
          <BtnPrimary onClick={function() { if (onNavigate) onNavigate('portal'); }}>
            Go to my portal
          </BtnPrimary>
        </div>
      </div>
    );
  }

  var VIEWS = [
    { id: 'overview',    label: 'Overview',        section: 'Platform',  badge: null },
    { id: 'users',       label: 'User management', section: null,        badge: 3    },
    { id: 'catalogue',   label: 'Catalogue',       section: 'Programme', badge: null },
    { id: 'content',     label: 'Content CMS',     section: 'Settings',  badge: null },
    { id: 'scoring',     label: 'Scoring engine',  section: null,        badge: null },
    { id: 'billing',     label: 'Billing',         section: null,        badge: null },
    { id: 'automations', label: 'Automations',     section: null,        badge: null },
  ];

  var TITLES = {
    overview:    'Platform overview',
    users:       'User management',
    catalogue:   'Service catalogue',
    content:     'Content CMS',
    scoring:     'Scoring engine',
    billing:     'Billing and payments',
    automations: 'Automations',
  };

  return (
    <div className="cs-admin-shell" style={{
      display: 'grid',
      gridTemplateColumns: '260px 1fr',
      minHeight: '100vh',
      fontFamily: FF,
    }}>

      {/* ═════════ Sidebar (obsidian) ═════════ */}
      <aside style={{
        background: P.OBS,
        display: 'flex',
        flexDirection: 'column',
        borderRight: '1px solid ' + P.OBS3,
      }}>
        {/* Brand head */}
        <div style={{
          padding: 'clamp(28px, 3vw, 36px) clamp(22px, 2.4vw, 28px)',
          borderBottom: '1px solid ' + P.OBS3,
          textAlign: 'left',
        }}>
          <div style={{
            fontFamily: FF,
            fontSize: 'clamp(22px, 1.9vw, 28px)',
            fontStyle: 'italic',
            color: P.IVY,
            fontWeight: 500,
            lineHeight: 1.2,
            letterSpacing: '-0.008em',
          }}>Admin dashboard</div>
        </div>

        {/* Nav */}
        <nav style={{ flex: 1, padding: '16px 0', display: 'flex', flexDirection: 'column' }}>
          {VIEWS.map(function(v) {
            var active = view === v.id;
            return (
              <React.Fragment key={v.id}>
                {v.section && (
                  <div style={{
                    fontFamily: FF,
                    fontSize: 'clamp(11px, 0.95vw, 12px)',
                    fontWeight: 700,
                    letterSpacing: '0.32em',
                    textTransform: 'uppercase',
                    color: P.TF,
                    padding: '20px 28px 8px',
                  }}>{v.section}</div>
                )}
                <div
                  onClick={function() { setView(v.id); }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    padding: '14px 28px 14px 26px',
                    fontFamily: FF,
                    fontSize: 'clamp(15px, 1.3vw, 17px)',
                    color: active ? P.PAP : 'rgba(240,232,212,0.7)',
                    cursor: 'pointer',
                    background: active ? P.OBS2 : 'transparent',
                    borderLeft: active ? '3px solid ' + P.GO : '3px solid transparent',
                    fontWeight: active ? 600 : 500,
                    letterSpacing: '-0.005em',
                    transition: 'all 0.15s',
                  }}
                  onMouseEnter={function(e) {
                    if (!active) {
                      e.currentTarget.style.background = P.OBS2;
                      e.currentTarget.style.color = P.IVY;
                    }
                  }}
                  onMouseLeave={function(e) {
                    if (!active) {
                      e.currentTarget.style.background = 'transparent';
                      e.currentTarget.style.color = 'rgba(240,232,212,0.7)';
                    }
                  }}>
                  {v.label}
                  {v.badge && (
                    <span style={{
                      marginLeft: 'auto',
                      fontFamily: FF,
                      fontSize: 'clamp(11px, 0.95vw, 12px)',
                      fontWeight: 700,
                      letterSpacing: '0.18em',
                      background: P.RED,
                      color: P.PAP,
                      padding: '3px 10px',
                    }}>{v.badge}</span>
                  )}
                </div>
              </React.Fragment>
            );
          })}
        </nav>

        {/* Footer — epigraph + back link */}
        <div style={{
          padding: 'clamp(22px, 2.4vw, 28px) 28px',
          borderTop: '1px solid ' + P.OBS3,
        }}>
          <p style={{
            fontFamily: FF,
            fontSize: 'clamp(14px, 1.2vw, 16px)',
            fontStyle: 'italic',
            color: P.GO2,
            opacity: 0.72,
            lineHeight: 1.5,
            margin: '0 0 18px',
            fontWeight: 500,
          }}>
            Cleopatra studied the science of her own body.
          </p>
          <button
            onClick={function() { if (onNavigate) onNavigate('landing'); }}
            style={{
              fontFamily: FS,
              fontSize: 'clamp(12px, 1vw, 13px)',
              fontWeight: 700,
              letterSpacing: '0.28em',
              textTransform: 'uppercase',
              color: P.GO2,
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              padding: 0,
              transition: 'color 0.2s',
            }}
            onMouseEnter={function(e) { e.currentTarget.style.color = P.PAP; }}
            onMouseLeave={function(e) { e.currentTarget.style.color = P.GO2; }}>
            Back to site
          </button>
        </div>
      </aside>

      {/* ═════════ Main area ═════════ */}
      <div style={{ display: 'flex', flexDirection: 'column', background: P.SAND, minWidth: 0 }}>

        {/* Topbar */}
        <header style={{
          background: P.PAP,
          borderBottom: '1px solid ' + P.SAND3,
          padding: 'clamp(20px, 2.2vw, 28px) clamp(24px, 2.8vw, 40px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 20,
          flexWrap: 'wrap',
        }}>
          <h1 style={{
            fontFamily: FF,
            fontSize: 'clamp(26px, 2.8vw, 38px)',
            fontWeight: 500,
            color: P.INK,
            lineHeight: 1.15,
            letterSpacing: '-0.014em',
            margin: 0,
          }}>{TITLES[view]}</h1>

          <div style={{
            display: 'flex',
            gap: 14,
            alignItems: 'center',
            flexWrap: 'wrap',
          }}>
            <span style={{
              fontFamily: FF,
              fontSize: 'clamp(15px, 1.3vw, 17px)',
              fontStyle: 'italic',
              color: P.BR2,
              fontWeight: 500,
            }}>{todayDateLong()}</span>
            <button
              onClick={function() { if (onNavigate) onNavigate('clinical'); }}
              style={{
                fontFamily: FS,
                fontSize: 'clamp(12px, 1vw, 13px)',
                fontWeight: 700,
                letterSpacing: '0.28em',
                textTransform: 'uppercase',
                padding: '12px 24px',
                border: '1px solid ' + P.GO,
                background: 'transparent',
                color: P.BR2,
                cursor: 'pointer',
                transition: 'background 0.2s',
              }}
              onMouseEnter={function(e) { e.currentTarget.style.background = 'rgba(201, 169, 97, 0.08)'; }}
              onMouseLeave={function(e) { e.currentTarget.style.background = 'transparent'; }}>
              Clinical view
            </button>
            <button style={{
              fontFamily: FS,
              fontSize: 'clamp(12px, 1vw, 13px)',
              fontWeight: 700,
              letterSpacing: '0.28em',
              textTransform: 'uppercase',
              padding: '12px 28px',
              background: P.INK,
              color: P.PAP,
              border: 'none',
              cursor: 'pointer',
              transition: 'background 0.2s',
            }}
              onMouseEnter={function(e) { e.currentTarget.style.background = P.BR; }}
              onMouseLeave={function(e) { e.currentTarget.style.background = P.INK; }}>
              Export report
            </button>
          </div>
        </header>

        {/* View body */}
        <main style={{
          padding: 'clamp(24px, 2.8vw, 40px)',
          overflowY: 'auto',
          flex: 1,
          background: P.SAND,
        }}>
          <div style={{ maxWidth: 1680, margin: '0 auto' }}>
            {view === 'overview'    && <OverviewView />}
            {view === 'users'       && <UsersView />}
            {view === 'catalogue'   && <CatalogueView />}
            {view === 'content'     && <ContentView email={email} />}
            {view === 'scoring'     && <ScoringView />}
            {view === 'billing'     && <BillingView />}
            {view === 'automations' && <AutomationsView />}
          </div>
        </main>
      </div>

      {/* Responsive: collapse sidebar below 900px (stacks above main area) */}
      <style>{`
        @media (max-width: 900px) {
          .cs-admin-shell {
            grid-template-columns: 1fr !important;
            min-height: auto !important;
          }
        }
      `}</style>
    </div>
  );
}
